import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { X, ChevronDown, Check, Plus, Loader2, Info, ShipWheel, Cloud, Braces } from 'lucide-react';
import type { ActionTemplate, WorkflowNode, WorkflowEdge, GitHubWorkflowInput, OutputField, CustomBlock } from '../../api/client';
import { apiClient } from '../../api/client';
import { ApprovalRulesEditor } from './ApprovalRulesEditor';
import { VariablePicker, buildVariableGroups } from './VariablePicker';
import type { VariableGroup } from './VariablePicker';
import { useTheme } from '@/components/theme-provider';
import cloudflareIcon from '../../assets/icons/cloudflare.svg';
import pagerdutyIcon from '../../assets/icons/pagerduty-icon.svg';
import datadogLightIcon from '../../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../../assets/icons/datadog-dark-mode.png';
import argocdIcon from '../../assets/icons/argocd.svg';
import gitlabIcon from '../../assets/icons/gitlab-clean.svg';
import helmLightIcon from '../../assets/icons/helm-light.svg';
import helmDarkIcon from '../../assets/icons/helm-dark.svg';
import vercelIcon from '../../assets/icons/vercel.svg';
import vercelLightIcon from '../../assets/icons/vercel-light.svg';

interface NodeConfigPanelProps {
  node: WorkflowNode;
  actionTemplates: ActionTemplate[];
  onUpdate: (nodeId: string, data: any) => void;
  onClose: () => void;
  githubRepos?: string[];
  gitlabRepos?: string[];
  confluenceSpaces?: { key: string; name: string }[];
  jiraProjects?: { key: string; name: string }[];
  linearTeams?: { key: string; name: string }[];
  clusters?: { id: string; name: string }[];
  awsAccounts?: { key: string; name: string }[];
  gcpProjects?: { key: string; name: string }[];
  vercelProjects?: { key: string; name: string }[];
  railwayProjects?: { key: string; name: string }[];
  railwayServices?: { key: string; name: string }[];
  railwayEnvironments?: { key: string; name: string }[];
  flyioApps?: { key: string; name: string }[];
  nebiusProjects?: { key: string; name: string }[];
  nebiusClusters?: { key: string; name: string }[];
  beamDeployments?: { key: string; name: string }[];
  beamContainers?: { key: string; name: string }[];
  beamMachines?: { key: string; name: string }[];
  beamSandboxes?: { key: string; name: string }[];
  daytonaSandboxes?: { key: string; name: string }[];
  daytonaSnapshots?: { key: string; name: string }[];
  daytonaVolumes?: { key: string; name: string }[];
  daytonaTargets?: { key: string; name: string }[];
  supabaseProjects?: { key: string; name: string }[];
  supabaseBranches?: { key: string; name: string }[];
  planetscaleDatabases?: { key: string; name: string }[];
  planetscaleBranches?: { key: string; name: string }[];
  planetscaleDeployRequests?: { key: string; name: string }[];
  neonProjects?: { key: string; name: string }[];
  neonBranches?: { key: string; name: string }[];
  neonEndpoints?: { key: string; name: string }[];
  clickhouseServices?: { key: string; name: string }[];
  clickhouseClickPipes?: { key: string; name: string }[];
  clickhouseBackups?: { key: string; name: string }[];
  jenkinsJobs?: { key: string; name: string }[];
  circleciProjects?: { key: string; name: string }[];
  sonarcloudProjects?: { key: string; name: string }[];
  oktaUsers?: { key: string; name: string }[];
  oktaGroups?: { key: string; name: string }[];
  terraformWorkspaces?: { key: string; name: string }[];
  databricksJobs?: { key: string; name: string }[];
  databricksClusters?: { key: string; name: string }[];
  databricksPipelines?: { key: string; name: string }[];
  databricksWarehouses?: { key: string; name: string }[];
  pulumiStacks?: { key: string; name: string }[];
  vaultMounts?: { key: string; name: string }[];
  vaultPolicies?: { key: string; name: string }[];
  vaultDatabaseMounts?: { key: string; name: string }[];
  infisicalProjects?: { key: string; name: string }[];
  infisicalSecretSyncs?: { key: string; name: string }[];
  cloudflareZones?: { key: string; name: string }[];
  cloudflareWorkers?: { key: string; name: string }[];
  cloudflareLBPools?: { key: string; name: string }[];
  cloudflareTunnels?: { key: string; name: string }[];
  cloudflareAccessApps?: { key: string; name: string }[];
  allNodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
  triggerVariables?: Record<string, OutputField[]>;
  customBlocks?: CustomBlock[];
}

export function NodeConfigPanel({ node, actionTemplates, onUpdate, onClose, githubRepos = [], gitlabRepos = [], confluenceSpaces = [], jiraProjects = [], linearTeams = [], clusters = [], awsAccounts = [], gcpProjects = [], vercelProjects = [], railwayProjects = [], railwayServices = [], railwayEnvironments = [], flyioApps = [], nebiusProjects = [], nebiusClusters = [], beamDeployments = [], beamContainers = [], beamMachines = [], beamSandboxes = [], daytonaSandboxes = [], daytonaSnapshots = [], daytonaVolumes = [], daytonaTargets = [], supabaseProjects = [], supabaseBranches = [], planetscaleDatabases = [], planetscaleBranches = [], planetscaleDeployRequests = [], neonProjects = [], neonBranches = [], neonEndpoints = [], clickhouseServices = [], clickhouseClickPipes = [], clickhouseBackups = [], jenkinsJobs = [], circleciProjects = [], sonarcloudProjects = [], oktaUsers = [], oktaGroups = [], terraformWorkspaces = [], databricksJobs = [], databricksClusters = [], databricksPipelines = [], databricksWarehouses = [], pulumiStacks = [], vaultMounts = [], vaultPolicies = [], vaultDatabaseMounts = [], infisicalProjects = [], infisicalSecretSyncs = [], cloudflareZones = [], cloudflareWorkers = [], cloudflareLBPools = [], cloudflareTunnels = [], cloudflareAccessApps = [], allNodes = [], edges = [], triggerVariables = {}, customBlocks = [] }: NodeConfigPanelProps) {
  const data = node.data || {};
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Loop (Poll Until) and For Each nodes render their embedded action's config
  // fields with the exact same behavior as a regular action node, so resolve
  // the template for all three. This also enables the dynamic-fetch effects
  // below (Fly machines, Nebius node groups, AWS resource IDs, ...) for them.
  const template = (node.type === 'action' || node.type === 'loop' || node.type === 'for_each')
    ? actionTemplates.find(a => a.id === data.action)
    : undefined;

  const RCA_ACTIONS = new Set(['kestrel-trigger-rca', 'kestrel-trigger-cloud-rca']);
  const workflowHasRCA = allNodes.some(n => n.data?.action && RCA_ACTIONS.has(n.data.action));

  // Execute Script: analyze the script to infer named inputs/outputs.
  const [scriptAnalyzing, setScriptAnalyzing] = useState(false);
  const [scriptAnalyzeError, setScriptAnalyzeError] = useState<string | null>(null);

  const handleAnalyzeScript = useCallback(async () => {
    const script = (data.config?.script as string) || '';
    const language = (data.config?.language as string) || 'python';
    if (!script.trim()) {
      setScriptAnalyzeError('Add a script first.');
      return;
    }
    setScriptAnalyzing(true);
    setScriptAnalyzeError(null);
    try {
      const result = await apiClient.parseScriptIO(script, language);
      if (!result.success) {
        setScriptAnalyzeError(result.error || 'Failed to analyze script.');
        return;
      }
      // Merge inferred inputs into the key-value inputs config (preserve any
      // existing values the user already set).
      const existingInputs: Record<string, string> = (data.config?.inputs && typeof data.config.inputs === 'object')
        ? { ...data.config.inputs }
        : {};
      for (const inp of result.inputs || []) {
        if (!inp.key) continue;
        if (existingInputs[inp.key] === undefined) {
          existingInputs[inp.key] = inp.default_value || '';
        }
      }
      // Persist inferred outputs on the node so they surface in the VariablePicker
      // for downstream blocks and are editable here.
      onUpdate(node.id, {
        ...data,
        config: { ...data.config, inputs: existingInputs },
        script_outputs: result.outputs || [],
      });
    } catch (e: any) {
      setScriptAnalyzeError(e?.message || 'Failed to analyze script.');
    } finally {
      setScriptAnalyzing(false);
    }
  }, [data, node.id, onUpdate]);

  const triggerSource = allNodes.find(n => n.type === 'trigger')?.data?.source || 'kubernetes';
  const isRequestTriggered = triggerSource === 'request';

  const [githubBranches, setGithubBranches] = useState<string[]>([]);
  const [githubWorkflows, setGithubWorkflows] = useState<{name: string; file: string}[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  // Kyverno policy names for the trigger filter multi-select, fetched live from
  // the operator (scoped to the first selected cluster when one is chosen).
  const [kyvernoPolicyOptions, setKyvernoPolicyOptions] = useState<{ key: string; name: string }[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);

  const [cfDNSRecords, setCfDNSRecords] = useState<{key: string; name: string}[]>([]);
  const [cfWAFRulesets, setCfWAFRulesets] = useState<{key: string; name: string}[]>([]);
  const [cfWorkerVersions, setCfWorkerVersions] = useState<{key: string; name: string}[]>([]);
  const [cfAccessPolicies, setCfAccessPolicies] = useState<{key: string; name: string}[]>([]);
  const [cfLoadingRecords, setCfLoadingRecords] = useState(false);
  const [cfLoadingRulesets, setCfLoadingRulesets] = useState(false);
  const [cfLoadingVersions, setCfLoadingVersions] = useState(false);
  const [cfLoadingPolicies, setCfLoadingPolicies] = useState(false);

  const [flyioMachines, setFlyioMachines] = useState<{ key: string; name: string }[]>([]);
  const [flyioLoadingMachines, setFlyioLoadingMachines] = useState(false);

  const [nebiusNodeGroups, setNebiusNodeGroups] = useState<{ key: string; name: string }[]>([]);
  const [nebiusLoadingNodeGroups, setNebiusLoadingNodeGroups] = useState(false);
  const [nebiusInstances, setNebiusInstances] = useState<{ key: string; name: string }[]>([]);
  const [nebiusLoadingInstances, setNebiusLoadingInstances] = useState(false);
  const [nebiusSubnets, setNebiusSubnets] = useState<{ key: string; name: string }[]>([]);
  const [nebiusLoadingSubnets, setNebiusLoadingSubnets] = useState(false);
  const [nebiusPlatforms, setNebiusPlatforms] = useState<{ key: string; name: string }[]>([]);
  const [nebiusLoadingPlatforms, setNebiusLoadingPlatforms] = useState(false);
  const [nebiusPresets, setNebiusPresets] = useState<{ key: string; name: string }[]>([]);
  const [nebiusLoadingPresets, setNebiusLoadingPresets] = useState(false);

  const [circleciPipelines, setCircleCIPipelines] = useState<{ key: string; name: string }[]>([]);
  const [circleciLoadingPipelines, setCircleCILoadingPipelines] = useState(false);

  const [terraformRuns, setTerraformRuns] = useState<{ key: string; name: string }[]>([]);
  const [terraformLoadingRuns, setTerraformLoadingRuns] = useState(false);

  const [vaultSecretPaths, setVaultSecretPaths] = useState<{ key: string; name: string }[]>([]);
  const [vaultLoadingSecretPaths, setVaultLoadingSecretPaths] = useState(false);
  const [vaultStaticRoles, setVaultStaticRoles] = useState<{ key: string; name: string }[]>([]);
  const [vaultLoadingStaticRoles, setVaultLoadingStaticRoles] = useState(false);

  const [infisicalEnvironments, setInfisicalEnvironments] = useState<{ key: string; name: string }[]>([]);
  const [infisicalLoadingEnvironments, setInfisicalLoadingEnvironments] = useState(false);
  const [infisicalFolders, setInfisicalFolders] = useState<{ key: string; name: string }[]>([]);
  const [infisicalLoadingFolders, setInfisicalLoadingFolders] = useState(false);

  const [awsBudgetNames, setAwsBudgetNames] = useState<{ key: string; name: string }[]>([]);
  const [awsCEServices, setAwsCEServices] = useState<{ key: string; name: string }[]>([]);
  const [awsResourceIDs, setAwsResourceIDs] = useState<Record<string, { key: string; name: string }[]>>({});
  const [awsLoadingResourceIDs, setAwsLoadingResourceIDs] = useState(false);

  // GCP cost dropdowns. Zones/regions are project-scoped; the resource-name
  // sources are additionally zone- or region-scoped, so they refetch when the
  // location changes.
  const [gcpZones, setGcpZones] = useState<{ key: string; name: string }[]>([]);
  const [gcpRegions, setGcpRegions] = useState<{ key: string; name: string }[]>([]);
  const [gcpBudgetNames, setGcpBudgetNames] = useState<{ key: string; name: string }[]>([]);
  const [gcpBillingServices, setGcpBillingServices] = useState<{ key: string; name: string }[]>([]);
  const [gcpResourceNames, setGcpResourceNames] = useState<Record<string, { key: string; name: string }[]>>({});
  const [gcpLoadingResourceNames, setGcpLoadingResourceNames] = useState(false);

  // GCP compute/ops dropdowns (Phase 2). Node pools are additionally scoped by
  // the selected cluster so a multi-cluster project doesn't offer pools from the
  // wrong cluster.
  const [gcpInstanceGroups, setGcpInstanceGroups] = useState<{ key: string; name: string }[]>([]);
  const [gcpGKEClusters, setGcpGKEClusters] = useState<{ key: string; name: string }[]>([]);
  const [gcpNodePools, setGcpNodePools] = useState<{ key: string; name: string }[]>([]);
  const [gcpCloudRunServices, setGcpCloudRunServices] = useState<{ key: string; name: string }[]>([]);

  // GCP security/data dropdowns (Phase 3). Keyed by source name so one effect
  // and one state slot serve all six, rather than six near-identical pairs.
  const [gcpSecurityData, setGcpSecurityData] = useState<Record<string, { key: string; name: string }[]>>({});
  const [awsOpsData, setAwsOpsData] = useState<Record<string, { key: string; name: string }[]>>({});
  // Trigger-scoped GCP dropdowns. A trigger scopes by a list of projects in
  // signal_filter (not a single config.gcp_project), so the option lists are
  // fetched separately from the action-block dropdowns above.
  const [gcpTriggerZones, setGcpTriggerZones] = useState<{ key: string; name: string }[]>([]);
  const [gcpTriggerRegions, setGcpTriggerRegions] = useState<{ key: string; name: string }[]>([]);
  const [gcpTriggerResources, setGcpTriggerResources] = useState<Record<string, { key: string; name: string }[]>>({});
  const [gcpBuildTriggers, setGcpBuildTriggers] = useState<{ key: string; name: string }[]>([]);
  const [gcpAlertPolicies, setGcpAlertPolicies] = useState<{ key: string; name: string }[]>([]);

  // Unique Pulumi project names derived from the stack list (project/stack).
  const pulumiProjectOptions = React.useMemo(() => {
    const projects = new Set<string>();
    for (const s of pulumiStacks) {
      const slash = s.key.indexOf('/');
      if (slash > 0) projects.add(s.key.slice(0, slash));
    }
    return Array.from(projects).sort().map(p => ({ id: p, name: p }));
  }, [pulumiStacks]);

  const dynamicSourceMap: Record<string, { key: string; name: string }[]> = {
    confluence_spaces: confluenceSpaces,
    jira_projects: jiraProjects,
    linear_teams: linearTeams,
    aws_accounts: awsAccounts,
    vercel_projects: vercelProjects,
    railway_projects: railwayProjects,
    railway_services: railwayServices,
    railway_environments: railwayEnvironments,
    flyio_apps: flyioApps,
    flyio_machines: flyioMachines,
    nebius_projects: nebiusProjects,
    nebius_clusters: nebiusClusters,
    nebius_node_groups: nebiusNodeGroups,
    nebius_instances: nebiusInstances,
    nebius_subnets: nebiusSubnets,
    nebius_platforms: nebiusPlatforms,
    nebius_presets: nebiusPresets,
    beam_deployments: beamDeployments,
    beam_containers: beamContainers,
    beam_machines: beamMachines,
    beam_sandboxes: beamSandboxes,
    daytona_sandboxes: daytonaSandboxes,
    daytona_snapshots: daytonaSnapshots,
    daytona_volumes: daytonaVolumes,
    daytona_targets: daytonaTargets,
    supabase_projects: supabaseProjects,
    supabase_branches: supabaseBranches,
    planetscale_databases: planetscaleDatabases,
    planetscale_branches: planetscaleBranches,
    planetscale_deploy_requests: planetscaleDeployRequests,
    neon_projects: neonProjects,
    neon_branches: neonBranches,
    neon_endpoints: neonEndpoints,
    clickhouse_services: clickhouseServices,
    clickhouse_clickpipes: clickhouseClickPipes,
    clickhouse_backups: clickhouseBackups,
    jenkins_jobs: jenkinsJobs,
    circleci_projects: circleciProjects,
    circleci_pipelines: circleciPipelines,
    sonarcloud_projects: sonarcloudProjects,
    okta_users: oktaUsers,
    okta_groups: oktaGroups,
    terraform_workspaces: terraformWorkspaces,
    databricks_jobs: databricksJobs,
    databricks_clusters: databricksClusters,
    databricks_pipelines: databricksPipelines,
    databricks_warehouses: databricksWarehouses,
    terraform_runs: terraformRuns,
    aws_budget_names: awsBudgetNames,
    aws_ce_services: awsCEServices,
    aws_instance_ids: awsResourceIDs['instances'] || [],
    aws_volume_ids: awsResourceIDs['volumes'] || [],
    aws_eip_allocation_ids: awsResourceIDs['eips'] || [],
    aws_snapshot_ids: awsResourceIDs['snapshots'] || [],
    aws_asg_names: awsOpsData['asg-names'] || [],
    aws_eks_clusters: awsOpsData['eks-clusters'] || [],
    aws_nodegroups: awsOpsData['nodegroups'] || [],
    aws_lambda_functions: awsOpsData['lambda-functions'] || [],
    aws_ecs_services: awsOpsData['ecs-services'] || [],
    aws_log_groups: awsOpsData['log-groups'] || [],
    aws_rds_instances: awsOpsData['rds-instances'] || [],
    aws_dynamodb_tables: awsOpsData['dynamodb-tables'] || [],
    aws_sqs_queues: awsOpsData['sqs-queues'] || [],
    aws_iam_users: awsOpsData['iam-users'] || [],
    gcp_projects: gcpProjects,
    gcp_zones: gcpZones,
    gcp_regions: gcpRegions,
    gcp_budget_names: gcpBudgetNames,
    gcp_billing_services: gcpBillingServices,
    gcp_instance_names: gcpResourceNames['instances'] || [],
    gcp_disk_names: gcpResourceNames['disks'] || [],
    gcp_address_names: gcpResourceNames['addresses'] || [],
    gcp_snapshot_names: gcpResourceNames['snapshots'] || [],
    gcp_instance_groups: gcpInstanceGroups,
    gcp_gke_clusters: gcpGKEClusters,
    gcp_node_pools: gcpNodePools,
    gcp_cloudrun_services: gcpCloudRunServices,
    gcp_service_accounts: gcpSecurityData['service-accounts'] || [],
    gcp_buckets: gcpSecurityData['buckets'] || [],
    gcp_sql_instances: gcpSecurityData['sql-instances'] || [],
    gcp_subscriptions: gcpSecurityData['subscriptions'] || [],
    gcp_dataflow_jobs: gcpSecurityData['dataflow-jobs'] || [],
    gcp_datasets: gcpSecurityData['datasets'] || [],
    pulumi_stacks: pulumiStacks,
    vault_mounts: vaultMounts,
    vault_secret_paths: vaultSecretPaths,
    vault_policies: vaultPolicies,
    vault_database_mounts: vaultDatabaseMounts,
    vault_static_roles: vaultStaticRoles,
    infisical_projects: infisicalProjects,
    infisical_environments: infisicalEnvironments,
    infisical_folders: infisicalFolders,
    infisical_secret_syncs: infisicalSecretSyncs,
    cloudflare_zones: cloudflareZones,
    cloudflare_workers: cloudflareWorkers,
    cloudflare_lb_pools: cloudflareLBPools,
    cloudflare_tunnels: cloudflareTunnels,
    cloudflare_access_apps: cloudflareAccessApps,
    cloudflare_dns_records: cfDNSRecords,
    cloudflare_waf_rulesets: cfWAFRulesets,
    cloudflare_worker_versions: cfWorkerVersions,
    cloudflare_access_policies: cfAccessPolicies,
  };

  const variableGroups: VariableGroup[] = React.useMemo(
    () => (node.type === 'action' || node.type === 'condition' || node.type === 'approval' || node.type === 'loop' || node.type === 'for_each')
      ? buildVariableGroups(node.id, allNodes, edges, actionTemplates, triggerVariables, customBlocks)
      : [],
    [node.id, node.type, allNodes, edges, actionTemplates, triggerVariables, customBlocks]
  );

  // Upstream "Create Sandbox" steps, surfaced as first-class sandbox_id options.
  // A chained sandbox workflow references an upstream Create Sandbox step's
  // output ({{step_outputs.<id>.sandbox_id}}) rather than a literal ID, so these
  // are the primary choices for any sandbox_id field. Beam and Daytona create
  // blocks both emit a sandbox_id output; only offer options matching the
  // current node's integration so a Daytona picker never lists Beam sandboxes.
  const upstreamSandboxOptions: { key: string; name: string }[] = React.useMemo(() => {
    if (node.type !== 'action' && node.type !== 'loop' && node.type !== 'for_each') return [];
    const adjacency = new Map<string, string[]>();
    for (const e of edges) {
      const arr = adjacency.get(e.target) || [];
      arr.push(e.source);
      adjacency.set(e.target, arr);
    }
    const upstream = new Set<string>();
    const queue = [node.id];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const src of adjacency.get(cur) || []) {
        if (!upstream.has(src)) {
          upstream.add(src);
          queue.push(src);
        }
      }
    }
    const createSandboxActions = ['beam-create-sandbox', 'daytona-create-sandbox'];
    const opts: { key: string; name: string }[] = [];
    for (const n of allNodes) {
      if (!upstream.has(n.id)) continue;
      if (createSandboxActions.includes(n.data?.action) && n.data?.integration === data.integration) {
        const label = n.data?.label || 'Create Sandbox';
        opts.push({ key: `{{step_outputs.${n.id}.sandbox_id}}`, name: `${label} (${n.id}) → sandbox_id` });
      }
    }
    return opts;
  }, [node.id, node.type, allNodes, edges, data.integration]);

  // Upstream "Create Instance" steps, surfaced as first-class instance_id
  // options for Nebius pickers. A chained provisioning workflow references the
  // upstream Create Instance step's output ({{step_outputs.<id>.instance_id}})
  // since the instance doesn't exist yet at build time.
  const upstreamNebiusInstanceOptions: { key: string; name: string }[] = React.useMemo(() => {
    if (node.type !== 'action' && node.type !== 'loop' && node.type !== 'for_each') return [];
    if (data.integration !== 'nebius') return [];
    const adjacency = new Map<string, string[]>();
    for (const e of edges) {
      const arr = adjacency.get(e.target) || [];
      arr.push(e.source);
      adjacency.set(e.target, arr);
    }
    const upstream = new Set<string>();
    const queue = [node.id];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const src of adjacency.get(cur) || []) {
        if (!upstream.has(src)) {
          upstream.add(src);
          queue.push(src);
        }
      }
    }
    const opts: { key: string; name: string }[] = [];
    for (const n of allNodes) {
      if (!upstream.has(n.id)) continue;
      if (n.data?.action === 'nebius-create-instance') {
        const label = n.data?.label || 'Create Instance';
        opts.push({ key: `{{step_outputs.${n.id}.instance_id}}`, name: `${label} (${n.id}) → instance_id` });
      }
    }
    return opts;
  }, [node.id, node.type, allNodes, edges, data.integration]);

  // Upstream "Create Node Group" steps, surfaced as first-class node_group_id
  // options for Nebius node-group pickers (scale/delete), mirroring the
  // instance chaining above: the node group doesn't exist at build time, so
  // downstream steps reference {{step_outputs.<id>.node_group_id}}.
  const upstreamNebiusNodeGroupOptions: { key: string; name: string }[] = React.useMemo(() => {
    if (node.type !== 'action' && node.type !== 'loop' && node.type !== 'for_each') return [];
    if (data.integration !== 'nebius') return [];
    const adjacency = new Map<string, string[]>();
    for (const e of edges) {
      const arr = adjacency.get(e.target) || [];
      arr.push(e.source);
      adjacency.set(e.target, arr);
    }
    const upstream = new Set<string>();
    const queue = [node.id];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const src of adjacency.get(cur) || []) {
        if (!upstream.has(src)) {
          upstream.add(src);
          queue.push(src);
        }
      }
    }
    const opts: { key: string; name: string }[] = [];
    for (const n of allNodes) {
      if (!upstream.has(n.id)) continue;
      if (n.data?.action === 'nebius-create-node-group') {
        const label = n.data?.label || 'Create Node Group';
        opts.push({ key: `{{step_outputs.${n.id}.node_group_id}}`, name: `${label} (${n.id}) → node_group_id` });
      }
    }
    return opts;
  }, [node.id, node.type, allNodes, edges, data.integration]);

  // Shared label row for dynamic-select fields: renders the label plus a VariablePicker
  // so users can insert a template variable into ANY dynamic dropdown (across every
  // integration). Inserting a variable routes through the universal {{...}} gate at the
  // top of the dynamic_select branch, which renders a consistent code + Clear view.
  const renderDynamicSelectLabel = (field: { name: string; label: string; required?: boolean; description?: string }) => (
    <>
      <div className="flex items-center justify-between gap-1">
        <Label className="text-xs font-medium">
          {field.label}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {variableGroups.length > 0 && (
          <VariablePicker
            variableGroups={variableGroups}
            onInsert={(v) => handleFieldChange(field.name, v)}
          />
        )}
      </div>
      {field.description && <p className="text-xs text-muted-foreground mb-1">{field.description}</p>}
    </>
  );

  useEffect(() => {
    const selectedRepo = (data.signal_filter?.github_repos || ['*'])[0];
    if (selectedRepo && selectedRepo !== '*' && data.source === 'github') {
      setLoadingBranches(true);
      setLoadingWorkflows(true);
      apiClient.getGitHubBranches(selectedRepo)
        .then(r => setGithubBranches(r.branches || []))
        .catch(() => setGithubBranches([]))
        .finally(() => setLoadingBranches(false));
      apiClient.getGitHubWorkflows(selectedRepo)
        .then(r => setGithubWorkflows(r.workflows || []))
        .catch(() => setGithubWorkflows([]))
        .finally(() => setLoadingWorkflows(false));
    } else {
      setGithubBranches([]);
      setGithubWorkflows([]);
    }
  }, [data.signal_filter?.github_repos, data.source]);

  useEffect(() => {
    if (node.type !== 'trigger' || data.source !== 'kyverno') {
      return;
    }
    const selectedClusters = (data.signal_filter?.cluster_ids || []).filter((id: string) => id !== '*');
    const clusterId = selectedClusters[0] || '';
    apiClient.getKyvernoPolicies(clusterId || undefined)
      .then(policies => setKyvernoPolicyOptions(policies.map(p => ({ key: p.name, name: p.namespace ? `${p.name} (${p.namespace})` : p.name }))))
      .catch(() => setKyvernoPolicyOptions([]));
  }, [node.type, data.source, data.signal_filter?.cluster_ids]);

  useEffect(() => {
    const appName = data.config?.app_name;
    const hasMachineField = (template?.fields || []).some((f: any) => f.dynamic_source === 'flyio_machines');
    if (
      hasMachineField &&
      appName &&
      typeof appName === 'string' &&
      appName.trim() !== '' &&
      !appName.includes('{{')
    ) {
      setFlyioLoadingMachines(true);
      apiClient.getFlyMachines(appName)
        .then(r => setFlyioMachines(r || []))
        .catch(() => setFlyioMachines([]))
        .finally(() => setFlyioLoadingMachines(false));
    } else {
      setFlyioMachines([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.app_name, node.id]);

  useEffect(() => {
    const clusterId = data.config?.cluster_id;
    const hasNodeGroupField = (template?.fields || []).some((f: any) => f.dynamic_source === 'nebius_node_groups');
    if (
      hasNodeGroupField &&
      clusterId &&
      typeof clusterId === 'string' &&
      clusterId.trim() !== '' &&
      !clusterId.includes('{{')
    ) {
      setNebiusLoadingNodeGroups(true);
      apiClient.getNebiusNodeGroups(clusterId)
        .then(r => setNebiusNodeGroups(r || []))
        .catch(() => setNebiusNodeGroups([]))
        .finally(() => setNebiusLoadingNodeGroups(false));
    } else {
      setNebiusNodeGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.cluster_id, node.id]);

  useEffect(() => {
    const projectId = data.config?.project_id;
    const hasInstanceField = (template?.fields || []).some((f: any) => f.dynamic_source === 'nebius_instances');
    if (
      hasInstanceField &&
      projectId &&
      typeof projectId === 'string' &&
      projectId.trim() !== '' &&
      !projectId.includes('{{')
    ) {
      setNebiusLoadingInstances(true);
      apiClient.getNebiusInstances(projectId)
        .then(r => setNebiusInstances(r || []))
        .catch(() => setNebiusInstances([]))
        .finally(() => setNebiusLoadingInstances(false));
    } else {
      setNebiusInstances([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project_id, node.id]);

  useEffect(() => {
    const projectId = data.config?.project_id;
    const hasSubnetField = (template?.fields || []).some((f: any) => f.dynamic_source === 'nebius_subnets');
    if (
      hasSubnetField &&
      projectId &&
      typeof projectId === 'string' &&
      projectId.trim() !== '' &&
      !projectId.includes('{{')
    ) {
      setNebiusLoadingSubnets(true);
      apiClient.getNebiusSubnets(projectId)
        .then(r => setNebiusSubnets(r || []))
        .catch(() => setNebiusSubnets([]))
        .finally(() => setNebiusLoadingSubnets(false));
    } else {
      setNebiusSubnets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project_id, node.id]);

  useEffect(() => {
    const projectId = data.config?.project_id;
    const hasPlatformField = (template?.fields || []).some((f: any) => f.dynamic_source === 'nebius_platforms');
    if (
      hasPlatformField &&
      projectId &&
      typeof projectId === 'string' &&
      projectId.trim() !== '' &&
      !projectId.includes('{{')
    ) {
      setNebiusLoadingPlatforms(true);
      apiClient.getNebiusPlatforms(projectId)
        .then(r => setNebiusPlatforms(r || []))
        .catch(() => setNebiusPlatforms([]))
        .finally(() => setNebiusLoadingPlatforms(false));
    } else {
      setNebiusPlatforms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project_id, node.id]);

  useEffect(() => {
    const projectId = data.config?.project_id;
    const platform = data.config?.platform;
    const hasPresetField = (template?.fields || []).some((f: any) => f.dynamic_source === 'nebius_presets');
    if (
      hasPresetField &&
      projectId && typeof projectId === 'string' && projectId.trim() !== '' && !projectId.includes('{{') &&
      platform && typeof platform === 'string' && platform.trim() !== '' && !platform.includes('{{')
    ) {
      setNebiusLoadingPresets(true);
      apiClient.getNebiusPresets(projectId, platform)
        .then(r => setNebiusPresets(r || []))
        .catch(() => setNebiusPresets([]))
        .finally(() => setNebiusLoadingPresets(false));
    } else {
      setNebiusPresets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project_id, data.config?.platform, node.id]);

  useEffect(() => {
    const projectSlug = data.config?.project_slug;
    const hasPipelineField = (template?.fields || []).some((f: any) => f.dynamic_source === 'circleci_pipelines');
    if (
      hasPipelineField &&
      projectSlug &&
      typeof projectSlug === 'string' &&
      projectSlug.trim() !== '' &&
      !projectSlug.includes('{{')
    ) {
      setCircleCILoadingPipelines(true);
      apiClient.getCircleCIPipelines(projectSlug)
        .then(r => setCircleCIPipelines(r || []))
        .catch(() => setCircleCIPipelines([]))
        .finally(() => setCircleCILoadingPipelines(false));
    } else {
      setCircleCIPipelines([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project_slug, node.id]);

  useEffect(() => {
    const workspace = data.config?.workspace;
    const hasRunField = (template?.fields || []).some((f: any) => f.dynamic_source === 'terraform_runs');
    if (
      hasRunField &&
      workspace &&
      typeof workspace === 'string' &&
      workspace.trim() !== '' &&
      !workspace.includes('{{')
    ) {
      setTerraformLoadingRuns(true);
      apiClient.getTerraformRuns(workspace)
        .then(r => setTerraformRuns(r || []))
        .catch(() => setTerraformRuns([]))
        .finally(() => setTerraformLoadingRuns(false));
    } else {
      setTerraformRuns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.workspace, node.id]);

  // Vault secret paths — scoped to the selected KV mount.
  useEffect(() => {
    const mount = data.config?.mount;
    const hasPathField = (template?.fields || []).some((f: any) => f.dynamic_source === 'vault_secret_paths');
    if (
      hasPathField &&
      mount &&
      typeof mount === 'string' &&
      mount.trim() !== '' &&
      !mount.includes('{{')
    ) {
      setVaultLoadingSecretPaths(true);
      apiClient.getVaultSecretPaths(mount)
        .then(r => setVaultSecretPaths(r || []))
        .catch(() => setVaultSecretPaths([]))
        .finally(() => setVaultLoadingSecretPaths(false));
    } else {
      setVaultSecretPaths([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.mount, node.id]);

  // Vault static roles — scoped to the selected database mount.
  useEffect(() => {
    const mount = data.config?.mount;
    const hasRoleField = (template?.fields || []).some((f: any) => f.dynamic_source === 'vault_static_roles');
    if (
      hasRoleField &&
      (!mount || typeof mount !== 'string' || !mount.includes('{{'))
    ) {
      setVaultLoadingStaticRoles(true);
      apiClient.getVaultStaticRoles(typeof mount === 'string' && mount.trim() !== '' ? mount : undefined)
        .then(r => setVaultStaticRoles(r || []))
        .catch(() => setVaultStaticRoles([]))
        .finally(() => setVaultLoadingStaticRoles(false));
    } else {
      setVaultStaticRoles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.mount, node.id]);

  // Infisical environments — scoped to the selected project.
  useEffect(() => {
    const project = data.config?.project;
    const hasEnvField = (template?.fields || []).some((f: any) => f.dynamic_source === 'infisical_environments');
    if (
      hasEnvField &&
      project &&
      typeof project === 'string' &&
      project.trim() !== '' &&
      !project.includes('{{')
    ) {
      setInfisicalLoadingEnvironments(true);
      apiClient.getInfisicalEnvironments(project)
        .then(r => setInfisicalEnvironments(r || []))
        .catch(() => setInfisicalEnvironments([]))
        .finally(() => setInfisicalLoadingEnvironments(false));
    } else {
      setInfisicalEnvironments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project, node.id]);

  // Infisical folders — scoped to the selected project and environment.
  useEffect(() => {
    const project = data.config?.project;
    const environment = data.config?.environment;
    const hasFolderField = (template?.fields || []).some((f: any) => f.dynamic_source === 'infisical_folders');
    if (
      hasFolderField &&
      project && typeof project === 'string' && project.trim() !== '' && !project.includes('{{') &&
      environment && typeof environment === 'string' && environment.trim() !== '' && !environment.includes('{{')
    ) {
      setInfisicalLoadingFolders(true);
      apiClient.getInfisicalFolders(project, environment)
        .then(r => setInfisicalFolders(r || []))
        .catch(() => setInfisicalFolders([]))
        .finally(() => setInfisicalLoadingFolders(false));
    } else {
      setInfisicalFolders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.project, data.config?.environment, node.id]);

  // AWS budget names — scoped to the selected AWS account.
  useEffect(() => {
    const awsAccount = data.config?.aws_account;
    const hasBudgetField = (template?.fields || []).some((f: any) => f.dynamic_source === 'aws_budget_names');
    if (hasBudgetField && (!awsAccount || typeof awsAccount !== 'string' || !awsAccount.includes('{{'))) {
      apiClient.getAWSBudgetNames(typeof awsAccount === 'string' ? awsAccount : undefined)
        .then(r => setAwsBudgetNames(r || []))
        .catch(() => setAwsBudgetNames([]));
    } else {
      setAwsBudgetNames([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.aws_account, node.id]);

  // AWS Cost Explorer service names — scoped to the selected AWS account.
  useEffect(() => {
    const awsAccount = data.config?.aws_account;
    const hasServiceField = (template?.fields || []).some((f: any) => f.dynamic_source === 'aws_ce_services');
    if (hasServiceField && (!awsAccount || typeof awsAccount !== 'string' || !awsAccount.includes('{{'))) {
      apiClient.getAWSCEServices(typeof awsAccount === 'string' ? awsAccount : undefined)
        .then(r => setAwsCEServices(r || []))
        .catch(() => setAwsCEServices([]));
    } else {
      setAwsCEServices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.aws_account, node.id]);

  // AWS EC2 resource IDs (instances, volumes, EIPs, snapshots) for the cost
  // remediation blocks — scoped to the selected AWS account and region.
  useEffect(() => {
    const sourceToType: Record<string, 'instances' | 'volumes' | 'eips' | 'snapshots'> = {
      aws_instance_ids: 'instances',
      aws_volume_ids: 'volumes',
      aws_eip_allocation_ids: 'eips',
      aws_snapshot_ids: 'snapshots',
    };
    const neededTypes = Array.from(new Set(
      (template?.fields || [])
        .map((f: any) => sourceToType[f.dynamic_source])
        .filter(Boolean)
    )) as ('instances' | 'volumes' | 'eips' | 'snapshots')[];
    const awsAccount = data.config?.aws_account;
    const region = data.config?.region;
    const accountUsable = !awsAccount || (typeof awsAccount === 'string' && !awsAccount.includes('{{'));
    if (neededTypes.length > 0 && accountUsable) {
      setAwsLoadingResourceIDs(true);
      Promise.all(neededTypes.map(t =>
        apiClient.getAWSResourceIDs(
          t,
          typeof awsAccount === 'string' ? awsAccount : undefined,
          typeof region === 'string' && !region.includes('{{') ? region : undefined
        ).then(r => [t, r || []] as const).catch(() => [t, []] as const)
      )).then(entries => {
        setAwsResourceIDs(Object.fromEntries(entries));
      }).finally(() => setAwsLoadingResourceIDs(false));
    } else {
      setAwsResourceIDs({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.aws_account, data.config?.region, node.id]);

  // AWS ops/security/data dropdowns (ASGs, EKS clusters/nodegroups, Lambda
  // functions, ECS services, log groups, RDS instances, DynamoDB tables, SQS
  // queues, IAM users) — scoped to the selected account and region, with
  // nodegroups additionally scoped by the chosen EKS cluster and ECS services
  // by the chosen ECS cluster. Only the sources this block actually declares
  // are fetched.
  useEffect(() => {
    const sourceToPath: Record<string, string> = {
      aws_asg_names: 'asg-names',
      aws_eks_clusters: 'eks-clusters',
      aws_nodegroups: 'nodegroups',
      aws_lambda_functions: 'lambda-functions',
      aws_ecs_services: 'ecs-services',
      aws_log_groups: 'log-groups',
      aws_rds_instances: 'rds-instances',
      aws_dynamodb_tables: 'dynamodb-tables',
      aws_sqs_queues: 'sqs-queues',
      aws_iam_users: 'iam-users',
    };
    const neededPaths = Array.from(new Set(
      (template?.fields || [])
        .map((f: any) => sourceToPath[f.dynamic_source])
        .filter(Boolean)
    )) as string[];
    const awsAccount = data.config?.aws_account;
    const region = data.config?.region;
    const clusterName = data.config?.cluster_name;
    const ecsCluster = data.config?.cluster;
    const usable = (v: any) => typeof v === 'string' && v !== '' && !v.includes('{{') ? v : undefined;
    const accountUsable = !awsAccount || usable(awsAccount) !== undefined || awsAccount === '';
    if (neededPaths.length > 0 && accountUsable) {
      Promise.all(neededPaths.map(path => {
        const params: Record<string, string | undefined> = {
          aws_account: usable(awsAccount),
          region: usable(region),
        };
        if (path === 'nodegroups') params.cluster_name = usable(clusterName);
        if (path === 'ecs-services') params.cluster = usable(ecsCluster);
        return apiClient.getAWSOpsDropdown(path, params)
          .then(r => [path, r || []] as const)
          .catch(() => [path, []] as const);
      })).then(entries => {
        setAwsOpsData(Object.fromEntries(entries));
      });
    } else {
      setAwsOpsData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.aws_account, data.config?.region, data.config?.cluster_name, data.config?.cluster, node.id]);

  // GCP zones and regions — project-scoped. Fetched whenever the block has a
  // location field so the zone dropdown is populated before the user picks
  // resources (the resource dropdowns are zone-scoped).
  useEffect(() => {
    const project = data.config?.gcp_project;
    const fields = template?.fields || [];
    const needsZones = fields.some((f: any) => f.dynamic_source === 'gcp_zones');
    const needsRegions = fields.some((f: any) => f.dynamic_source === 'gcp_regions');
    // A project still holding an unresolved template can't scope a query.
    const projectUsable = !project || (typeof project === 'string' && !project.includes('{{'));
    const projectArg = typeof project === 'string' && !project.includes('{{') ? project : undefined;

    if (needsZones && projectUsable) {
      apiClient.getGCPZones(projectArg).then(r => setGcpZones(r || [])).catch(() => setGcpZones([]));
    } else {
      setGcpZones([]);
    }
    if (needsRegions && projectUsable) {
      apiClient.getGCPRegions(projectArg).then(r => setGcpRegions(r || [])).catch(() => setGcpRegions([]));
    } else {
      setGcpRegions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.gcp_project, node.id]);

  // GCP budget names and billing services — project-scoped.
  useEffect(() => {
    const project = data.config?.gcp_project;
    const fields = template?.fields || [];
    const projectUsable = !project || (typeof project === 'string' && !project.includes('{{'));
    const projectArg = typeof project === 'string' && !project.includes('{{') ? project : undefined;

    if (fields.some((f: any) => f.dynamic_source === 'gcp_budget_names') && projectUsable) {
      apiClient.getGCPBudgetNames(projectArg).then(r => setGcpBudgetNames(r || [])).catch(() => setGcpBudgetNames([]));
    } else {
      setGcpBudgetNames([]);
    }
    if (fields.some((f: any) => f.dynamic_source === 'gcp_billing_services') && projectUsable) {
      apiClient.getGCPBillingServices(projectArg).then(r => setGcpBillingServices(r || [])).catch(() => setGcpBillingServices([]));
    } else {
      setGcpBillingServices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.gcp_project, node.id]);

  // GCP resource names (instances, disks, addresses, snapshots) for the cost
  // remediation blocks — scoped to the selected project plus zone or region.
  useEffect(() => {
    const sourceToType: Record<string, 'instances' | 'disks' | 'addresses' | 'snapshots'> = {
      gcp_instance_names: 'instances',
      gcp_disk_names: 'disks',
      gcp_address_names: 'addresses',
      gcp_snapshot_names: 'snapshots',
    };
    const neededTypes = Array.from(new Set(
      (template?.fields || [])
        .map((f: any) => sourceToType[f.dynamic_source])
        .filter(Boolean)
    )) as ('instances' | 'disks' | 'addresses' | 'snapshots')[];

    const project = data.config?.gcp_project;
    const zone = data.config?.zone;
    const region = data.config?.region;
    const projectUsable = !project || (typeof project === 'string' && !project.includes('{{'));
    const usable = (v: unknown) => (typeof v === 'string' && v !== '' && !v.includes('{{') ? v : undefined);

    if (neededTypes.length > 0 && projectUsable) {
      setGcpLoadingResourceNames(true);
      Promise.all(neededTypes.map(t =>
        apiClient.getGCPResourceNames(t, usable(project), usable(zone), usable(region))
          .then(r => [t, r || []] as const)
          .catch(() => [t, []] as const)
      )).then(entries => {
        setGcpResourceNames(Object.fromEntries(entries));
      }).finally(() => setGcpLoadingResourceNames(false));
    } else {
      setGcpResourceNames({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.gcp_project, data.config?.zone, data.config?.region, node.id]);

  // GCP compute/ops dropdowns. Each source is fetched only when the block
  // actually has a field for it, so a Cloud Run block never pays for a GKE
  // listing.
  useEffect(() => {
    const fields = template?.fields || [];
    const project = data.config?.gcp_project;
    const projectUsable = !project || (typeof project === 'string' && !project.includes('{{'));
    const usable = (v: unknown) => (typeof v === 'string' && v !== '' && !v.includes('{{') ? v : undefined);
    const projectArg = usable(project);

    if (!projectUsable) {
      setGcpInstanceGroups([]);
      setGcpGKEClusters([]);
      setGcpNodePools([]);
      setGcpCloudRunServices([]);
      return;
    }

    if (fields.some((f: any) => f.dynamic_source === 'gcp_instance_groups')) {
      apiClient.getGCPInstanceGroups(projectArg, usable(data.config?.zone))
        .then(r => setGcpInstanceGroups(r || []))
        .catch(() => setGcpInstanceGroups([]));
    } else {
      setGcpInstanceGroups([]);
    }

    if (fields.some((f: any) => f.dynamic_source === 'gcp_gke_clusters')) {
      apiClient.getGCPGKEClusters(projectArg).then(r => setGcpGKEClusters(r || [])).catch(() => setGcpGKEClusters([]));
    } else {
      setGcpGKEClusters([]);
    }

    if (fields.some((f: any) => f.dynamic_source === 'gcp_node_pools')) {
      // Pass the chosen cluster so the pool list is scoped to it.
      apiClient.getGCPNodePools(projectArg, usable(data.config?.cluster_name))
        .then(r => setGcpNodePools(r || []))
        .catch(() => setGcpNodePools([]));
    } else {
      setGcpNodePools([]);
    }

    if (fields.some((f: any) => f.dynamic_source === 'gcp_cloudrun_services')) {
      apiClient.getGCPCloudRunServices(projectArg, usable(data.config?.region))
        .then(r => setGcpCloudRunServices(r || []))
        .catch(() => setGcpCloudRunServices([]));
    } else {
      setGcpCloudRunServices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.gcp_project, data.config?.zone, data.config?.region, data.config?.cluster_name, node.id]);

  // GCP security and data dropdowns. Only the sources this block declares are
  // fetched, so a Pub/Sub block never pays for a Cloud SQL or bucket listing.
  useEffect(() => {
    const sourceToKind: Record<string, 'service-accounts' | 'buckets' | 'sql-instances' | 'subscriptions' | 'dataflow-jobs' | 'datasets'> = {
      gcp_service_accounts: 'service-accounts',
      gcp_buckets: 'buckets',
      gcp_sql_instances: 'sql-instances',
      gcp_subscriptions: 'subscriptions',
      gcp_dataflow_jobs: 'dataflow-jobs',
      gcp_datasets: 'datasets',
    };
    const needed = Array.from(new Set(
      (template?.fields || [])
        .map((f: any) => sourceToKind[f.dynamic_source])
        .filter(Boolean)
    ));

    const project = data.config?.gcp_project;
    const projectUsable = !project || (typeof project === 'string' && !project.includes('{{'));
    const usable = (v: unknown) => (typeof v === 'string' && v !== '' && !v.includes('{{') ? v : undefined);

    if (needed.length === 0 || !projectUsable) {
      setGcpSecurityData({});
      return;
    }
    Promise.all(needed.map(kind =>
      apiClient.getGCPResourceList(kind, usable(project), usable(data.config?.region))
        .then(r => [kind, r || []] as const)
        .catch(() => [kind, []] as const)
    )).then(entries => setGcpSecurityData(Object.fromEntries(entries)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.config?.gcp_project, data.config?.region, node.id]);

  // GCP trigger scoping dropdowns. Only fetched for GCP triggers, and scoped to
  // the first concrete project in the filter so the lists match what the user
  // selected. Each resource kind is fetched only when the trigger's event
  // selection makes that field visible, keeping the API calls proportional to
  // what is actually on screen.
  useEffect(() => {
    if (node.type !== 'trigger' || data.source !== 'gcp') {
      setGcpTriggerZones([]);
      setGcpTriggerRegions([]);
      setGcpTriggerResources({});
      return;
    }
    // Resolve a single concrete project to scope by: template values and the
    // "*" wildcard can't scope an API call, and the dropdown endpoints fall
    // back to the tenant's default connection when no project is given.
    const projects: string[] = data.signal_filter?.gcp_connection_ids || [];
    const project = projects.find(p => typeof p === 'string' && p !== '*' && !p.includes('{{'));

    apiClient.getGCPZones(project).then(r => setGcpTriggerZones(r || [])).catch(() => setGcpTriggerZones([]));
    apiClient.getGCPRegions(project).then(r => setGcpTriggerRegions(r || [])).catch(() => setGcpTriggerRegions([]));

    const events: string[] = data.signal_filter?.gcp_event_types?.length
      ? data.signal_filter.gcp_event_types
      : [data.signal_type || '*'];
    const shows = (fieldKey: string) => {
      const relevant = GCP_FIELD_EVENTS[fieldKey] || [];
      return events.some(e => e === '*' || relevant.includes(e));
    };

    // 'instances' comes from the compute resource endpoint; the rest come from
    // the security/data resource-list endpoint.
    const kinds = (['service-accounts', 'buckets', 'sql-instances', 'subscriptions', 'dataflow-jobs', 'datasets'] as const)
      .filter(kind => shows({
        'service-accounts': 'service_accounts',
        'buckets': 'buckets',
        'sql-instances': 'sql_instances',
        'subscriptions': 'subscriptions',
        'dataflow-jobs': 'dataflow_jobs',
        'datasets': 'datasets',
      }[kind]));

    // Each entry is [state key, option rows]. The helper pins the tuple type so
    // an empty fallback isn't inferred as a readonly empty array, and keeps the
    // five fetches below from repeating the same then/catch shape.
    type GCPOptionRows = { key: string; name: string }[];
    const fetchOptions = (
      key: string,
      request: Promise<GCPOptionRows>,
    ): Promise<[string, GCPOptionRows]> =>
      request
        .then(rows => [key, rows || []] as [string, GCPOptionRows])
        .catch(() => [key, []] as [string, GCPOptionRows]);

    const requests: Promise<[string, GCPOptionRows]>[] = kinds.map(kind =>
      fetchOptions(kind, apiClient.getGCPResourceList(kind, project))
    );
    if (shows('instances')) {
      requests.push(fetchOptions('instances', apiClient.getGCPResourceNames('instances', project)));
    }
    if (shows('instance_groups')) {
      requests.push(fetchOptions('instance-groups', apiClient.getGCPInstanceGroups(project)));
    }
    if (shows('clusters') || shows('node_pools')) {
      requests.push(fetchOptions('gke-clusters', apiClient.getGCPGKEClusters(project)));
    }
    if (shows('services')) {
      requests.push(fetchOptions('cloudrun-services', apiClient.getGCPCloudRunServices(project)));
    }
    // GCP Services, build triggers, and alert policies are scoped by project
    // only, so they are fetched directly rather than through the resource map.
    // These share state with the action-block panel, which populates it from
    // template.fields — a trigger node has no template, so without this the
    // fields fall back to free text.
    // GCP Services is global scoping shown for every event, so it is fetched
    // unconditionally rather than gated on the event selection.
    apiClient.getGCPBillingServices(project)
      .then(r => setGcpBillingServices(r || []))
      .catch(() => setGcpBillingServices([]));
    if (shows('build_triggers')) {
      apiClient.getGCPBuildTriggers(project)
        .then(r => setGcpBuildTriggers(r || []))
        .catch(() => setGcpBuildTriggers([]));
    }
    if (shows('alert_policies')) {
      apiClient.getGCPAlertPolicies(project)
        .then(r => setGcpAlertPolicies(r || []))
        .catch(() => setGcpAlertPolicies([]));
    }
    if (shows('node_pools')) {
      apiClient.getGCPNodePools(project)
        .then(r => setGcpNodePools(r || []))
        .catch(() => setGcpNodePools([]));
    }

    if (requests.length === 0) {
      setGcpTriggerResources({});
      return;
    }
    Promise.all(requests).then(entries => setGcpTriggerResources(Object.fromEntries(entries)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, node.id, data.source, data.signal_type, JSON.stringify(data.signal_filter?.gcp_connection_ids || []), JSON.stringify(data.signal_filter?.gcp_event_types || [])]);

  useEffect(() => {
    const zoneId = data.config?.zone_id;
    if (zoneId && typeof zoneId === 'string' && zoneId.length > 10) {
      setCfLoadingRecords(true);
      setCfLoadingRulesets(true);
      apiClient.getCloudflareDNSRecords(zoneId).then(r => setCfDNSRecords(r)).finally(() => setCfLoadingRecords(false));
      apiClient.getCloudflareWAFRulesets(zoneId).then(r => setCfWAFRulesets(r)).finally(() => setCfLoadingRulesets(false));
    } else {
      setCfDNSRecords([]);
      setCfWAFRulesets([]);
    }
  }, [data.config?.zone_id]);

  useEffect(() => {
    const scriptName = data.config?.script_name;
    if (scriptName && typeof scriptName === 'string' && scriptName.length > 0) {
      setCfLoadingVersions(true);
      apiClient.getCloudflareWorkerVersions(scriptName).then(r => setCfWorkerVersions(r)).finally(() => setCfLoadingVersions(false));
    } else {
      setCfWorkerVersions([]);
    }
  }, [data.config?.script_name]);

  useEffect(() => {
    const appId = data.config?.app_id;
    if (appId && typeof appId === 'string' && appId.length > 10) {
      setCfLoadingPolicies(true);
      apiClient.getCloudflareAccessPolicies(appId).then(r => setCfAccessPolicies(r)).finally(() => setCfLoadingPolicies(false));
    } else {
      setCfAccessPolicies([]);
    }
  }, [data.config?.app_id]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const newConfig = { ...data.config, [fieldName]: value };
    onUpdate(node.id, { ...data, config: newConfig });
  };

  const handleSignalFilterChange = (fieldName: string, value: any) => {
    const newFilter = { ...(data.signal_filter || {}), [fieldName]: value };
    onUpdate(node.id, { ...data, signal_filter: newFilter });
  };

  const handleLabelChange = (value: string) => {
    onUpdate(node.id, { ...data, label: value });
  };

  return (
    <div className="border-l bg-background flex flex-col h-full min-w-0">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-sm">Configure Node</h3>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-xs font-medium">Label</Label>
            <Input
              value={data.label || ''}
              onChange={e => handleLabelChange(e.target.value)}
              className="mt-1 h-8 text-sm"
              placeholder="Display name"
            />
          </div>

          <div className="text-xs text-muted-foreground">
            Type: <span className="font-medium capitalize">{node.type}</span>
            {data.integration && <> · Integration: <span className="font-medium">{data.integration === 'custom' && data.integration_name ? data.integration_name : data.integration}</span></>}
          </div>

          {data.integration === 'custom' && (data.description || data.method) && (
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {data.description && <p>{data.description}</p>}
              {data.method && (
                <p><span className="font-mono font-medium uppercase">{data.method}</span> request</p>
              )}
            </div>
          )}

          {/* Custom block parameter fields */}
          {data.integration === 'custom' && data.custom_parameters?.length > 0 && (
            <>
              {data.custom_parameters.map((p: any) => (
                <div key={p.key}>
                  <Label className="text-xs font-medium">{p.label || p.key}</Label>
                  {p.description && <p className="text-xs text-muted-foreground mb-1">{p.description}</p>}
                  <Input
                    value={data.config?.[p.key] || ''}
                    onChange={e => onUpdate(node.id, { ...data, config: { ...data.config, [p.key]: e.target.value } })}
                    placeholder={p.default_value || `{{request.${p.key}}} or {{step_outputs.action-1.${p.key}}}`}
                    className="mt-1 h-8 text-sm font-mono"
                  />
                </div>
              ))}
            </>
          )}

          {/* Trigger node config */}
          {node.type === 'trigger' && (
            <>
              {data.source !== 'request' && (
              <>
              <div>
                <Label className="text-xs font-medium">Source</Label>
                <p className="text-xs text-muted-foreground mt-1 capitalize">{data.source || 'kubernetes'}</p>
              </div>
              <div>
                <Label className="text-xs font-medium">Signal Type</Label>
                <Input
                  value={data.signal_type || ''}
                  onChange={e => onUpdate(node.id, { ...data, signal_type: e.target.value })}
                  className="mt-1 h-8 text-sm"
                  placeholder={data.source === 'aws' ? 'cloudtrail' : 'pod_status'}
                />
              </div>
              </>
              )}

              {/* K8s-specific filters */}
              {(data.source || 'kubernetes') === 'kubernetes' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Target Clusters</Label>
                    {clusters.length > 0 ? (
                      <ClusterMultiSelect
                        clusters={clusters}
                        selectedIds={data.signal_filter?.cluster_ids || []}
                        onChange={ids => {
                          const currentNS = data.signal_filter?.namespaces || ['*'];
                          const isAllNS = currentNS.length === 0 || (currentNS.length === 1 && currentNS[0] === '*');
                          const newFilter = {
                            ...(data.signal_filter || {}),
                            cluster_ids: ids,
                            ...(!isAllNS ? { namespaces: ['*'], workload_names: ['*'] } : {}),
                          };
                          onUpdate(node.id, { ...data, signal_filter: newFilter });
                        }}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-1">No clusters connected</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Namespaces</Label>
                    <NamespaceMultiSelect
                      clusterIds={data.signal_filter?.cluster_ids || ['*']}
                      selectedNamespaces={data.signal_filter?.namespaces || ['*']}
                      onChange={ns => {
                        const currentWorkloads = data.signal_filter?.workload_names || ['*'];
                        const isAllWorkloads = currentWorkloads.length === 0 || (currentWorkloads.length === 1 && currentWorkloads[0] === '*');
                        const newFilter = {
                          ...(data.signal_filter || {}),
                          namespaces: ns,
                          ...(!isAllWorkloads ? { workload_names: ['*'] } : {}),
                        };
                        onUpdate(node.id, { ...data, signal_filter: newFilter });
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Workloads</Label>
                    <WorkloadMultiSelect
                      clusterIds={data.signal_filter?.cluster_ids || ['*']}
                      namespaces={data.signal_filter?.namespaces || ['*']}
                      selectedWorkloads={data.signal_filter?.workload_names || ['*']}
                      onChange={wl => handleSignalFilterChange('workload_names', wl)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Reasons</Label>
                    <Input
                      value={(data.signal_filter?.reasons || []).join(', ')}
                      onChange={e => {
                        const val = e.target.value.trim();
                        handleSignalFilterChange('reasons', val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
                      }}
                      className="mt-1 h-8 text-sm"
                      placeholder="e.g. CrashLoopBackOff, OOMKilled"
                    />
                  </div>
                </>
              )}

              {/* AWS-specific filters */}
              {data.source === 'aws' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Target Accounts</Label>
                    {awsAccounts && awsAccounts.length > 0 ? (
                      <AWSAccountMultiSelect
                        accounts={awsAccounts}
                        selectedIds={data.signal_filter?.aws_connection_ids || []}
                        onChange={ids => handleSignalFilterChange('aws_connection_ids', ids)}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-1">No AWS accounts connected</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">AWS Services</Label>
                    <Input
                      value={(data.signal_filter?.service_names || []).join(', ')}
                      onChange={e => {
                        const val = e.target.value.trim();
                        handleSignalFilterChange('service_names', val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
                      }}
                      className="mt-1 h-8 text-sm"
                      placeholder="e.g. iam, s3, ec2, rds"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Incident Types</Label>
                    <IncidentTypeMultiSelect
                      selectedServices={data.signal_filter?.service_names || []}
                      selectedTypes={data.signal_filter?.incident_types || []}
                      onChange={types => handleSignalFilterChange('incident_types', types)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Severities</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {['critical', 'high', 'medium', 'low'].map(sev => {
                        const selected = (data.signal_filter?.severities || []).includes(sev);
                        return (
                          <button
                            key={sev}
                            type="button"
                            onClick={() => {
                              const current = data.signal_filter?.severities || [];
                              const updated = selected ? current.filter((s: string) => s !== sev) : [...current, sev];
                              handleSignalFilterChange('severities', updated);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >
                            {sev}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Regions</Label>
                    <AWSRegionMultiSelect
                      selectedRegions={(data.signal_filter?.regions || []).filter((r: string) => r !== '*')}
                      onChange={regions => handleSignalFilterChange('regions', regions)}
                    />
                  </div>
                </>
              )}

              {/* GCP-specific filters. Scoping fields first (projects, events,
                  regions, zones), then the threshold/resource fields that only
                  apply to the events this trigger is narrowed to. */}
              {data.source === 'gcp' && (() => {
                const gcpEvents: string[] = data.signal_filter?.gcp_event_types?.length
                  ? data.signal_filter.gcp_event_types
                  : [data.signal_type || '*'];
                const showsGCP = (fieldKey: string) => {
                  const relevant = GCP_FIELD_EVENTS[fieldKey] || [];
                  return gcpEvents.some((e: string) => e === '*' || relevant.includes(e));
                };
                const opts = (rows: { key: string; name: string }[] | undefined) =>
                  (rows || []).map(r => ({ id: r.key, name: r.name }));
                return (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Target Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('gcp_connection_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={(gcpProjects || []).map(p => ({ id: p.key, name: p.name }))}
                      selectedValues={data.signal_filter?.gcp_connection_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('gcp_connection_ids', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict this trigger to specific connected GCP projects. Leave as "All projects" to match every connected project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={GCP_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.gcp_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('gcp_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Narrow this trigger to specific GCP events. The scoping and threshold fields below adapt to the events you select.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Regions</Label>
                    <MultiSelectWithTemplate
                      options={opts(gcpTriggerRegions)}
                      selectedValues={data.signal_filter?.gcp_regions || ['*']}
                      onChange={vals => handleSignalFilterChange('gcp_regions', vals)}
                      allLabel="All regions"
                      placeholder="All regions (or region name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only enforced for events that carry a region, so a project-wide event is never filtered out by this list.</p>
                  </div>
                  {showsGCP('zones') && (
                    <div>
                      <Label className="text-xs font-medium">Zones</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerZones)}
                        selectedValues={data.signal_filter?.gcp_zones || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_zones', vals)}
                        allLabel="All zones"
                        placeholder="All zones (or zone name)"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Applies to instance, instance-group, and node-pool events.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">GCP Services</Label>
                    <MultiSelectWithTemplate
                      options={opts(gcpBillingServices)}
                      selectedValues={data.signal_filter?.gcp_service_names || ['*']}
                      onChange={vals => handleSignalFilterChange('gcp_service_names', vals)}
                      allLabel="All services"
                      placeholder="e.g. Compute Engine, BigQuery"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {gcpBillingServices.length > 0
                        ? 'Restrict to specific GCP services (as they appear in billing and Cloud Monitoring).'
                        // The picker is populated from the BigQuery billing export, which is the
                        // only place service names as they appear in billing can be enumerated.
                        // Without it the field still works, so say why rather than leaving the
                        // user wondering whether the dropdown is broken.
                        : 'Restrict to specific GCP services. Configure the BigQuery billing export on this project to pick from the services you actually use; until then, type names as they appear in billing (e.g. Compute Engine).'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Severities</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {GCP_SEVERITY_OPTIONS.map(sev => {
                        const selected = (data.signal_filter?.gcp_severities || []).includes(sev);
                        return (
                          <button
                            key={sev}
                            type="button"
                            onClick={() => {
                              const current = data.signal_filter?.gcp_severities || [];
                              const updated = selected ? current.filter((s: string) => s !== sev) : [...current, sev];
                              handleSignalFilterChange('gcp_severities', updated);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >
                            {sev}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Select none to match every severity. Applies to Security Command Center findings and Cloud Monitoring alerts.</p>
                  </div>
                  {showsGCP('instances') && (
                    <div>
                      <Label className="text-xs font-medium">Instances</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['instances'])}
                        selectedValues={data.signal_filter?.gcp_instance_names || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_instance_names', vals)}
                        allLabel="All instances"
                        placeholder="All instances (or instance name)"
                      />
                    </div>
                  )}
                  {showsGCP('instance_groups') && (
                    <div>
                      <Label className="text-xs font-medium">Instance Groups</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['instance-groups'])}
                        selectedValues={data.signal_filter?.gcp_instance_groups || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_instance_groups', vals)}
                        allLabel="All instance groups"
                        placeholder="All instance groups (or MIG name)"
                      />
                    </div>
                  )}
                  {showsGCP('clusters') && (
                    <div>
                      <Label className="text-xs font-medium">GKE Clusters</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['gke-clusters'])}
                        selectedValues={data.signal_filter?.gcp_clusters || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_clusters', vals)}
                        allLabel="All clusters"
                        placeholder="All clusters (or cluster name)"
                      />
                    </div>
                  )}
                  {showsGCP('node_pools') && (
                    <div>
                      <Label className="text-xs font-medium">Node Pools</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpNodePools)}
                        selectedValues={data.signal_filter?.gcp_node_pools || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_node_pools', vals)}
                        allLabel="All node pools"
                        placeholder="All node pools (or pool name)"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Node pool names are cluster-scoped; leave as "All node pools" to match every pool in the selected clusters.</p>
                    </div>
                  )}
                  {showsGCP('services') && (
                    <div>
                      <Label className="text-xs font-medium">Cloud Run Services</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['cloudrun-services'])}
                        selectedValues={data.signal_filter?.gcp_services || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_services', vals)}
                        allLabel="All services"
                        placeholder="All services (or service name)"
                      />
                    </div>
                  )}
                  {showsGCP('build_triggers') && (
                    <div>
                      <Label className="text-xs font-medium">Build Triggers</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpBuildTriggers)}
                        selectedValues={data.signal_filter?.gcp_build_triggers || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_build_triggers', vals)}
                        allLabel="All build triggers"
                        placeholder="All build triggers (or trigger name)"
                      />
                    </div>
                  )}
                  {showsGCP('alert_policies') && (
                    <div>
                      <Label className="text-xs font-medium">Alert Policies</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpAlertPolicies)}
                        selectedValues={data.signal_filter?.gcp_alert_policies || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_alert_policies', vals)}
                        allLabel="All alert policies"
                        placeholder="All alert policies (or display name)"
                      />
                    </div>
                  )}
                  {showsGCP('finding_categories') && (
                    <div>
                      <Label className="text-xs font-medium">Finding Categories</Label>
                      <Input
                        value={(data.signal_filter?.gcp_finding_categories || []).filter((v: string) => v !== '*').join(', ')}
                        onChange={e => {
                          const val = e.target.value.trim();
                          handleSignalFilterChange('gcp_finding_categories', val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
                        }}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. PUBLIC_BUCKET_ACL, OVER_PRIVILEGED_ACCOUNT"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Security Command Center finding categories. Leave blank to match every category.</p>
                    </div>
                  )}
                  {showsGCP('service_accounts') && (
                    <div>
                      <Label className="text-xs font-medium">Service Accounts</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['service-accounts'])}
                        selectedValues={data.signal_filter?.gcp_service_accounts || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_service_accounts', vals)}
                        allLabel="All service accounts"
                        placeholder="All service accounts (or email)"
                      />
                    </div>
                  )}
                  {showsGCP('roles') && (
                    <div>
                      <Label className="text-xs font-medium">IAM Roles</Label>
                      <MultiSelectWithTemplate
                        options={GCP_PRIVILEGED_ROLE_OPTIONS}
                        selectedValues={data.signal_filter?.gcp_roles || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_roles', vals)}
                        allLabel="Built-in privileged roles"
                        allowTemplate={false}
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Which role grants count as privileged. Leave as the default to use the built-in privileged role list.</p>
                    </div>
                  )}
                  {showsGCP('buckets') && (
                    <div>
                      <Label className="text-xs font-medium">Buckets</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['buckets'])}
                        selectedValues={data.signal_filter?.gcp_buckets || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_buckets', vals)}
                        allLabel="All buckets"
                        placeholder="All buckets (or bucket name)"
                      />
                    </div>
                  )}
                  {showsGCP('datasets') && (
                    <div>
                      <Label className="text-xs font-medium">BigQuery Datasets</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['datasets'])}
                        selectedValues={data.signal_filter?.gcp_datasets || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_datasets', vals)}
                        allLabel="All datasets"
                        placeholder="All datasets (or dataset ID)"
                      />
                    </div>
                  )}
                  {showsGCP('sql_instances') && (
                    <div>
                      <Label className="text-xs font-medium">Cloud SQL Instances</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['sql-instances'])}
                        selectedValues={data.signal_filter?.gcp_sql_instances || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_sql_instances', vals)}
                        allLabel="All instances"
                        placeholder="All instances (or instance name)"
                      />
                    </div>
                  )}
                  {showsGCP('subscriptions') && (
                    <div>
                      <Label className="text-xs font-medium">Pub/Sub Subscriptions</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['subscriptions'])}
                        selectedValues={data.signal_filter?.gcp_subscriptions || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_subscriptions', vals)}
                        allLabel="All subscriptions"
                        placeholder="All subscriptions (or subscription ID)"
                      />
                    </div>
                  )}
                  {showsGCP('dataflow_jobs') && (
                    <div>
                      <Label className="text-xs font-medium">Dataflow Jobs</Label>
                      <MultiSelectWithTemplate
                        options={opts(gcpTriggerResources['dataflow-jobs'])}
                        selectedValues={data.signal_filter?.gcp_dataflow_jobs || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_dataflow_jobs', vals)}
                        allLabel="All jobs"
                        placeholder="All jobs (or job name)"
                      />
                    </div>
                  )}
                  {showsGCP('log') && (
                    <>
                      <div>
                        <Label className="text-xs font-medium">Log Filter</Label>
                        <Input
                          value={data.signal_filter?.gcp_log_filter || ''}
                          onChange={e => handleSignalFilterChange('gcp_log_filter', e.target.value)}
                          className="mt-1 h-8 text-sm font-mono text-xs"
                          placeholder='e.g. resource.type="cloud_run_revision"'
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">Cloud Logging filter expression, ANDed with the severity below.</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Minimum Log Severity</Label>
                        <select
                          value={data.signal_filter?.gcp_log_severity || 'ERROR'}
                          onChange={e => handleSignalFilterChange('gcp_log_severity', e.target.value)}
                          className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="WARNING">Warning</option>
                          <option value="ERROR">Error</option>
                          <option value="CRITICAL">Critical</option>
                          <option value="ALERT">Alert</option>
                          <option value="EMERGENCY">Emergency</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Minimum Error Count</Label>
                        <Input
                          type="number"
                          min={1}
                          value={data.signal_filter?.gcp_min_error_count ?? 1}
                          onChange={e => handleSignalFilterChange('gcp_min_error_count', Number(e.target.value))}
                          className="mt-1 h-8 text-sm"
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">Fire only when at least this many matching entries appear in one poll window.</p>
                      </div>
                    </>
                  )}
                  {showsGCP('min_cost_impact') && (
                    <div>
                      <Label className="text-xs font-medium">Minimum Cost Impact (USD)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={data.signal_filter?.gcp_min_cost_impact ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_min_cost_impact', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 100"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Ignore anomalies and forecast overruns below this dollar impact.</p>
                    </div>
                  )}
                  {showsGCP('spike_percent') && (
                    <div>
                      <Label className="text-xs font-medium">Spend Spike Threshold (%)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.gcp_spike_percent ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_spike_percent', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 50"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire when daily spend rises by at least this percent over the trailing baseline.</p>
                    </div>
                  )}
                  {showsGCP('budget_threshold') && (
                    <div>
                      <Label className="text-xs font-medium">Budget Threshold (%)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={200}
                        value={data.signal_filter?.gcp_budget_threshold_percent ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_budget_threshold_percent', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 80"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire when a budget's month-to-date spend crosses this percent of its amount.</p>
                    </div>
                  )}
                  {showsGCP('min_savings') && (
                    <div>
                      <Label className="text-xs font-medium">Minimum Monthly Savings (USD)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={data.signal_filter?.gcp_min_monthly_savings ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_min_monthly_savings', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 25"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Only report idle resources worth at least this much per month.</p>
                    </div>
                  )}
                  {showsGCP('idle_types') && (
                    <div>
                      <Label className="text-xs font-medium">Idle Resource Types</Label>
                      <MultiSelectWithTemplate
                        options={GCP_IDLE_RESOURCE_TYPE_OPTIONS}
                        selectedValues={data.signal_filter?.gcp_idle_resource_types || ['*']}
                        onChange={vals => handleSignalFilterChange('gcp_idle_resource_types', vals)}
                        allLabel="All resource types"
                        allowTemplate={false}
                      />
                    </div>
                  )}
                  {showsGCP('unhealthy_threshold') && (
                    <div>
                      <Label className="text-xs font-medium">Unhealthy Instance Threshold</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.gcp_unhealthy_threshold ?? 1}
                        onChange={e => handleSignalFilterChange('gcp_unhealthy_threshold', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">How many unhealthy instances a managed group must have before firing.</p>
                    </div>
                  )}
                  {showsGCP('min_duration') && (
                    <div>
                      <Label className="text-xs font-medium">Minimum Duration (minutes)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={data.signal_filter?.gcp_min_duration_minutes ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_min_duration_minutes', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 10"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Dwell time before a degraded node pool fires, so routine upgrades stay quiet.</p>
                    </div>
                  )}
                  {showsGCP('key_age') && (
                    <div>
                      <Label className="text-xs font-medium">Maximum Key Age (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.gcp_max_key_age_days ?? 90}
                        onChange={e => handleSignalFilterChange('gcp_max_key_age_days', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire when a user-managed service account key is older than this.</p>
                    </div>
                  )}
                  {showsGCP('max_bytes_billed') && (
                    <div>
                      <Label className="text-xs font-medium">Max Bytes Billed (GB)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.gcp_max_bytes_billed_gb ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_max_bytes_billed_gb', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 1000"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Flag BigQuery queries that scan more than this many gigabytes.</p>
                    </div>
                  )}
                  {showsGCP('max_backlog') && (
                    <div>
                      <Label className="text-xs font-medium">Max Backlog Messages</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.gcp_max_backlog_messages ?? ''}
                        onChange={e => handleSignalFilterChange('gcp_max_backlog_messages', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                        placeholder="e.g. 10000"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire when undelivered messages on a subscription exceed this count.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.gcp_poll_interval || ''}
                      onChange={e => handleSignalFilterChange('gcp_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Default for this event family</option>
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                      <option value="1h">1 hour</option>
                      <option value="6h">6 hours</option>
                      <option value="12h">12 hours</option>
                      <option value="24h">24 hours</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">GCP has no tenant-wide outbound webhooks for these services, so all GCP events are poll-based. Defaults are per family: ops polls every few minutes, while cost polls run hourly because they query your BigQuery billing export (which you pay for).</p>
                  </div>
                </>
                );
              })()}

              {/* PagerDuty-specific filters */}
              {data.source === 'pagerduty' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">PD Service Names</Label>
                    <Input
                      value={(data.signal_filter?.pd_service_names || []).join(', ')}
                      onChange={e => {
                        const val = e.target.value.trim();
                        handleSignalFilterChange('pd_service_names', val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
                      }}
                      className="mt-1 h-8 text-sm"
                      placeholder="All services (comma-separated to filter)"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Urgency</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {['high', 'low'].map(val => {
                        const selected = (data.signal_filter?.pd_urgencies || []).includes(val);
                        return (
                          <button key={val} type="button"
                            onClick={() => {
                              const current = data.signal_filter?.pd_urgencies || [];
                              const updated = selected ? current.filter((s: string) => s !== val) : [...current, val];
                              handleSignalFilterChange('pd_urgencies', updated.length > 0 ? updated : undefined);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >{val}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Severity</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {['critical', 'error', 'warning', 'info'].map(val => {
                        const selected = (data.signal_filter?.pd_severities || []).includes(val);
                        return (
                          <button key={val} type="button"
                            onClick={() => {
                              const current = data.signal_filter?.pd_severities || [];
                              const updated = selected ? current.filter((s: string) => s !== val) : [...current, val];
                              handleSignalFilterChange('pd_severities', updated.length > 0 ? updated : undefined);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >{val}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Status</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {['triggered', 'acknowledged', 'resolved'].map(val => {
                        const selected = (data.signal_filter?.pd_statuses || []).includes(val);
                        return (
                          <button key={val} type="button"
                            onClick={() => {
                              const current = data.signal_filter?.pd_statuses || [];
                              const updated = selected ? current.filter((s: string) => s !== val) : [...current, val];
                              handleSignalFilterChange('pd_statuses', updated.length > 0 ? updated : undefined);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >{val}</button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* PostHog-specific filters */}
              {data.source === 'posthog' && (data.signal_type || '').replace(/[$_-]/g, '').toLowerCase() === 'rageclick' && (
                <div>
                  <Label className="text-xs font-medium">Min Rage Clicks per Session</Label>
                  <Input
                    type="number"
                    min={1}
                    value={data.signal_filter?.min_rage_clicks ?? 1}
                    onChange={e => {
                      const val = parseInt(e.target.value, 10);
                      handleSignalFilterChange('min_rage_clicks', isNaN(val) || val < 1 ? 1 : val);
                    }}
                    className="mt-1 h-8 text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Only trigger after this many rage clicks in the same session.</p>
                </div>
              )}

              {/* PostHog Console Error log level filter */}
              {data.source === 'posthog' && (data.signal_type === '$log_entry' || data.signal_type === 'log_entry') && (
                <div>
                  <Label className="text-xs font-medium">Log Level</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {['error', 'warn', 'info', 'debug'].map(val => {
                      const current = data.signal_filter?.posthog_log_level || '';
                      const selected = current === val;
                      return (
                        <button key={val} type="button"
                          onClick={() => {
                            handleSignalFilterChange('posthog_log_level', selected ? undefined : val);
                          }}
                          className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                        >{val}</button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Filter to a specific console log level.</p>
                </div>
              )}

              {/* PostHog Log Alert filters */}
              {data.source === 'posthog' && data.signal_type === 'log_alert' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Severity Levels</Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {['fatal', 'error', 'warn', 'info', 'debug'].map(val => {
                        const selected = (data.signal_filter?.posthog_log_alert_severities || []).includes(val);
                        return (
                          <button key={val} type="button"
                            onClick={() => {
                              const current = data.signal_filter?.posthog_log_alert_severities || [];
                              const updated = selected ? current.filter((s: string) => s !== val) : [...current, val];
                              handleSignalFilterChange('posthog_log_alert_severities', updated.length > 0 ? updated : undefined);
                            }}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 border-border hover:bg-muted'}`}
                          >{val}</button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Log severity levels to trigger on. Leave empty for error + fatal.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Error Threshold</Label>
                    <Input
                      type="number"
                      min={1}
                      value={data.signal_filter?.posthog_log_alert_threshold ?? 1}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        handleSignalFilterChange('posthog_log_alert_threshold', isNaN(val) || val < 1 ? 1 : val);
                      }}
                      className="mt-1 h-8 text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Number of ERROR/FATAL logs in the time window before triggering.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Time Window (minutes)</Label>
                    <Input
                      type="number"
                      min={1}
                      value={data.signal_filter?.posthog_log_alert_window_mins ?? 5}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        handleSignalFilterChange('posthog_log_alert_window_mins', isNaN(val) || val < 1 ? 1 : val);
                      }}
                      className="mt-1 h-8 text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Rolling time window to count error logs in.</p>
                  </div>
                </>
              )}

              {/* Request-specific filters — only show categories for catch-all "any" signal type */}
              {data.source === 'request' && data.signal_type === 'any' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Request Sources</Label>
                    <RequestSourcesMultiSelect
                      selected={data.signal_filter?.request_sources || []}
                      onChange={ids => handleSignalFilterChange('request_sources', ids)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to accept from any source.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Request Types</Label>
                    <RequestTypesMultiSelect
                      selected={data.signal_filter?.request_types || []}
                      onChange={ids => handleSignalFilterChange('request_types', ids)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave empty to trigger on any request type.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Allowed Users</Label>
                    <AllowedUsersMultiSelect
                      selectedIds={data.signal_filter?.allowed_user_ids || []}
                      onChange={ids => handleSignalFilterChange('allowed_user_ids', ids)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Restrict to specific users. If both Users and Groups are empty, anyone can trigger.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Allowed Groups</Label>
                    <AllowedGroupsMultiSelect
                      selectedIds={data.signal_filter?.allowed_group_ids || []}
                      onChange={ids => handleSignalFilterChange('allowed_group_ids', ids)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Restrict to specific groups. If both Users and Groups are empty, anyone can trigger.</p>
                  </div>
                  {/* Integration-specific scoping fields */}
                  {(() => {
                    const category = (data.signal_filter?.request_categories || [])[0] || '';
                    const scopeValues = data.signal_filter?.request_scope || {};
                    const updateScope = (field: string, values: string[]) => {
                      const newScope = { ...scopeValues, [field]: values };
                      const newFilter = { ...(data.signal_filter || {}), request_scope: newScope };
                      onUpdate(node.id, { ...data, signal_filter: newFilter });
                    };
                    switch (category) {
                      case 'k8s':
                      case 'helm':
                        return (
                          <>
                            <div>
                              <Label className="text-xs font-medium">Allowed Clusters</Label>
                              <ClusterMultiSelect
                                clusters={clusters}
                                selectedIds={scopeValues.clusters || ['*']}
                                onChange={ids => updateScope('clusters', ids)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Allowed Namespaces</Label>
                              <NamespaceMultiSelect
                                clusterIds={(scopeValues.clusters || ['*']).filter((id: string) => id !== '*')}
                                selectedNamespaces={scopeValues.namespaces || ['*']}
                                onChange={ns => updateScope('namespaces', ns)}
                              />
                            </div>
                          </>
                        );
                      case 'cloud':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed AWS Accounts</Label>
                            <AWSAccountMultiSelect
                              accounts={awsAccounts}
                              selectedIds={scopeValues.aws_accounts || ['*']}
                              onChange={ids => updateScope('aws_accounts', ids)}
                            />
                          </div>
                        );
                      case 'gcp':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed GCP Projects</Label>
                            <ClusterMultiSelect
                              clusters={(gcpProjects || []).map(p => ({ id: p.key, name: p.name }))}
                              selectedIds={scopeValues.projects || ['*']}
                              onChange={ids => updateScope('projects', ids)}
                              allLabel="All Projects"
                            />
                          </div>
                        );
                      case 'cloudflare':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Zones</Label>
                            <ClusterMultiSelect
                              clusters={cloudflareZones.map(z => ({ id: z.key, name: z.name }))}
                              selectedIds={scopeValues.zones || ['*']}
                              onChange={ids => updateScope('zones', ids)}
                              allLabel="All Zones"
                            />
                          </div>
                        );
                      case 'pagerduty':
                        return null;
                      case 'argocd':
                        return (
                          <>
                            <div>
                              <Label className="text-xs font-medium">Allowed Clusters</Label>
                              <ClusterMultiSelect
                                clusters={clusters}
                                selectedIds={scopeValues.clusters || ['*']}
                                onChange={ids => updateScope('clusters', ids)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Allowed Applications</Label>
                              <ArgoCDAppSelect
                                value={(scopeValues.apps || []).join(',')}
                                onChange={v => updateScope('apps', v ? v.split(',') : [])}
                                placeholder="All applications"
                                clusterId={(scopeValues.clusters || []).filter((id: string) => id !== '*')[0] || ''}
                              />
                              <p className="text-xs text-muted-foreground mt-1">Select a cluster first, then pick an application. Leave empty to allow all.</p>
                            </div>
                          </>
                        );
                      case 'argo-rollouts':
                      case 'fluxcd':
                        return (
                          <>
                            <div>
                              <Label className="text-xs font-medium">Allowed Clusters</Label>
                              <ClusterMultiSelect
                                clusters={clusters}
                                selectedIds={scopeValues.clusters || ['*']}
                                onChange={ids => updateScope('clusters', ids)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Allowed Namespaces</Label>
                              <NamespaceMultiSelect
                                clusterIds={(scopeValues.clusters || ['*']).filter((id: string) => id !== '*')}
                                selectedNamespaces={scopeValues.namespaces || ['*']}
                                onChange={ns => updateScope('namespaces', ns)}
                              />
                            </div>
                          </>
                        );
                      case 'karpenter':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Clusters</Label>
                            <ClusterMultiSelect
                              clusters={clusters}
                              selectedIds={scopeValues.clusters || ['*']}
                              onChange={ids => updateScope('clusters', ids)}
                            />
                          </div>
                        );
                      case 'kyverno':
                        return (
                          <>
                            <div>
                              <Label className="text-xs font-medium">Allowed Clusters</Label>
                              <ClusterMultiSelect
                                clusters={clusters}
                                selectedIds={scopeValues.clusters || ['*']}
                                onChange={ids => updateScope('clusters', ids)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Allowed Namespaces</Label>
                              <NamespaceMultiSelect
                                clusterIds={(scopeValues.clusters || ['*']).filter((id: string) => id !== '*')}
                                selectedNamespaces={scopeValues.namespaces || ['*']}
                                onChange={ns => updateScope('namespaces', ns)}
                              />
                            </div>
                          </>
                        );
                      case 'trivy':
                        return (
                          <>
                            <div>
                              <Label className="text-xs font-medium">Allowed Clusters</Label>
                              <ClusterMultiSelect
                                clusters={clusters}
                                selectedIds={scopeValues.clusters || ['*']}
                                onChange={ids => updateScope('clusters', ids)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Allowed Namespaces</Label>
                              <NamespaceMultiSelect
                                clusterIds={(scopeValues.clusters || ['*']).filter((id: string) => id !== '*')}
                                selectedNamespaces={scopeValues.namespaces || ['*']}
                                onChange={ns => updateScope('namespaces', ns)}
                              />
                            </div>
                          </>
                        );
                      case 'github':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Repositories</Label>
                            <ClusterMultiSelect
                              clusters={githubRepos.map(r => ({ id: r, name: r }))}
                              selectedIds={scopeValues.repos || ['*']}
                              onChange={ids => updateScope('repos', ids)}
                              allLabel="All Repositories"
                            />
                          </div>
                        );
                      case 'gitlab':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Repositories</Label>
                            <ClusterMultiSelect
                              clusters={gitlabRepos.map(r => ({ id: r, name: r }))}
                              selectedIds={scopeValues.repos || ['*']}
                              onChange={ids => updateScope('repos', ids)}
                              allLabel="All Repositories"
                            />
                          </div>
                        );
                      case 'vercel':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Projects</Label>
                            <ClusterMultiSelect
                              clusters={vercelProjects.map(p => ({ id: p.key, name: p.name }))}
                              selectedIds={scopeValues.projects || ['*']}
                              onChange={ids => updateScope('projects', ids)}
                              allLabel="All Projects"
                            />
                          </div>
                        );
                      case 'railway':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Projects</Label>
                            <ClusterMultiSelect
                              clusters={railwayProjects.map(p => ({ id: p.key, name: p.name }))}
                              selectedIds={scopeValues.projects || ['*']}
                              onChange={ids => updateScope('projects', ids)}
                              allLabel="All Projects"
                            />
                          </div>
                        );
                      case 'flyio':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Apps</Label>
                            <ClusterMultiSelect
                              clusters={flyioApps.map(a => ({ id: a.key, name: a.name }))}
                              selectedIds={scopeValues.apps || ['*']}
                              onChange={ids => updateScope('apps', ids)}
                              allLabel="All Apps"
                            />
                          </div>
                        );
                      case 'nebius':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Projects</Label>
                            <ClusterMultiSelect
                              clusters={nebiusProjects.map(p => ({ id: p.key, name: p.name }))}
                              selectedIds={scopeValues.projects || ['*']}
                              onChange={ids => updateScope('projects', ids)}
                              allLabel="All Projects"
                            />
                          </div>
                        );
                      case 'beam':
                        return (
                          <div>
                            <Label className="text-xs font-medium">Allowed Deployments</Label>
                            <ClusterMultiSelect
                              clusters={beamDeployments.map(d => ({ id: d.key, name: d.name }))}
                              selectedIds={scopeValues.deployments || ['*']}
                              onChange={ids => updateScope('deployments', ids)}
                              allLabel="All Deployments"
                            />
                          </div>
                        );
                      default:
                        return null;
                    }
                  })()}
                </>
              )}


              {/* GitHub-specific filters */}
              {data.source === 'github' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Repository</Label>
                    {githubRepos.length > 0 ? (
                      <GitHubSearchSelect
                        options={githubRepos.map(r => ({ value: r, label: r }))}
                        value={(data.signal_filter?.github_repos || ['*'])[0] === '*' ? '' : (data.signal_filter?.github_repos || ['*'])[0]}
                        onChange={v => handleSignalFilterChange('github_repos', v ? [v] : ['*'])}
                        placeholder="All repositories"
                        allLabel="All repositories"
                      />
                    ) : (
                      <Input
                        value={(data.signal_filter?.github_repos || ['*']).filter((r: string) => r !== '*').join(', ')}
                        onChange={e => {
                          const val = e.target.value.trim();
                          const repos = val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : ['*'];
                          handleSignalFilterChange('github_repos', repos);
                        }}
                        className="mt-1 h-8 text-sm"
                        placeholder="All repositories (or org/repo-name)"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Branch</Label>
                    {loadingBranches ? (
                      <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                    ) : githubBranches.length > 0 ? (
                      <GitHubSearchSelect
                        options={githubBranches.map(b => ({ value: b, label: b }))}
                        value={(data.signal_filter?.github_branches || ['*'])[0] === '*' ? '' : (data.signal_filter?.github_branches || ['*'])[0]}
                        onChange={v => handleSignalFilterChange('github_branches', v ? [v] : ['*'])}
                        placeholder="All branches"
                        allLabel="All branches"
                      />
                    ) : (
                      <Input
                        value={(data.signal_filter?.github_branches || ['*']).filter((b: string) => b !== '*').join(', ')}
                        onChange={e => {
                          const val = e.target.value.trim();
                          const branches = val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : ['*'];
                          handleSignalFilterChange('github_branches', branches);
                        }}
                        className="mt-1 h-8 text-sm"
                        placeholder="All branches (or main, production)"
                      />
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter by target/base branch. Leave empty for all.</p>
                  </div>
                  {(data.signal_type || '').startsWith('workflow_run') && (
                    <div>
                      <Label className="text-xs font-medium">Workflow Name</Label>
                      {loadingWorkflows ? (
                        <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                      ) : githubWorkflows.length > 0 ? (
                        <GitHubSearchSelect
                          options={githubWorkflows.map(wf => ({ value: wf.name, label: wf.name }))}
                          value={(data.signal_filter?.github_workflow_names || ['*'])[0] === '*' ? '' : (data.signal_filter?.github_workflow_names || ['*'])[0]}
                          onChange={v => handleSignalFilterChange('github_workflow_names', v ? [v] : ['*'])}
                          placeholder="All workflows"
                          allLabel="All workflows"
                        />
                      ) : (
                        <Input
                          value={(data.signal_filter?.github_workflow_names || ['*']).filter((w: string) => w !== '*').join(', ')}
                          onChange={e => {
                            const val = e.target.value.trim();
                            const names = val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : ['*'];
                            handleSignalFilterChange('github_workflow_names', names);
                          }}
                          className="mt-1 h-8 text-sm"
                          placeholder="All workflows (or deploy, ci, test)"
                        />
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">Filter by GitHub Actions workflow name.</p>
                    </div>
                  )}
                </>
              )}
              {data.source === 'flyio' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">App</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('fly_app_names', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={flyioApps.map(a => ({ id: a.key, name: a.name }))}
                      selectedValues={data.signal_filter?.fly_app_names || ['*']}
                      onChange={vals => handleSignalFilterChange('fly_app_names', vals)}
                      allLabel="All apps"
                      placeholder="All apps (or app name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Fly apps. Leave as "All apps" to match every app. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={FLY_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.fly_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('fly_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific synthesized Fly Machine lifecycle events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Regions</Label>
                    <MultiSelectWithTemplate
                      options={FLY_REGION_OPTIONS}
                      selectedValues={data.signal_filter?.fly_regions || ['*']}
                      onChange={vals => handleSignalFilterChange('fly_regions', vals)}
                      allLabel="All regions"
                      placeholder="All regions (or region code)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only trigger for machines in these Fly regions. Leave as "All regions" to match every region.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <Select
                      value={data.signal_filter?.fly_poll_interval || '5m'}
                      onValueChange={v => handleSignalFilterChange('fly_poll_interval', v)}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1m">1 minute</SelectItem>
                        <SelectItem value="5m">5 minutes</SelectItem>
                        <SelectItem value="15m">15 minutes</SelectItem>
                        <SelectItem value="30m">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Fly Machines API to detect this trigger.</p>
                  </div>
                </>
              )}
              {data.source === 'databricks' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Jobs</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('databricks_job_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={databricksJobs.map(j => ({ id: j.key, name: j.name }))}
                      selectedValues={data.signal_filter?.databricks_job_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('databricks_job_ids', vals)}
                      allLabel="All jobs"
                      placeholder="All jobs (or job ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter job-run events to one or more Databricks jobs. Leave as "All jobs" to match every job. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={DATABRICKS_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.databricks_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('databricks_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific synthesized Databricks events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Clusters</Label>
                    <MultiSelectWithTemplate
                      options={databricksClusters.map(c => ({ id: c.key, name: c.name }))}
                      selectedValues={data.signal_filter?.databricks_cluster_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('databricks_cluster_ids', vals)}
                      allLabel="All clusters"
                      placeholder="All clusters (or cluster ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter cluster events to one or more clusters. Only applies to cluster triggers.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Pipelines</Label>
                    <MultiSelectWithTemplate
                      options={databricksPipelines.map(p => ({ id: p.key, name: p.name }))}
                      selectedValues={data.signal_filter?.databricks_pipeline_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('databricks_pipeline_ids', vals)}
                      allLabel="All pipelines"
                      placeholder="All pipelines (or pipeline ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter DLT pipeline events to one or more pipelines. Only applies to pipeline triggers.</p>
                  </div>
                  {(data.signal_type === 'job_run.long_running' || (data.signal_filter?.databricks_event_types || []).includes('job_run.long_running')) && (
                    <div>
                      <Label className="text-xs font-medium">Duration Threshold (minutes)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.databricks_min_duration_minutes ?? 60}
                        onChange={e => handleSignalFilterChange('databricks_min_duration_minutes', parseInt(e.target.value, 10) || 60)}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire when a still-running job run exceeds this duration.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <Select
                      value={data.signal_filter?.databricks_poll_interval || '5m'}
                      onValueChange={v => handleSignalFilterChange('databricks_poll_interval', v)}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1m">1 minute</SelectItem>
                        <SelectItem value="5m">5 minutes</SelectItem>
                        <SelectItem value="15m">15 minutes</SelectItem>
                        <SelectItem value="30m">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Databricks API to detect this trigger.</p>
                  </div>
                </>
              )}
              {data.source === 'nebius' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('nebius_project_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={nebiusProjects.map(p => ({ id: p.key, name: p.name }))}
                      selectedValues={data.signal_filter?.nebius_project_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('nebius_project_ids', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Nebius projects. Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Clusters</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('nebius_cluster_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={nebiusClusters.map(c => ({ id: c.key, name: c.name }))}
                      selectedValues={data.signal_filter?.nebius_cluster_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('nebius_cluster_ids', vals)}
                      allLabel="All clusters"
                      placeholder="All clusters (or cluster ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only trigger for these managed Kubernetes clusters. Leave as "All clusters" to match every cluster. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={NEBIUS_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.nebius_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('nebius_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific synthesized Nebius node and instance events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <Select
                      value={data.signal_filter?.nebius_poll_interval || '5m'}
                      onValueChange={v => handleSignalFilterChange('nebius_poll_interval', v)}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1m">1 minute</SelectItem>
                        <SelectItem value="5m">5 minutes</SelectItem>
                        <SelectItem value="15m">15 minutes</SelectItem>
                        <SelectItem value="30m">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Nebius API to detect this trigger.</p>
                  </div>
                </>
              )}
              {data.source === 'beam' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Deployments</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('beam_deployment_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={beamDeployments.map(d => ({ id: d.key, name: d.name }))}
                      selectedValues={data.signal_filter?.beam_deployment_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('beam_deployment_ids', vals)}
                      allLabel="All deployments"
                      placeholder="All deployments (or deployment ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Beam deployments. Leave as "All deployments" to match every deployment. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={BEAM_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.beam_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('beam_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific synthesized Beam task, container, deployment, and machine events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <Select
                      value={data.signal_filter?.beam_poll_interval || '5m'}
                      onValueChange={v => handleSignalFilterChange('beam_poll_interval', v)}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1m">1 minute</SelectItem>
                        <SelectItem value="5m">5 minutes</SelectItem>
                        <SelectItem value="15m">15 minutes</SelectItem>
                        <SelectItem value="30m">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Beam API to detect this trigger.</p>
                  </div>
                </>
              )}
              {data.source === 'daytona' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Sandboxes</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('daytona_sandbox_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={daytonaSandboxes.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.daytona_sandbox_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('daytona_sandbox_ids', vals)}
                      allLabel="All sandboxes"
                      placeholder="All sandboxes (or sandbox ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Daytona sandboxes. Leave as "All sandboxes" to match every sandbox. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={DAYTONA_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.daytona_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('daytona_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Daytona sandbox, snapshot, and volume events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                </>
              )}
              {data.source === 'supabase' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('supabase_project_refs', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={supabaseProjects.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.supabase_project_refs || ['*']}
                      onChange={vals => handleSignalFilterChange('supabase_project_refs', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project ref)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Supabase projects. Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={SUPABASE_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.supabase_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('supabase_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Supabase events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Tables (Database Row Event only)</Label>
                    <MultiSelectWithTemplate
                      options={[]}
                      selectedValues={data.signal_filter?.supabase_tables || ['*']}
                      onChange={vals => handleSignalFilterChange('supabase_tables', vals)}
                      allLabel="All tables"
                      placeholder="All tables (or table name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For the Database Row Event trigger, restrict to specific tables. Leave as "All tables" to match every table.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.supabase_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('supabase_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Supabase Management API for this trigger (control-plane events are poll-based). Does not apply to Database Row Events.</p>
                  </div>
                </>
              )}
              {data.source === 'planetscale' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Databases</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('planetscale_databases', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={planetscaleDatabases.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.planetscale_databases || ['*']}
                      onChange={vals => handleSignalFilterChange('planetscale_databases', vals)}
                      allLabel="All databases"
                      placeholder="All databases (or database name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more PlanetScale databases. Leave as "All databases" to match every database. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Branches</Label>
                    <MultiSelectWithTemplate
                      options={planetscaleBranches.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.planetscale_branches || ['*']}
                      onChange={vals => handleSignalFilterChange('planetscale_branches', vals)}
                      allLabel="All branches"
                      placeholder="All branches (or branch name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For branch and deploy-request events, restrict to specific branches. Leave as "All branches" to match every branch.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={PLANETSCALE_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.planetscale_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('planetscale_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific PlanetScale events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Backup Poll Interval</Label>
                    <select
                      value={data.signal_filter?.planetscale_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('planetscale_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only applies to backup.completed / backup.failed (poll-based). All other PlanetScale events are webhook-delivered in real time.</p>
                  </div>
                </>
              )}
              {data.source === 'neon' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('neon_project_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={neonProjects.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.neon_project_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('neon_project_ids', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Neon projects. Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Branches</Label>
                    <MultiSelectWithTemplate
                      options={neonBranches.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.neon_branch_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('neon_branch_ids', vals)}
                      allLabel="All branches"
                      placeholder="All branches (or branch ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For branch events, restrict to specific branches. Leave as "All branches" to match every branch.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={NEON_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.neon_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('neon_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Neon events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.neon_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('neon_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Neon API for this trigger. Neon has no control-plane webhooks, so all Neon events are poll-based.</p>
                  </div>
                </>
              )}
              {data.source === 'clickhouse' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Services</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('clickhouse_service_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={clickhouseServices.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.clickhouse_service_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('clickhouse_service_ids', vals)}
                      allLabel="All services"
                      placeholder="All services (or service ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more ClickHouse Cloud services. Leave as "All services" to match every service. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={CLICKHOUSE_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.clickhouse_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('clickhouse_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific ClickHouse events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  {(data.signal_filter?.clickhouse_event_types || [data.signal_type]).some((et: string) => et === 'query.error_spike' || et === '*') && (
                    <div>
                      <Label className="text-xs font-medium">Error Spike Threshold</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.clickhouse_error_threshold ?? 10}
                        onChange={e => handleSignalFilterChange('clickhouse_error_threshold', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire query.error_spike when at least this many new failed queries/inserts are observed within one poll window.</p>
                    </div>
                  )}
                  {(data.signal_filter?.clickhouse_event_types || [data.signal_type]).some((et: string) => et === 'usage.threshold' || et === '*') && (
                    <>
                      <div>
                        <Label className="text-xs font-medium">Monthly Budget (CHC)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={data.signal_filter?.clickhouse_usage_budget_chc ?? ''}
                          placeholder="e.g. 500"
                          onChange={e => handleSignalFilterChange('clickhouse_usage_budget_chc', Number(e.target.value))}
                          className="mt-1 h-8 text-sm"
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">Monthly spend budget in ClickHouse Credits. Required for the usage.threshold trigger to fire.</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Usage Threshold (%)</Label>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={data.signal_filter?.clickhouse_usage_threshold_percent ?? 80}
                          onChange={e => handleSignalFilterChange('clickhouse_usage_threshold_percent', Number(e.target.value))}
                          className="mt-1 h-8 text-sm"
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">Fire usage.threshold when month-to-date spend crosses this percent of the budget (at most once per month).</p>
                      </div>
                    </>
                  )}
                  {(data.signal_filter?.clickhouse_event_types || [data.signal_type]).some((et: string) => et === 'service.too_many_parts' || et === '*') && (
                    <div>
                      <Label className="text-xs font-medium">Parts Threshold</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.clickhouse_parts_threshold ?? 3000}
                        onChange={e => handleSignalFilterChange('clickhouse_parts_threshold', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire service.too_many_parts when the max part count for a partition reaches this value.</p>
                    </div>
                  )}
                  {(data.signal_filter?.clickhouse_event_types || [data.signal_type]).some((et: string) => et === 'service.high_query_concurrency' || et === '*') && (
                    <div>
                      <Label className="text-xs font-medium">Concurrency Threshold</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.clickhouse_concurrency_threshold ?? 1000}
                        onChange={e => handleSignalFilterChange('clickhouse_concurrency_threshold', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire service.high_query_concurrency when concurrent running queries reach this value.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.clickhouse_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('clickhouse_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the ClickHouse Cloud API for this trigger. ClickHouse Cloud has no control-plane webhooks, so all ClickHouse events are poll-based.</p>
                  </div>
                </>
              )}
              {data.source === 'jenkins' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Jobs</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('jenkins_jobs', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={jenkinsJobs.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.jenkins_jobs || ['*']}
                      onChange={vals => handleSignalFilterChange('jenkins_jobs', vals)}
                      allLabel="All jobs"
                      placeholder="All jobs (or job full name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Jenkins jobs (use the full name for folder-nested jobs, e.g. folder/job). Leave as "All jobs" to match every job. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={JENKINS_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.jenkins_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('jenkins_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to build started or build completed events. Leave as "All event types" to match both.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Build Statuses</Label>
                    <MultiSelectWithTemplate
                      options={JENKINS_BUILD_STATUS_OPTIONS}
                      selectedValues={data.signal_filter?.jenkins_build_statuses || ['*']}
                      onChange={vals => handleSignalFilterChange('jenkins_build_statuses', vals)}
                      allLabel="All statuses"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For completed builds, restrict to specific results (SUCCESS, FAILURE, UNSTABLE, ABORTED). Ignored for build.started events.</p>
                  </div>
                </>
              )}
              {data.source === 'circleci' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('circleci_projects', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={circleciProjects.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.circleci_projects || ['*']}
                      onChange={vals => handleSignalFilterChange('circleci_projects', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or slug, e.g. gh/org/repo)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more CircleCI project slugs (e.g. gh/org/repo). Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={CIRCLECI_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.circleci_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('circleci_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to workflow-completed or job-completed webhook events. Leave as "All event types" to match both.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Branches</Label>
                    <MultiSelectWithTemplate
                      options={[]}
                      selectedValues={data.signal_filter?.circleci_branches || ['*']}
                      onChange={vals => handleSignalFilterChange('circleci_branches', vals)}
                      allLabel="All branches"
                      placeholder="All branches (or branch name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific branches (e.g. main). Leave as "All branches" to match every branch. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Statuses</Label>
                    <MultiSelectWithTemplate
                      options={CIRCLECI_STATUS_OPTIONS}
                      selectedValues={data.signal_filter?.circleci_statuses || ['*']}
                      onChange={vals => handleSignalFilterChange('circleci_statuses', vals)}
                      allLabel="All statuses"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific workflow/job outcomes (success, failed, error, canceled, unauthorized). Leave as "All statuses" to match any outcome.</p>
                  </div>
                </>
              )}
              {data.source === 'sonarcloud' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('sonarcloud_projects', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={sonarcloudProjects.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.sonarcloud_projects || ['*']}
                      onChange={vals => handleSignalFilterChange('sonarcloud_projects', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project key)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more SonarCloud project keys. Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={SONARCLOUD_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.sonarcloud_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('sonarcloud_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to completed analyses or failed analysis tasks. Leave as "All event types" to match both.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Quality Gate Statuses</Label>
                    <MultiSelectWithTemplate
                      options={SONARCLOUD_QUALITY_GATE_STATUS_OPTIONS}
                      selectedValues={data.signal_filter?.sonarcloud_quality_gate_statuses || ['*']}
                      onChange={vals => handleSignalFilterChange('sonarcloud_quality_gate_statuses', vals)}
                      allLabel="All statuses"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For completed analyses, restrict to quality gate results (OK, ERROR, NONE). Ignored for analysis.failed events.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Branches</Label>
                    <MultiSelectWithTemplate
                      options={[]}
                      selectedValues={data.signal_filter?.sonarcloud_branches || ['*']}
                      onChange={vals => handleSignalFilterChange('sonarcloud_branches', vals)}
                      allLabel="All branches"
                      placeholder="All branches (or branch name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific branches (e.g. main). Leave as "All branches" to match every branch. Supports template variables.</p>
                  </div>
                </>
              )}
              {data.source === 'okta' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={OKTA_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.okta_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('okta_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Okta System Log event types. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Target Users</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('okta_target_users', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={oktaUsers.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.okta_target_users || ['*']}
                      onChange={vals => handleSignalFilterChange('okta_target_users', vals)}
                      allLabel="All users"
                      placeholder="All users (or login/email)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to events about specific users (login/email). Leave as "All users" to match everyone. Supports template variables.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Actors</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('okta_actors', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={oktaUsers.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.okta_actors || ['*']}
                      onChange={vals => handleSignalFilterChange('okta_actors', vals)}
                      allLabel="All actors"
                      placeholder="All actors (or login/email)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to events performed by specific admins/users. Leave as "All actors" to match anyone. Supports template variables.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Target Groups</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('okta_target_groups', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={oktaGroups.map(s => ({ id: s.name, name: s.name }))}
                      selectedValues={data.signal_filter?.okta_target_groups || ['*']}
                      onChange={vals => handleSignalFilterChange('okta_target_groups', vals)}
                      allLabel="All groups"
                      placeholder="All groups (or group name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For group membership events, restrict to specific group names (e.g. Okta Administrators). Leave as "All groups" to match any group. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Outcomes</Label>
                    <MultiSelectWithTemplate
                      options={OKTA_OUTCOME_OPTIONS}
                      selectedValues={data.signal_filter?.okta_outcomes || ['*']}
                      onChange={vals => handleSignalFilterChange('okta_outcomes', vals)}
                      allLabel="All outcomes"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to SUCCESS or FAILURE outcomes. Leave as "All outcomes" to match both.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.okta_poll_interval || '1m'}
                      onChange={e => handleSignalFilterChange('okta_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel tails the Okta System Log for this trigger. Only subscribed event types are requested. 1 minute is recommended for security-response triggers.</p>
                  </div>
                </>
              )}
              {data.source === 'kyverno' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Target Clusters</Label>
                    {clusters.length > 0 ? (
                      <ClusterMultiSelect
                        clusters={clusters}
                        selectedIds={data.signal_filter?.cluster_ids || []}
                        onChange={ids => {
                          const currentNS = data.signal_filter?.namespaces || ['*'];
                          const isAllNS = currentNS.length === 0 || (currentNS.length === 1 && currentNS[0] === '*');
                          const newFilter = {
                            ...(data.signal_filter || {}),
                            cluster_ids: ids,
                            ...(!isAllNS ? { namespaces: ['*'] } : {}),
                          };
                          onUpdate(node.id, { ...data, signal_filter: newFilter });
                        }}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-1">No clusters connected</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to violations from specific clusters. Leave as "All clusters" to match every cluster with Kyverno installed.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Namespaces</Label>
                    <NamespaceMultiSelect
                      clusterIds={data.signal_filter?.cluster_ids || ['*']}
                      selectedNamespaces={data.signal_filter?.namespaces || ['*']}
                      onChange={ns => handleSignalFilterChange('namespaces', ns)}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to violations on resources in specific namespaces. Leave as "All namespaces" to include cluster-scoped resources too.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Policies</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('kyverno_policies', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={kyvernoPolicyOptions.map(p => ({ id: p.key, name: p.name }))}
                      selectedValues={data.signal_filter?.kyverno_policies || ['*']}
                      onChange={vals => handleSignalFilterChange('kyverno_policies', vals)}
                      allLabel="All policies"
                      placeholder="All policies (or policy name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to violations produced by specific Kyverno policies (e.g. disallow-privileged-containers). Leave as "All policies" to match any. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Severities</Label>
                    <MultiSelectWithTemplate
                      options={KYVERNO_SEVERITY_OPTIONS}
                      selectedValues={data.signal_filter?.kyverno_severities || ['*']}
                      onChange={vals => handleSignalFilterChange('kyverno_severities', vals)}
                      allLabel="All severities"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific severities as annotated on the policy (policies.kyverno.io/severity). Leave as "All severities" to match any, including policies without a severity annotation.</p>
                  </div>
                </>
              )}
              {data.source === 'trivy' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Target Clusters</Label>
                    {clusters.length > 0 ? (
                      <ClusterMultiSelect
                        clusters={clusters}
                        selectedIds={data.signal_filter?.cluster_ids || []}
                        onChange={ids => {
                          const currentNS = data.signal_filter?.namespaces || ['*'];
                          const isAllNS = currentNS.length === 0 || (currentNS.length === 1 && currentNS[0] === '*');
                          const newFilter = {
                            ...(data.signal_filter || {}),
                            cluster_ids: ids,
                            ...(!isAllNS ? { namespaces: ['*'] } : {}),
                          };
                          onUpdate(node.id, { ...data, signal_filter: newFilter });
                        }}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic mt-1">No clusters connected</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to findings from specific clusters. Leave as "All clusters" to match every cluster with trivy-operator installed.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Namespaces</Label>
                    <NamespaceMultiSelect
                      clusterIds={data.signal_filter?.cluster_ids || ['*']}
                      selectedNamespaces={data.signal_filter?.namespaces || ['*']}
                      onChange={ns => handleSignalFilterChange('namespaces', ns)}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to findings on workloads in specific namespaces. Leave as "All namespaces" to include cluster-scoped reports too.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Severities</Label>
                    <MultiSelectWithTemplate
                      options={TRIVY_SEVERITY_OPTIONS}
                      selectedValues={data.signal_filter?.trivy_severities || ['*']}
                      onChange={vals => handleSignalFilterChange('trivy_severities', vals)}
                      allLabel="All severities"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific finding severities as reported by Trivy. Leave as "All severities" to match any.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Resource Kinds</Label>
                    <MultiSelectWithTemplate
                      options={TRIVY_RESOURCE_KIND_OPTIONS}
                      selectedValues={data.signal_filter?.trivy_resource_kinds || ['*']}
                      onChange={vals => handleSignalFilterChange('trivy_resource_kinds', vals)}
                      allLabel="All resource kinds"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to findings on specific workload or RBAC resource kinds. Leave as "All resource kinds" to match any.</p>
                  </div>
                </>
              )}
              {data.source === 'terraform' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Workspaces</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('terraform_workspaces', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={terraformWorkspaces.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.terraform_workspaces || ['*']}
                      onChange={vals => handleSignalFilterChange('terraform_workspaces', vals)}
                      allLabel="All workspaces"
                      placeholder="All workspaces (or workspace name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Terraform Cloud workspaces. Leave as "All workspaces" to match every workspace in the organization. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={TERRAFORM_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.terraform_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('terraform_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific run or health-assessment notification events. Leave as "All event types" to match every event.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Run Statuses</Label>
                    <MultiSelectWithTemplate
                      options={TERRAFORM_RUN_STATUS_OPTIONS}
                      selectedValues={data.signal_filter?.terraform_run_statuses || ['*']}
                      onChange={vals => handleSignalFilterChange('terraform_run_statuses', vals)}
                      allLabel="All statuses"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific run statuses at notification time (planned, applied, errored, ...). Ignored for assessment events.</p>
                  </div>
                </>
              )}
              {data.source === 'pulumi' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Stacks</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('pulumi_stacks', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={pulumiStacks.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.pulumi_stacks || ['*']}
                      onChange={vals => handleSignalFilterChange('pulumi_stacks', vals)}
                      allLabel="All stacks"
                      placeholder="All stacks (or project/stack)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Pulumi stacks (project/stack). Leave as "All stacks" to match every stack in the organization. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Projects</Label>
                    <MultiSelectWithTemplate
                      options={pulumiProjectOptions}
                      selectedValues={data.signal_filter?.pulumi_projects || ['*']}
                      onChange={vals => handleSignalFilterChange('pulumi_projects', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project name)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Pulumi projects. Leave as "All projects" to match every project.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={PULUMI_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.pulumi_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('pulumi_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific stack, update, deployment, drift, or policy webhook events. Leave as "All event types" to match every event.</p>
                  </div>
                </>
              )}
              {data.source === 'vault' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Secrets Engine Mounts</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('vault_mounts', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={vaultMounts.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.vault_mounts || ['*']}
                      onChange={vals => handleSignalFilterChange('vault_mounts', vals)}
                      allLabel="All KV mounts"
                      placeholder="All KV mounts (or mount path)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For secret events, restrict to one or more KV v2 mounts (e.g. secret/). Leave as "All KV mounts" to watch every mount. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Secret Paths</Label>
                    <MultiSelectWithTemplate
                      options={[]}
                      selectedValues={data.signal_filter?.vault_secret_paths || ['*']}
                      onChange={vals => handleSignalFilterChange('vault_secret_paths', vals)}
                      allLabel="All paths"
                      placeholder="All paths (or path prefix, e.g. app/prod)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For secret events, restrict to path prefixes within the mount (e.g. app/prod). Leave as "All paths" to watch everything.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={VAULT_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.vault_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('vault_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Vault events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  {(data.signal_filter?.vault_event_types || [data.signal_type]).some((et: string) => et === 'secret.stale' || et === '*') && (
                    <div>
                      <Label className="text-xs font-medium">Max Secret Age (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={data.signal_filter?.vault_secret_max_age_days ?? 90}
                        onChange={e => handleSignalFilterChange('vault_secret_max_age_days', Number(e.target.value))}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Fire secret.stale when a secret's latest version is at least this old — use it to drive rotation workflows.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.vault_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('vault_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Vault API for this trigger. Vault has no outbound webhooks, so all Vault events are poll-based. Only secret metadata is read — never values. 1 minute is recommended for seal/health triggers.</p>
                  </div>
                </>
              )}
              {data.source === 'infisical' && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-1">
                      <Label className="text-xs font-medium">Projects</Label>
                      {variableGroups.length > 0 && (
                        <VariablePicker
                          variableGroups={variableGroups}
                          onInsert={(v) => handleSignalFilterChange('infisical_project_ids', [v])}
                        />
                      )}
                    </div>
                    <MultiSelectWithTemplate
                      options={infisicalProjects.map(s => ({ id: s.key, name: s.name }))}
                      selectedValues={data.signal_filter?.infisical_project_ids || ['*']}
                      onChange={vals => handleSignalFilterChange('infisical_project_ids', vals)}
                      allLabel="All projects"
                      placeholder="All projects (or project ID)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter to one or more Infisical projects. Leave as "All projects" to match every project. Supports template variables.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Environments</Label>
                    <MultiSelectWithTemplate
                      options={INFISICAL_ENVIRONMENT_OPTIONS}
                      selectedValues={data.signal_filter?.infisical_environments || ['*']}
                      onChange={vals => handleSignalFilterChange('infisical_environments', vals)}
                      allLabel="All environments"
                      placeholder="All environments (or slug, e.g. prod)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For secret events, restrict to environment slugs (e.g. prod). Leave as "All environments" to match every environment.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Folder Paths</Label>
                    <MultiSelectWithTemplate
                      options={[]}
                      selectedValues={data.signal_filter?.infisical_secret_paths || ['*']}
                      onChange={vals => handleSignalFilterChange('infisical_secret_paths', vals)}
                      allLabel="All paths"
                      placeholder="All paths (or path prefix, e.g. /backend)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">For secret events, restrict to folder path prefixes (e.g. /backend). Leave as "All paths" to watch everything.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Event Types</Label>
                    <MultiSelectWithTemplate
                      options={INFISICAL_EVENT_TYPE_OPTIONS}
                      selectedValues={data.signal_filter?.infisical_event_types || ['*']}
                      onChange={vals => handleSignalFilterChange('infisical_event_types', vals)}
                      allLabel="All event types"
                      allowTemplate={false}
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Restrict to specific Infisical events. Leave as "All event types" to match every event this trigger detects.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Poll Interval</Label>
                    <select
                      value={data.signal_filter?.infisical_poll_interval || '5m'}
                      onChange={e => handleSignalFilterChange('infisical_poll_interval', e.target.value)}
                      className="mt-1 w-full block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1m">1 minute</option>
                      <option value="5m">5 minutes</option>
                      <option value="15m">15 minutes</option>
                      <option value="30m">30 minutes</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often Kestrel polls the Infisical audit log and project APIs for this trigger. All Infisical events are poll-based. Secret values are never read.</p>
                  </div>
                </>
              )}
              {data.source === 'schedule' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Interval</Label>
                    <Select
                      value={data.signal_filter?.schedule_interval || 'daily'}
                      onValueChange={v => handleSignalFilterChange('schedule_interval', v)}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">How often this workflow runs.</p>
                  </div>
                  {(data.signal_filter?.schedule_interval || 'daily') === 'weekly' && (
                    <div>
                      <Label className="text-xs font-medium">Day of Week</Label>
                      <Select
                        value={String(data.signal_filter?.schedule_day_of_week ?? 1)}
                        onValueChange={v => handleSignalFilterChange('schedule_day_of_week', parseInt(v, 10))}
                      >
                        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Sunday</SelectItem>
                          <SelectItem value="1">Monday</SelectItem>
                          <SelectItem value="2">Tuesday</SelectItem>
                          <SelectItem value="3">Wednesday</SelectItem>
                          <SelectItem value="4">Thursday</SelectItem>
                          <SelectItem value="5">Friday</SelectItem>
                          <SelectItem value="6">Saturday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(data.signal_filter?.schedule_interval || 'daily') === 'monthly' && (
                    <div>
                      <Label className="text-xs font-medium">Day of Month</Label>
                      <Select
                        value={String(data.signal_filter?.schedule_day_of_month || 1)}
                        onValueChange={v => handleSignalFilterChange('schedule_day_of_month', parseInt(v, 10))}
                      >
                        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Days 1-28 only, so the schedule fires every month.</p>
                    </div>
                  )}
                  {(data.signal_filter?.schedule_interval || 'daily') !== 'hourly' ? (
                    <div>
                      <Label className="text-xs font-medium">Time (UTC)</Label>
                      <Input
                        type="time"
                        value={data.signal_filter?.schedule_time_utc || '09:00'}
                        onChange={e => handleSignalFilterChange('schedule_time_utc', e.target.value)}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Time of day in UTC (24-hour) when the workflow runs.</p>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs font-medium">Minute of Hour</Label>
                      <Input
                        type="time"
                        value={data.signal_filter?.schedule_time_utc || '09:00'}
                        onChange={e => handleSignalFilterChange('schedule_time_utc', e.target.value)}
                        className="mt-1 h-8 text-sm"
                      />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Only the minute portion is used for hourly schedules (e.g. 09:30 fires at :30 every hour).</p>
                    </div>
                  )}
                </>
              )}
              {data.source === 'datadog' && (
                <>
                  <div>
                    <Label className="text-xs font-medium">Monitor Name</Label>
                    <Input
                      value={(data.signal_filter?.datadog_monitor_names || []).filter((m: string) => m !== '*').join(', ')}
                      onChange={e => {
                        const val = e.target.value.trim();
                        const names = val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                        handleSignalFilterChange('datadog_monitor_names', names.length > 0 ? names : undefined);
                      }}
                      className="mt-1 h-8 text-sm"
                      placeholder="All monitors (or substring filter)"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Filter by monitor name substring. Leave empty for all monitors.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Alert Transitions</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {['Alert', 'Warn', 'OK', 'No Data'].map(transition => {
                        const current = data.signal_filter?.datadog_alert_transitions || [];
                        const isSelected = current.includes(transition);
                        return (
                          <button
                            key={transition}
                            type="button"
                            onClick={() => {
                              const updated = isSelected
                                ? current.filter((t: string) => t !== transition)
                                : [...current, transition];
                              handleSignalFilterChange('datadog_alert_transitions', updated.length > 0 ? updated : undefined);
                            }}
                            className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                              isSelected
                                ? 'bg-primary/10 border-primary/50 text-primary font-medium'
                                : 'border-border text-muted-foreground hover:border-primary/30'
                            }`}
                          >
                            {transition}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Select which state transitions should trigger the workflow.</p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Tags Filter</Label>
                    <Input
                      value={(data.signal_filter?.datadog_tags || []).join(', ')}
                      onChange={e => {
                        const val = e.target.value.trim();
                        const tags = val ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                        handleSignalFilterChange('datadog_tags', tags.length > 0 ? tags : undefined);
                      }}
                      className="mt-1 h-8 text-sm"
                      placeholder="e.g. env:production, service:api"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only trigger for monitors with these tags (comma-separated).</p>
                  </div>
                </>
              )}
            </>
          )}
          {node.type === 'condition' && (
            <>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Field Path</Label>
                  {variableGroups.length > 0 && (
                    <VariablePicker
                      variableGroups={variableGroups}
                      onInsert={(v) => {
                        const path = v.replace(/^\{\{/, '').replace(/\}\}$/, '');
                        onUpdate(node.id, { ...data, field: path });
                      }}
                    />
                  )}
                </div>
                <Input
                  value={data.field || ''}
                  onChange={e => onUpdate(node.id, { ...data, field: e.target.value })}
                  className="mt-1 h-8 text-sm font-mono"
                  placeholder="e.g. rca_result.fix_type"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Operator</Label>
                <Select
                  value={data.operator || 'equals'}
                  onValueChange={v => onUpdate(node.id, { ...data, operator: v })}
                >
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="not_equals">Not Equals</SelectItem>
                    <SelectItem value="contains">Contains</SelectItem>
                    <SelectItem value="not_contains">Not Contains</SelectItem>
                    <SelectItem value="exists">Exists</SelectItem>
                    <SelectItem value="not_exists">Not Exists</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {data.operator !== 'exists' && data.operator !== 'not_exists' && (
                <div>
                  <Label className="text-xs font-medium">Value</Label>
                  <Input
                    value={data.value || ''}
                    onChange={e => onUpdate(node.id, { ...data, value: e.target.value })}
                    className="mt-1 h-8 text-sm"
                    placeholder="e.g. yaml, high, true"
                  />
                </div>
              )}
            </>
          )}

          {/* Approval node config */}
          {node.type === 'approval' && (
            <>
              <div>
                <Label className="text-xs font-medium">Approval Type</Label>
                <Select
                  value={data.approval_type || 'manual'}
                  onValueChange={v => onUpdate(node.id, { ...data, approval_type: v })}
                >
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual (In-App)</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                    <SelectItem value="pr_approval">PR/MR Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(data.approval_type === 'manual' || data.approval_type === 'slack' || !data.approval_type) && (
                <div>
                  <Label className="text-xs font-medium">Require Approval From</Label>
                  <p className="text-xs text-muted-foreground mb-1">Define who must approve. Leave empty to allow anyone.</p>
                  <ApprovalRulesEditor
                    value={data.config?.approval_rules || []}
                    onChange={rules => onUpdate(node.id, { ...data, config: { ...data.config, approval_rules: rules } })}
                  />
                </div>
              )}
              {data.approval_type === 'slack' && (
                <div>
                  <Label className="text-xs font-medium">Slack Channel</Label>
                  <Input
                    className="mt-1 h-8 text-sm"
                    placeholder="#approvals"
                    value={data.config?.channel || ''}
                    onChange={e => onUpdate(node.id, { ...data, config: { ...data.config, channel: e.target.value } })}
                  />
                </div>
              )}
            </>
          )}

          {/* Refine-approval node config (self-looping RCA refinement) */}
          {node.type === 'refine_approval' && (
            <>
              <p className="text-xs text-muted-foreground">
                Presents the upstream RCA + fixes for approval. On <span className="font-medium">Request changes</span>, the RCA agent re-runs with your free-text guidance and re-requests approval — looping until you approve or reject. Place this after a <span className="font-medium">Trigger RCA</span> block.
              </p>
              <div>
                <Label className="text-xs font-medium">Approval Channel</Label>
                <Select
                  value={data.approval_type || 'manual'}
                  onValueChange={v => onUpdate(node.id, { ...data, approval_type: v })}
                >
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Kestrel UI</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Max Refine Rounds</Label>
                <p className="text-xs text-muted-foreground mb-1">How many times changes can be requested before the loop advances on the approved branch.</p>
                <Input
                  type="number"
                  min={1}
                  className="mt-1 h-8 text-sm"
                  value={data.max_rounds ?? 5}
                  onChange={e => onUpdate(node.id, { ...data, max_rounds: parseInt(e.target.value, 10) || 5 })}
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Require Approval From</Label>
                <p className="text-xs text-muted-foreground mb-1">Define who must approve. Leave empty to allow anyone.</p>
                <ApprovalRulesEditor
                  value={data.config?.approval_rules || []}
                  onChange={rules => onUpdate(node.id, { ...data, config: { ...data.config, approval_rules: rules } })}
                />
              </div>
              {data.approval_type === 'slack' && (
                <div>
                  <Label className="text-xs font-medium">Slack Channel</Label>
                  <Input
                    className="mt-1 h-8 text-sm"
                    placeholder="#approvals"
                    value={data.config?.channel || ''}
                    onChange={e => onUpdate(node.id, { ...data, config: { ...data.config, channel: e.target.value } })}
                  />
                </div>
              )}
            </>
          )}

          {/* Loop (Poll Until) node config */}
          {node.type === 'loop' && (
            <>
              <p className="text-xs text-muted-foreground">
                Repeatedly runs the selected action at the poll interval and checks the exit condition against its output. Exits on the <span className="font-medium">Met</span> branch when the condition holds, or the <span className="font-medium">Timeout</span> branch when the timeout elapses.
              </p>
              <div>
                <Label className="text-xs font-medium">Action to Poll<span className="text-destructive ml-1">*</span></Label>
                <LoopActionSelect
                  value={data.action || ''}
                  options={actionTemplates.filter(a => a.integration !== 'approval')}
                  onSelect={v => {
                    const tmpl = actionTemplates.find(a => a.id === v);
                    onUpdate(node.id, {
                      ...data,
                      action: v,
                      integration: tmpl?.integration || data.integration,
                      config: {},
                      condition: { ...data.condition, field: '', value: '', values: undefined },
                    });
                  }}
                />
              </div>
            </>
          )}

          {/* For Each (fan-out) node config */}
          {node.type === 'for_each' && (
            <>
              <p className="text-xs text-muted-foreground">
                Resolves a list from an upstream step and runs the selected action once per item, in order. Templates in the action's fields may reference <span className="font-mono">{'{{item}}'}</span>, <span className="font-mono">{'{{item.<field>}}'}</span> and <span className="font-mono">{'{{item_index}}'}</span>.
              </p>
              <div>
                <div className="flex items-center justify-between gap-1">
                  <Label className="text-xs font-medium">Items to Iterate Over<span className="text-destructive ml-1">*</span></Label>
                  {variableGroups.length > 0 && (
                    <VariablePicker
                      variableGroups={variableGroups}
                      onInsert={v => onUpdate(node.id, { ...data, items_path: v })}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-1">A template that resolves to a list, e.g. an array output of an Execute Script step.</p>
                <Input
                  value={data.items_path || ''}
                  onChange={e => onUpdate(node.id, { ...data, items_path: e.target.value })}
                  placeholder="{{step_outputs.action-1.outputs.findings}}"
                  className="mt-1 h-8 text-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Action per Item<span className="text-destructive ml-1">*</span></Label>
                <LoopActionSelect
                  value={data.action || ''}
                  options={actionTemplates.filter(a => a.integration !== 'approval')}
                  onSelect={v => {
                    const tmpl = actionTemplates.find(a => a.id === v);
                    onUpdate(node.id, {
                      ...data,
                      action: v,
                      integration: tmpl?.integration || data.integration,
                      config: {},
                    });
                  }}
                />
              </div>
            </>
          )}

          {/* Action node: render fields from the action template */}
          {(node.type === 'action' || node.type === 'loop' || node.type === 'for_each') && template && (
            <>
              {template.fields.map(field => {
                // Hide edit-only fields when operation is "create"
                const currentOperation = data.config?.operation || 'create';
                if ((field.name === 'resource_spec' || field.name === 'additional_spec') && currentOperation !== 'edit') {
                  return null;
                }
                // Hide repo/path fields when operation is "create" (only needed for edits)
                if ((field.name === 'gitops_repo' || field.name === 'gitops_root_path' || field.name === 'git_provider') &&
                    data.action === 'kestrel-generate-k8s-manifest' && currentOperation !== 'edit') {
                  return null;
                }
                // Hide RCA/fix toggles on Slack blocks when no RCA block exists in the workflow
                if ((field.name === 'include_rca' || field.name === 'include_fixes') && !workflowHasRCA) {
                  return null;
                }
                // Override: Kestrel provisioning repo/gitops_repo → dropdown based on git_provider
                if ((field.name === 'repo' || field.name === 'gitops_repo') && data.integration === 'kestrel' && (githubRepos.length > 0 || gitlabRepos.length > 0)) {
                  const provider = data.config?.git_provider || 'github';
                  const repos = provider === 'gitlab' ? gitlabRepos : githubRepos;
                  const currentVal = data.config?.[field.name] || '';
                  return (
                    <div key={field.name}>
                      <Label className="text-xs font-medium">{field.label}{field.required && <span className="text-destructive ml-1">*</span>}</Label>
                      {field.description && <p className="text-xs text-muted-foreground mb-1">{field.description}</p>}
                      {repos.length > 0 ? (
                        <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                          <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder={field.placeholder || 'Select a repository'} /></SelectTrigger>
                          <SelectContent>
                            {repos.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={currentVal}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={field.placeholder}
                          className="mt-1 h-8 text-sm"
                        />
                      )}
                    </div>
                  );
                }
                // Override: GitHub repo field → dropdown of connected repos
                if (field.name === 'repo' && data.integration === 'github' && githubRepos.length > 0) {
                  const currentVal = data.config?.repo || '';
                  const exactMatch = !currentVal || currentVal === 'auto' || githubRepos.includes(currentVal);

                  if (!exactMatch && currentVal) {
                    const repoName = currentVal.split('/').pop()?.toLowerCase() || '';
                    const fuzzyMatch = githubRepos.find(r => r.split('/').pop()?.toLowerCase() === repoName);
                    if (fuzzyMatch) {
                      setTimeout(() => handleFieldChange('repo', fuzzyMatch), 0);
                    }
                  }
                  const isInvalid = !exactMatch && currentVal !== 'auto' && !currentVal.includes('{{');
                  return (
                    <div key={field.name}>
                      <Label className="text-xs font-medium">Repository</Label>
                      <Select value={currentVal || 'auto'} onValueChange={v => handleFieldChange('repo', v)}>
                        <SelectTrigger className={`mt-1 h-8 text-sm ${isInvalid ? 'border-destructive' : ''}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto-detect</SelectItem>
                          {currentVal && currentVal.includes('{{') && (
                            <SelectItem value={currentVal}>{currentVal}</SelectItem>
                          )}
                          {githubRepos.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {isInvalid && <p className="text-xs text-destructive mt-1">"{currentVal}" is not a connected repository</p>}
                    </div>
                  );
                }
                // Override: GitLab project field → dropdown of connected projects
                if (field.name === 'project' && data.integration === 'gitlab' && gitlabRepos.length > 0) {
                  const currentVal = data.config?.project || '';
                  const isInvalid = currentVal && currentVal !== 'auto' && !currentVal.includes('{{') && !gitlabRepos.includes(currentVal);
                  return (
                    <div key={field.name}>
                      <Label className="text-xs font-medium">Project</Label>
                      <Select value={currentVal || 'auto'} onValueChange={v => handleFieldChange('project', v)}>
                        <SelectTrigger className={`mt-1 h-8 text-sm ${isInvalid ? 'border-destructive' : ''}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto-detect</SelectItem>
                          {gitlabRepos.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {isInvalid && <p className="text-xs text-destructive mt-1">"{currentVal}" is not a connected project</p>}
                    </div>
                  );
                }
                // Override: GitHub workflow_file → dropdown from repo
                if (field.name === 'workflow_file' && data.integration === 'github') {
                  return (
                    <div key={field.name}>
                      <Label className="text-xs font-medium">
                        {field.label}
                        {field.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <GitHubWorkflowFileSelect
                        repo={data.config?.repo || ''}
                        value={data.config?.workflow_file || ''}
                        onChange={v => handleFieldChange('workflow_file', v)}
                      />
                    </div>
                  );
                }
                // Dynamic select: options fetched from API
                if (field.type === 'dynamic_select' && field.dynamic_source) {
                  // Universal template-variable support for ALL dynamic selects across every
                  // integration: if the current value contains a template variable ({{...}}),
                  // render a consistent read-only code view with a Clear button instead of the
                  // specialized dropdown. The VariablePicker (shown in the label row of each
                  // dynamic-select branch) lets users insert a variable, which lands here.
                  const dsCurrentVal = data.config?.[field.name];
                  if (typeof dsCurrentVal === 'string' && dsCurrentVal.includes('{{')) {
                    return (
                      <div key={field.name}>
                        <div className="flex items-center justify-between gap-1">
                          <Label className="text-xs font-medium">
                            {field.label}
                            {field.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          {variableGroups.length > 0 && (
                            <VariablePicker
                              variableGroups={variableGroups}
                              onInsert={(v) => handleFieldChange(field.name, v)}
                            />
                          )}
                        </div>
                        {field.description && (
                          <p className="text-xs text-muted-foreground mb-1">{field.description}</p>
                        )}
                        <div className="mt-1 flex items-center gap-2">
                          <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs overflow-x-auto whitespace-nowrap">
                            {dsCurrentVal}
                          </code>
                          <button
                            type="button"
                            onClick={() => handleFieldChange(field.name, '')}
                            className="text-xs text-muted-foreground hover:text-foreground shrink-0"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    );
                  }
                  const isSearchable = field.dynamic_source === 'slack_channels' || field.dynamic_source === 'slack_users';
                  const isMultiUser = field.dynamic_source === 'slack_users_multi';
                  const isOrgUsersMulti = field.dynamic_source === 'kestrel_org_users_multi';
                  const isJiraUsers = field.dynamic_source === 'jira_users';
                  const isJiraIssueTypes = field.dynamic_source === 'jira_issue_types';
                  const isLinearUsers = field.dynamic_source === 'linear_users';
                  const isLinearLabels = field.dynamic_source === 'linear_labels';
                  const isLinearProjects = field.dynamic_source === 'linear_projects';
                  const isLinearStates = field.dynamic_source === 'linear_states';
                  const isPDEscalationPolicies = field.dynamic_source === 'pd_escalation_policies';
                  const isPDServices = field.dynamic_source === 'pd_services';
                  const isDDMonitors = field.dynamic_source === 'dd_monitors';
                  const isDDClusters = field.dynamic_source === 'dd_clusters';
                  const isClustersWithFlows = field.dynamic_source === 'clusters_with_flows';
                  if (isClustersWithFlows) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <FlowClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                        />
                      </div>
                    );
                  }
                  if (isDDClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <DDClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                        />
                      </div>
                    );
                  }
                  const isArgoCDApps = field.dynamic_source === 'argocd_apps';
                  const isArgoCDClusters = field.dynamic_source === 'argocd_clusters';
                  const isAllClusters = field.dynamic_source === 'all_clusters';
                  const isClusterNamespaces = field.dynamic_source === 'cluster_namespaces';
                  if (isClusterNamespaces) {
                    const clusterIdField = data.config?.cluster_id || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <ClusterNamespaceSelect
                          clusterId={clusterIdField}
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select or type a namespace...'}
                        />
                      </div>
                    );
                  }
                  if (isAllClusters) {
                    const currentVal = data.config?.[field.name] || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {currentVal && currentVal.includes('{{') ? (
                          <div className="mt-1 flex items-center gap-2">
                            <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">{currentVal}</code>
                            <button type="button" onClick={() => handleFieldChange(field.name, '')} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
                          </div>
                        ) : (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder={field.placeholder || 'Select a cluster...'} /></SelectTrigger>
                            <SelectContent>
                              {clusters.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  }
                  if (isArgoCDClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <ArgoCDClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                        />
                      </div>
                    );
                  }
                  const isRolloutsClusters = field.dynamic_source === 'rollouts_clusters';
                  if (isRolloutsClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <GitopsClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                          fetchClusters={() => apiClient.getRolloutsClusters()}
                          emptyLabel="No clusters with Argo Rollouts found"
                        />
                      </div>
                    );
                  }
                  const isFluxClusters = field.dynamic_source === 'flux_clusters';
                  if (isFluxClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <GitopsClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                          fetchClusters={() => apiClient.getFluxClusters()}
                          emptyLabel="No clusters with Flux CD found"
                        />
                      </div>
                    );
                  }
                  const isKarpenterClusters = field.dynamic_source === 'karpenter_clusters';
                  if (isKarpenterClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <GitopsClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                          fetchClusters={() => apiClient.getKarpenterClusters()}
                          emptyLabel="No clusters with Karpenter found"
                        />
                      </div>
                    );
                  }
                  const isKarpenterNodePools = field.dynamic_source === 'karpenter_nodepools';
                  if (isKarpenterNodePools) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <KarpenterNodePoolSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a NodePool...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                        />
                      </div>
                    );
                  }
                  const isKyvernoClusters = field.dynamic_source === 'kyverno_clusters';
                  if (isKyvernoClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <GitopsClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                          fetchClusters={() => apiClient.getKyvernoClusters()}
                          emptyLabel="No clusters with Kyverno found"
                        />
                      </div>
                    );
                  }
                  const isKyvernoPolicies = field.dynamic_source === 'kyverno_policies';
                  if (isKyvernoPolicies) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <KyvernoPolicySelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a policy...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                        />
                      </div>
                    );
                  }
                  const isTrivyClusters = field.dynamic_source === 'trivy_clusters';
                  if (isTrivyClusters) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <GitopsClusterSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a cluster...'}
                          fetchClusters={() => apiClient.getTrivyClusters()}
                          emptyLabel="No clusters with Trivy found"
                        />
                      </div>
                    );
                  }
                  const isTrivyComplianceReports = field.dynamic_source === 'trivy_compliance_reports';
                  if (isTrivyComplianceReports) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <TrivyComplianceReportSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a report...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                        />
                      </div>
                    );
                  }
                  const isRolloutsNames = field.dynamic_source === 'rollouts_names';
                  if (isRolloutsNames) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <RolloutsNameSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a rollout...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                          namespace={data.config?.['namespace'] || ''}
                        />
                      </div>
                    );
                  }
                  const isRolloutsRevisions = field.dynamic_source === 'rollouts_revisions';
                  if (isRolloutsRevisions) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <RolloutsRevisionSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Previous revision (default)'}
                          clusterId={data.config?.['cluster_id'] || ''}
                          namespace={data.config?.['namespace'] || ''}
                          rolloutName={data.config?.['rollout_name'] || ''}
                        />
                      </div>
                    );
                  }
                  const isFluxResources = field.dynamic_source === 'flux_resources';
                  if (isFluxResources) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <FluxResourceSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a resource...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                          kind={data.config?.['resource_kind'] || 'Kustomization'}
                          namespace={data.config?.['namespace'] || ''}
                        />
                      </div>
                    );
                  }
                  const isConfluencePages = field.dynamic_source === 'confluence_pages';
                  if (isConfluencePages) {
                    const spaceKey = data.config?.space_key || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <ConfluencePageSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          spaceKey={spaceKey}
                          placeholder={field.placeholder || 'Search for a page...'}
                        />
                      </div>
                    );
                  }
                  if (isArgoCDApps) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <ArgoCDAppSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select an application...'}
                          clusterId={data.config?.['cluster_id'] || ''}
                        />
                      </div>
                    );
                  }
                  const isArgoCDHistory = field.dynamic_source === 'argocd_history';
                  if (isArgoCDHistory) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <ArgoCDHistorySelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Previous version (default)'}
                          clusterId={data.config?.['cluster_id'] || ''}
                          appName={data.config?.['app_name'] || ''}
                        />
                      </div>
                    );
                  }
                  if (isDDMonitors) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <DDMonitorSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Search for a monitor...'}
                        />
                      </div>
                    );
                  }
                  if (isPDServices) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <PDServiceSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Select a service...'}
                        />
                      </div>
                    );
                  }
                  if (isPDEscalationPolicies) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <PDEscalationPolicySelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Search for a policy...'}
                        />
                      </div>
                    );
                  }
                  if (isJiraIssueTypes) {
                    const projectKey = data.config?.project_key || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <JiraIssueTypeSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          projectKey={projectKey}
                          placeholder={field.placeholder || 'Select an issue type...'}
                        />
                      </div>
                    );
                  }
                  if (isJiraUsers) {
                    const projectKey = data.config?.project_key || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <JiraUserSearchSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          projectKey={projectKey}
                          placeholder={field.placeholder || 'Search for a user...'}
                        />
                      </div>
                    );
                  }
                  if (isLinearUsers) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <LinearUserSearchSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Search for a user...'}
                        />
                      </div>
                    );
                  }
                  if (isLinearLabels) {
                    const teamKey = data.config?.team_key || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <LinearLabelMultiSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          teamKey={teamKey}
                          placeholder={field.placeholder || 'Select labels...'}
                        />
                      </div>
                    );
                  }
                  if (isLinearProjects) {
                    const teamKey = data.config?.team_key || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <LinearProjectSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          teamKey={teamKey}
                          placeholder={field.placeholder || 'Select a project...'}
                        />
                      </div>
                    );
                  }
                  if (isLinearStates) {
                    const teamKey = data.config?.team_key || '';
                    const issueIdentifier = data.config?.issue_identifier || '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <LinearStateSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          teamKey={teamKey}
                          issueIdentifier={issueIdentifier}
                          placeholder={field.placeholder || 'Select a status...'}
                        />
                      </div>
                    );
                  }
                  if (isOrgUsersMulti) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <OrgUserMultiSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder="Search org members..."
                        />
                      </div>
                    );
                  }
                  if (isMultiUser) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <SlackMultiUserSelect
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Search users...'}
                        />
                      </div>
                    );
                  }
                  // AWS EC2 resource-ID multi-selects on the cost remediation blocks:
                  // options load from the live account/region; values are stored as a
                  // comma-separated string (what the handlers parse), manual IDs can be
                  // typed in, and template variables go through the universal {{...}}
                  // gate above via the VariablePicker in the label row.
                  const awsResourceSourceTypes: Record<string, string> = {
                    aws_instance_ids: 'instances',
                    aws_volume_ids: 'volumes',
                    aws_eip_allocation_ids: 'Elastic IPs',
                    aws_snapshot_ids: 'snapshots',
                  };
                  if (awsResourceSourceTypes[field.dynamic_source]) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <AWSResourceMultiSelect
                          options={dynamicSourceMap[field.dynamic_source] || []}
                          loading={awsLoadingResourceIDs}
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || `Select ${awsResourceSourceTypes[field.dynamic_source]}...`}
                          resourceLabel={awsResourceSourceTypes[field.dynamic_source]}
                        />
                      </div>
                    );
                  }
                  if (isSearchable) {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        <SlackSearchSelect
                          source={field.dynamic_source as 'slack_channels' | 'slack_users'}
                          value={data.config?.[field.name] || ''}
                          onChange={v => handleFieldChange(field.name, v)}
                          placeholder={field.placeholder || 'Search...'}
                        />
                      </div>
                    );
                  }
                  const options = dynamicSourceMap[field.dynamic_source] || [];
                  const currentVal = data.config?.[field.name] || '';
                  // Fly.io dynamic selects: the AI/NL builder may pre-fill a raw app name or
                  // machine id/name that doesn't exactly match a loaded option key. Fuzzy match
                  // it (case-insensitive, on key or name, and the bare id inside "name (id)")
                  // and rewrite the value to the canonical option key.
                  if (
                    (field.dynamic_source === 'flyio_apps' || field.dynamic_source === 'flyio_machines' ||
                     field.dynamic_source === 'nebius_projects' || field.dynamic_source === 'nebius_clusters' ||
                     field.dynamic_source === 'nebius_node_groups' || field.dynamic_source === 'nebius_instances' ||
                     field.dynamic_source === 'nebius_subnets' ||
                     field.dynamic_source === 'beam_deployments' || field.dynamic_source === 'beam_containers' ||
                     field.dynamic_source === 'beam_machines' || field.dynamic_source === 'beam_sandboxes' ||
                     field.dynamic_source === 'daytona_sandboxes' || field.dynamic_source === 'daytona_snapshots' ||
                     field.dynamic_source === 'daytona_volumes' || field.dynamic_source === 'daytona_targets' ||
                     field.dynamic_source === 'supabase_projects' || field.dynamic_source === 'supabase_branches' ||
                     field.dynamic_source === 'planetscale_databases' || field.dynamic_source === 'planetscale_branches' ||
                     field.dynamic_source === 'planetscale_deploy_requests' ||
                     field.dynamic_source === 'neon_projects' || field.dynamic_source === 'neon_branches' ||
                     field.dynamic_source === 'neon_endpoints' ||
                     field.dynamic_source === 'clickhouse_services' || field.dynamic_source === 'clickhouse_clickpipes' ||
                     field.dynamic_source === 'clickhouse_backups' ||
                     field.dynamic_source === 'jenkins_jobs' || field.dynamic_source === 'circleci_projects' ||
                     field.dynamic_source === 'circleci_pipelines' ||
                     field.dynamic_source === 'sonarcloud_projects' ||
                     field.dynamic_source === 'okta_users' || field.dynamic_source === 'okta_groups' ||
                     field.dynamic_source === 'terraform_workspaces' || field.dynamic_source === 'terraform_runs' ||
                     field.dynamic_source === 'databricks_jobs' || field.dynamic_source === 'databricks_clusters' ||
                     field.dynamic_source === 'databricks_pipelines' || field.dynamic_source === 'databricks_warehouses' ||
                     field.dynamic_source === 'pulumi_stacks' ||
                     field.dynamic_source === 'aws_budget_names' || field.dynamic_source === 'aws_ce_services') &&
                    typeof currentVal === 'string' &&
                    currentVal !== '' &&
                    !currentVal.includes('{{') &&
                    options.length > 0 &&
                    !options.some(o => o.key === currentVal)
                  ) {
                    const lc = currentVal.toLowerCase();
                    const fuzzy = options.find(o => {
                      if (o.key.toLowerCase() === lc) return true;
                      if (o.name.toLowerCase() === lc) return true;
                      // machine labels are typically "name (id) — region" — match the
                      // bare id inside the parentheses wherever it appears in the label
                      const idMatch = o.name.match(/\(([^)]+)\)/);
                      if (idMatch && idMatch[1].toLowerCase() === lc) return true;
                      return false;
                    });
                    if (fuzzy) {
                      setTimeout(() => handleFieldChange(field.name, fuzzy.key), 0);
                    }
                  }
                  // Nebius node groups load on demand once a cluster_id is chosen.
                  // Upstream Create Node Group outputs are offered first so chained
                  // provisioning workflows can reference the not-yet-created group.
                  if (field.dynamic_source === 'nebius_node_groups') {
                    const clusterChosen = typeof data.config?.cluster_id === 'string' && data.config.cluster_id.trim() !== '';
                    const nodeGroupOpts = [
                      ...upstreamNebiusNodeGroupOptions,
                      ...options.filter(o => !upstreamNebiusNodeGroupOptions.some(u => u.key === o.key)),
                    ];
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {nebiusLoadingNodeGroups ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : nodeGroupOpts.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a node group...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {nodeGroupOpts.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Node group ID'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {clusterChosen ? 'No node groups found for this cluster — enter a node group ID manually.' : 'Select a cluster first to load its node groups, or enter a node group ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // CircleCI pipelines load on demand once a project_slug is chosen.
                  if (field.dynamic_source === 'circleci_pipelines') {
                    const projectChosen = typeof data.config?.project_slug === 'string' && data.config.project_slug.trim() !== '' && !String(data.config.project_slug).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {circleciLoadingPipelines ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a pipeline...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Pipeline ID'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {projectChosen ? 'No recent pipelines found for this project — enter a pipeline ID manually.' : 'Select a project first to load its recent pipelines, or enter a pipeline ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Terraform runs load on demand once a workspace is chosen.
                  if (field.dynamic_source === 'terraform_runs') {
                    const workspaceChosen = typeof data.config?.workspace === 'string' && data.config.workspace.trim() !== '' && !String(data.config.workspace).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {terraformLoadingRuns ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a run...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Run ID (run-...)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {workspaceChosen ? 'No recent runs found for this workspace — enter a run ID manually.' : 'Select a workspace first to load its recent runs, or enter a run ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Vault secret paths load on demand once a mount is chosen. Paths
                  // may also be brand new (write-secret), so always allow manual entry.
                  if (field.dynamic_source === 'vault_secret_paths') {
                    const mountChosen = typeof data.config?.mount === 'string' && data.config.mount.trim() !== '' && !String(data.config.mount).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {vaultLoadingSecretPaths ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <>
                            <Select value={options.some(o => o.key === currentVal) ? currentVal : undefined} onValueChange={v => handleFieldChange(field.name, v)}>
                              <SelectTrigger className="mt-1 h-8 text-sm">
                                <SelectValue placeholder={field.placeholder || 'Select a secret path...'} />
                              </SelectTrigger>
                              <SelectContent>
                                {isRequestTriggered && (
                                  <SelectItem value={`{{request.${field.name}}}`}>
                                    <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                    <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                  </SelectItem>
                                )}
                                {options.map(o => (
                                  <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder="Or type a path (e.g. app/prod/db)"
                              className="mt-1 h-8 text-sm"
                            />
                          </>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Secret path (e.g. app/prod/db)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {mountChosen ? 'No existing paths found in this mount — type a path.' : 'Select a mount first to browse existing paths, or type a path.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Vault static roles load on demand once a database mount is chosen.
                  if (field.dynamic_source === 'vault_static_roles') {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {vaultLoadingStaticRoles ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a static role...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Static role name'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              No static roles found — select a database mount first, or enter a role name manually.
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Infisical environments load on demand once a project is chosen.
                  if (field.dynamic_source === 'infisical_environments') {
                    const projectChosen = typeof data.config?.project === 'string' && data.config.project.trim() !== '' && !String(data.config.project).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {infisicalLoadingEnvironments ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select an environment...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Environment slug (e.g. prod)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {projectChosen ? 'No environments found for this project — enter a slug manually.' : 'Select a project first to load its environments, or enter a slug manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Infisical folders load on demand once project + environment are
                  // chosen. Folders may also be brand new, so always allow manual entry.
                  if (field.dynamic_source === 'infisical_folders') {
                    const scopeChosen = typeof data.config?.project === 'string' && data.config.project.trim() !== '' && !String(data.config.project).includes('{{')
                      && typeof data.config?.environment === 'string' && data.config.environment.trim() !== '' && !String(data.config.environment).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {infisicalLoadingFolders ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <>
                            <Select value={options.some(o => o.key === currentVal) ? currentVal : undefined} onValueChange={v => handleFieldChange(field.name, v)}>
                              <SelectTrigger className="mt-1 h-8 text-sm">
                                <SelectValue placeholder={field.placeholder || 'Select a folder...'} />
                              </SelectTrigger>
                              <SelectContent>
                                {isRequestTriggered && (
                                  <SelectItem value={`{{request.${field.name}}}`}>
                                    <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                    <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                  </SelectItem>
                                )}
                                {options.map(o => (
                                  <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder="Or type a path (e.g. /backend)"
                              className="mt-1 h-8 text-sm"
                            />
                          </>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Folder path (e.g. /backend)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {scopeChosen ? 'No folders found — type a path (default /).' : 'Select a project and environment first to browse folders, or type a path (default /).'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Nebius instances load on demand once a project_id is chosen.
                  if (field.dynamic_source === 'nebius_instances') {
                    const projectChosen = typeof data.config?.project_id === 'string' && data.config.project_id.trim() !== '';
                    const instanceOpts = [
                      ...upstreamNebiusInstanceOptions,
                      ...options.filter(o => !upstreamNebiusInstanceOptions.some(u => u.key === o.key)),
                    ];
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {nebiusLoadingInstances ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : instanceOpts.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select an instance...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {instanceOpts.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Instance ID'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {projectChosen ? 'No instances found for this project — enter an instance ID manually.' : 'Select a project first to load its instances, or enter an instance ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Nebius subnets load on demand once a project_id is chosen.
                  if (field.dynamic_source === 'nebius_subnets') {
                    const projectChosen = typeof data.config?.project_id === 'string' && data.config.project_id.trim() !== '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {nebiusLoadingSubnets ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a subnet...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Subnet ID'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {projectChosen ? 'No subnets found for this project — enter a subnet ID manually.' : 'Select a project first to load its subnets, or enter a subnet ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Nebius platforms load on demand once a project_id is chosen
                  // (platform availability is regional/per-project).
                  if (field.dynamic_source === 'nebius_platforms') {
                    const projectChosen = typeof data.config?.project_id === 'string' && data.config.project_id.trim() !== '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {nebiusLoadingPlatforms ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a platform...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Platform (e.g. gpu-h100-sxm)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {projectChosen ? 'No platforms found for this project — enter a platform name manually.' : 'Select a project first to load its available platforms, or enter a platform name manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Nebius presets load on demand once a project_id and platform are chosen.
                  if (field.dynamic_source === 'nebius_presets') {
                    const platformChosen = typeof data.config?.platform === 'string' && data.config.platform.trim() !== '' && !String(data.config.platform).includes('{{');
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {nebiusLoadingPresets ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a preset...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Preset (e.g. 1gpu-16vcpu-200gb)'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {platformChosen ? 'No presets found for this platform — enter a preset manually.' : 'Select a platform first to load its presets, or enter a preset manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Fly machines load on demand once an app_name is chosen; surface loading
                  // and "pick an app first" states explicitly instead of a bare text input.
                  if (field.dynamic_source === 'flyio_machines') {
                    const appChosen = typeof data.config?.app_name === 'string' && data.config.app_name.trim() !== '';
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {flyioLoadingMachines ? (
                          <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                        ) : options.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a machine...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {options.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Machine ID'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {appChosen ? 'No machines found for this app — enter a machine ID manually.' : 'Select an app first to load its machines, or enter a machine ID manually.'}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // Beam/Daytona sandbox picker: a sandbox_id often references an
                  // upstream "Create Sandbox" step's output (sandboxes are created
                  // at runtime, so the live list may not include it at build
                  // time). Offer upstream Create Sandbox outputs first, then
                  // any live tracked sandboxes, plus manual entry + variable insert.
                  if (field.dynamic_source === 'beam_sandboxes' || field.dynamic_source === 'daytona_sandboxes') {
                    const sandboxOpts = [
                      ...upstreamSandboxOptions,
                      ...options.filter(o => !upstreamSandboxOptions.some(u => u.key === o.key)),
                    ];
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {sandboxOpts.length > 0 ? (
                          <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue placeholder={field.placeholder || 'Select a sandbox...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {isRequestTriggered && (
                                <SelectItem value={`{{request.${field.name}}}`}>
                                  <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                  <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                </SelectItem>
                              )}
                              {sandboxOpts.map(o => (
                                <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <>
                            <Input
                              value={currentVal}
                              onChange={e => handleFieldChange(field.name, e.target.value)}
                              placeholder={field.placeholder || 'Sandbox ID, or insert a {{step_outputs...}} variable'}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Add a Create Sandbox step upstream to pick its sandbox automatically, or insert a variable / enter a sandbox ID.
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }
                  // AWS cost dropdowns: optional filters with "leave blank for
                  // all" semantics — render a select plus a clear affordance,
                  // falling back to manual entry when nothing was loaded.
                  if (field.dynamic_source === 'aws_budget_names' || field.dynamic_source === 'aws_ce_services') {
                    return (
                      <div key={field.name}>
                        {renderDynamicSelectLabel(field)}
                        {options.length > 0 ? (
                          <div className="mt-1 flex items-center gap-2">
                            <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                              <SelectTrigger className="h-8 text-sm flex-1">
                                <SelectValue placeholder={field.placeholder || 'Select...'} />
                              </SelectTrigger>
                              <SelectContent>
                                {isRequestTriggered && (
                                  <SelectItem value={`{{request.${field.name}}}`}>
                                    <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                    <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                                  </SelectItem>
                                )}
                                {options.map(o => (
                                  <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {currentVal && !field.required && (
                              <button type="button" onClick={() => handleFieldChange(field.name, '')} className="text-xs text-muted-foreground hover:text-foreground shrink-0">Clear</button>
                            )}
                          </div>
                        ) : (
                          <Input
                            value={currentVal}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            className="mt-1 h-8 text-sm"
                          />
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={field.name}>
                      {renderDynamicSelectLabel(field)}
                      {options.length > 0 ? (
                        <Select value={currentVal} onValueChange={v => handleFieldChange(field.name, v)}>
                          <SelectTrigger className="mt-1 h-8 text-sm">
                            <SelectValue placeholder={field.placeholder || 'Select...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {isRequestTriggered && (
                              <SelectItem value={`{{request.${field.name}}}`}>
                                <span className="font-mono text-xs">{'{{'}request.{field.name}{'}}'}</span>
                                <span className="text-muted-foreground ml-1 text-xs">(from Slack request)</span>
                              </SelectItem>
                            )}
                            {options.map(o => (
                              <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={currentVal}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={field.placeholder}
                          className="mt-1 h-8 text-sm"
                        />
                      )}
                    </div>
                  );
                }
                // Override: Execute Script inputs → AI analysis + editable inputs/outputs
                if (field.name === 'inputs' && data.action === 'kestrel-execute-script') {
                  const scriptOutputs: { key: string; label?: string; description?: string }[] = Array.isArray(data.script_outputs) ? data.script_outputs : [];
                  const updateOutput = (idx: number, patch: Partial<{ key: string; label: string; description: string }>) => {
                    const next = scriptOutputs.map((o, i) => i === idx ? { ...o, ...patch } : o);
                    onUpdate(node.id, { ...data, script_outputs: next });
                  };
                  const removeOutput = (idx: number) => {
                    onUpdate(node.id, { ...data, script_outputs: scriptOutputs.filter((_, i) => i !== idx) });
                  };
                  const addOutput = () => {
                    onUpdate(node.id, { ...data, script_outputs: [...scriptOutputs, { key: '', label: '', description: '' }] });
                  };
                  return (
                    <div key={field.name} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-medium">{field.label}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={handleAnalyzeScript}
                          disabled={scriptAnalyzing}
                        >
                          {scriptAnalyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Braces className="h-3 w-3" />}
                          Analyze script
                        </Button>
                      </div>
                      {field.description && <p className="text-xs text-muted-foreground">{field.description}</p>}
                      {scriptAnalyzeError && <p className="text-xs text-destructive">{scriptAnalyzeError}</p>}
                      {renderField(field, data.config?.[field.name], (v) => handleFieldChange(field.name, v))}

                      {/* Inferred / editable outputs */}
                      <div className="pt-1">
                        <Label className="text-xs font-medium">Outputs</Label>
                        <p className="text-xs text-muted-foreground mb-1">
                          Keys the script writes to <code className="font-mono">/workspace/outputs.json</code>. Available downstream as <code className="font-mono">{'{{step_outputs.'}{node.id}{'.outputs.<key>}}'}</code>.
                        </p>
                        <div className="space-y-2">
                          {scriptOutputs.map((o, idx) => (
                            <div key={idx} className="flex items-center gap-1.5">
                              <Input
                                value={o.key}
                                onChange={e => updateOutput(idx, { key: e.target.value })}
                                placeholder="output_key"
                                className="h-7 text-xs flex-1"
                              />
                              <Input
                                value={o.description || ''}
                                onChange={e => updateOutput(idx, { description: e.target.value })}
                                placeholder="description (optional)"
                                className="h-7 text-xs flex-1"
                              />
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removeOutput(idx)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addOutput}>
                            <Plus className="h-3 w-3 mr-1" /> Add output
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                }
                // Override: GitHub workflow inputs → dynamic discovery
                if (field.name === 'inputs' && field.type === 'key-value' && data.action === 'github-trigger-workflow') {
                  return (
                    <div key={field.name}>
                      <Label className="text-xs font-medium">
                        {field.label}
                        {field.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <GitHubWorkflowInputsEditor
                        repo={data.config?.repo || ''}
                        workflowFile={data.config?.workflow_file || ''}
                        value={data.config?.inputs || {}}
                        onChange={v => handleFieldChange('inputs', v)}
                      />
                    </div>
                  );
                }
                return (
                <div key={field.name}>
                  <div className="flex items-center justify-between gap-1">
                    <Label className="text-xs font-medium">
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {(field.type === 'template' || field.type === 'string' || field.type === 'textarea' || field.type === 'select' || field.type === 'number') && variableGroups.length > 0 && (
                      <VariablePicker
                        variableGroups={variableGroups}
                        onInsert={(v) => {
                          handleFieldChange(field.name, v);
                        }}
                      />
                    )}
                  </div>
                  {field.description && (
                    <p className="text-xs text-muted-foreground mb-1">{field.description}</p>
                  )}
                  {renderField(field, data.config?.[field.name], (v) => handleFieldChange(field.name, v))}
                </div>
                );
              })}
            </>
          )}

          {/* Loop (Poll Until): exit condition + cadence, rendered after the
              polled action's config fields (shared with the action block above). */}
          {node.type === 'loop' && (
            <>
              <div className="pt-2 border-t">
                <Label className="text-xs font-semibold">Exit Condition<span className="text-destructive ml-1">*</span></Label>
                <p className="text-xs text-muted-foreground mb-1">Checked against the polled action's output each iteration.</p>
                <div>
                  <Label className="text-xs font-medium">Output Field Path</Label>
                  {(() => {
                    const outputs = template?.outputs || [];
                    const current = data.condition?.field || '';
                    if (outputs.length === 0) {
                      return (
                        <Input
                          value={current}
                          onChange={e => onUpdate(node.id, { ...data, condition: { ...data.condition, field: e.target.value } })}
                          className="mt-1 h-8 text-sm font-mono"
                          placeholder="e.g. state"
                        />
                      );
                    }
                    // Keep a saved value that isn't in the catalog (e.g. a nested
                    // path like outputs.result) selectable instead of blanking it.
                    const hasCurrent = !current || outputs.some(o => o.name === current);
                    return (
                      <Select
                        value={current}
                        onValueChange={v => onUpdate(node.id, {
                          ...data,
                          // Selected values are specific to the output field,
                          // so a field change resets them.
                          condition: { ...data.condition, field: v, value: '', values: undefined },
                        })}
                      >
                        {/* Radix SelectValue mirrors the selected item's children
                            (name + label badge); pass explicit children so the
                            closed trigger shows only the field path. */}
                        <SelectTrigger className="mt-1 h-8 text-sm font-mono">
                          <SelectValue placeholder="Select an output field">
                            {current ? <span className="font-mono">{current}</span> : null}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {!hasCurrent && <SelectItem value={current}><span className="font-mono">{current}</span></SelectItem>}
                          {outputs.map(o => (
                            <SelectItem key={o.name} value={o.name}>
                              <span className="font-mono">{o.name}</span>
                              {o.label && o.label !== o.name && <span className="text-muted-foreground ml-2 text-xs">{o.label}</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
                <div className="mt-2">
                  <Label className="text-xs font-medium">Operator</Label>
                  <Select
                    value={data.condition?.operator || 'equals'}
                    onValueChange={v => onUpdate(node.id, { ...data, condition: { ...data.condition, operator: v } })}
                  >
                    <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="not_equals">Not Equals</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="not_contains">Not Contains</SelectItem>
                      <SelectItem value="exists">Exists</SelectItem>
                      <SelectItem value="not_exists">Not Exists</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {data.condition?.operator !== 'exists' && data.condition?.operator !== 'not_exists' && (
                  <div className="mt-2">
                    <Label className="text-xs font-medium">Value</Label>
                    {(() => {
                      const outputs = template?.outputs || [];
                      const selectedOutput = outputs.find(o => o.name === (data.condition?.field || ''));
                      // Options come from catalog metadata: a fixed enum of
                      // discrete values, or a dynamic source of live resource
                      // IDs (clusters, sandboxes, ...).
                      let valueOptions: { key: string; name: string }[] = [];
                      if (selectedOutput?.values?.length) {
                        valueOptions = selectedOutput.values.map(v => ({ key: v, name: v }));
                      } else if (selectedOutput?.dynamic_source) {
                        if (selectedOutput.dynamic_source === 'all_clusters') {
                          valueOptions = clusters.map(c => ({ key: c.id, name: c.name }));
                        } else {
                          valueOptions = dynamicSourceMap[selectedOutput.dynamic_source] || [];
                        }
                      }
                      // Normalized selection: `values` (multi) wins over the
                      // legacy single `value`, matching backend semantics.
                      const selectedValues: string[] = Array.isArray(data.condition?.values) && data.condition.values.length > 0
                        ? data.condition.values
                        : (data.condition?.value ? [data.condition.value] : []);
                      if (valueOptions.length === 0) {
                        return (
                          <Input
                            value={data.condition?.value || ''}
                            onChange={e => onUpdate(node.id, { ...data, condition: { ...data.condition, value: e.target.value, values: undefined } })}
                            className="mt-1 h-8 text-sm"
                            placeholder="e.g. stopped"
                          />
                        );
                      }
                      return (
                        <>
                          <LoopConditionValueSelect
                            values={selectedValues}
                            options={valueOptions}
                            onChange={vals => onUpdate(node.id, {
                              ...data,
                              condition: {
                                ...data.condition,
                                // Mirror the first selection into the legacy
                                // single `value` so older consumers (node
                                // subtitle, server fallback) keep working.
                                value: vals[0] || '',
                                values: vals.length > 1 ? vals : undefined,
                              },
                            })}
                            placeholder="Select value(s)"
                          />
                          {selectedValues.length > 1 && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {(data.condition?.operator || 'equals') === 'not_equals' || data.condition?.operator === 'not_contains'
                                ? 'Condition is met when the output matches none of the selected values.'
                                : 'Condition is met when the output matches any of the selected values.'}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs font-medium">Poll Interval (seconds)</Label>
                  <Input
                    type="number"
                    min={30}
                    className="mt-1 h-8 text-sm"
                    value={data.interval_seconds ?? 60}
                    onChange={e => onUpdate(node.id, { ...data, interval_seconds: parseInt(e.target.value, 10) || 60 })}
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Minimum 30 seconds.</p>
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-medium">Timeout (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    className="mt-1 h-8 text-sm"
                    value={data.timeout_minutes ?? 60}
                    onChange={e => onUpdate(node.id, { ...data, timeout_minutes: parseInt(e.target.value, 10) || 60 })}
                  />
                </div>
              </div>
            </>
          )}

          {/* For Each: fan-out settings, rendered after the per-item action's
              config fields (shared with the action block above). */}
          {node.type === 'for_each' && (
            <div className="pt-2 border-t">
              <Label className="text-xs font-semibold">Fan-Out Settings</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex-1">
                  <Label className="text-xs font-medium">Max Items</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    className="mt-1 h-8 text-sm"
                    value={data.max_items ?? 25}
                    onChange={e => onUpdate(node.id, { ...data, max_items: parseInt(e.target.value, 10) || 25 })}
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Hard cap 100. Extra items are skipped.</p>
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-medium">Continue on Error</Label>
                  <div className="flex items-center gap-2 mt-2.5">
                    <Switch
                      checked={data.continue_on_error ?? false}
                      onCheckedChange={v => onUpdate(node.id, { ...data, continue_on_error: v })}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">Keep processing items when one fails.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function renderField(
  field: { name?: string; type: string; placeholder?: string; options?: { value: string; label: string }[]; default?: any },
  value: any,
  onChange: (value: any) => void,
) {
  switch (field.type) {
    case 'boolean':
      return (
        <div className="flex items-center gap-2 mt-1">
          <Switch
            checked={value ?? field.default ?? false}
            onCheckedChange={onChange}
          />
        </div>
      );
    case 'select':
      if (value && typeof value === 'string' && value.includes('{{')) {
        return (
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">
              {value}
            </code>
            <button type="button" onClick={() => onChange('')} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
          </div>
        );
      }
      return (
        <Select value={value ?? field.default ?? ''} onValueChange={onChange}>
          <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(field.options || []).filter(o => o.value !== '').map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'multi-select': {
      const selected: string[] = Array.isArray(value) ? value : (value ? [value] : (field.default ? [field.default] : []));
      const toggleOption = (optVal: string) => {
        const next = selected.includes(optVal) ? selected.filter(v => v !== optVal) : [...selected, optVal];
        onChange(next);
      };
      return (
        <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border rounded-md p-2">
          {(field.options || []).map(o => (
            <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggleOption(o.value)}
                className="rounded border-muted-foreground"
              />
              {o.label}
            </label>
          ))}
        </div>
      );
    }
    case 'number': {
      const isTemplate = typeof value === 'string' && value.includes('{{');
      if (isTemplate) {
        return (
          <Input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder}
            className="mt-1 h-8 text-sm font-mono"
          />
        );
      }
      return (
        <Input
          type="number"
          value={value ?? field.default ?? ''}
          onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder}
          className="mt-1 h-8 text-sm"
        />
      );
    }
    case 'template': {
      const isLargeField = ['body_template', 'content_template', 'message_template', 'script'].includes(field.name || '');
      return (
        <Textarea
          value={value ?? field.default ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`mt-1 text-sm font-mono ${isLargeField ? 'min-h-[60px] max-h-[200px] resize-y' : 'min-h-[60px] resize-none'}`}
          rows={isLargeField ? 4 : 3}
        />
      );
    }
    case 'key-value': {
      const pairs: Record<string, string> = (typeof value === 'object' && value !== null && !Array.isArray(value)) ? value : {};
      const entries = Object.entries(pairs);
      const updatePair = (oldKey: string, newKey: string, val: string) => {
        const updated: Record<string, string> = {};
        for (const [k, v] of Object.entries(pairs)) {
          if (k === oldKey) updated[newKey] = val;
          else updated[k] = v as string;
        }
        onChange(updated);
      };
      const removePair = (key: string) => {
        const updated = { ...pairs };
        delete updated[key];
        onChange(updated);
      };
      const addPair = () => {
        let newKey = 'key';
        let i = 1;
        while (pairs[newKey] !== undefined) { newKey = `key_${i++}`; }
        onChange({ ...pairs, [newKey]: '' });
      };
      return (
        <div className="mt-1 space-y-2">
          {entries.map(([k, v], idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <Input
                value={k}
                onChange={e => updatePair(k, e.target.value, v as string)}
                placeholder="key"
                className="h-7 text-xs flex-1"
              />
              <span className="text-muted-foreground text-xs">=</span>
              <Input
                value={v as string}
                onChange={e => updatePair(k, k, e.target.value)}
                placeholder="value"
                className="h-7 text-xs flex-1"
              />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removePair(k)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addPair}>
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
      );
    }
    case 'textarea':
      return (
        <div className="mt-1 space-y-1">
          <Textarea
            value={value ?? field.default ?? ''}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder || 'Paste YAML content or upload a file...'}
            className="text-sm font-mono min-h-[80px] max-h-[200px] resize-y"
            rows={4}
          />
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
            <input
              type="file"
              accept=".yaml,.yml,.json,.txt"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = () => onChange(reader.result as string);
                  reader.readAsText(file);
                }
                e.target.value = '';
              }}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload file
          </label>
        </div>
      );
    default:
      return (
        <Input
          value={value ?? field.default ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="mt-1 h-8 text-sm"
        />
      );
  }
}

function ClusterMultiSelect({
  clusters,
  selectedIds,
  onChange,
  allLabel = 'All Clusters',
}: {
  clusters: { id: string; name: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const isAllClusters = selectedIds.length === 0 || (selectedIds.length === 1 && selectedIds[0] === '*');

  const displayText = isAllClusters
    ? allLabel
    : clusters
        .filter(c => selectedIds.includes(c.id))
        .map(c => c.name)
        .join(', ') || allLabel;

  const toggle = (id: string) => {
    if (id === '*') {
      onChange(['*']);
      return;
    }
    let next: string[];
    if (isAllClusters) {
      next = [id];
    } else if (selectedIds.includes(id)) {
      next = selectedIds.filter(x => x !== id);
      if (next.length === 0) next = ['*'];
    } else {
      next = [...selectedIds.filter(x => x !== '*'), id];
    }
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          <button
            type="button"
            onClick={() => toggle('*')}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAllClusters ? 'bg-primary border-primary' : 'border-input'}`}>
              {isAllClusters && <Check className="w-3 h-3 text-primary-foreground" />}
            </div>
            {allLabel}
          </button>
          {clusters.map(c => {
            const checked = !isAllClusters && selectedIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                  {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                {c.name}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Fly.io synthesized Machine lifecycle event types (mirrors the poller in
// server/pkg/server/fly_poller.go and the FlyEventTypes matcher).
const FLY_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'machine.crashed', name: 'Machine crashed' },
  { id: 'machine.stopped', name: 'Machine stopped' },
  { id: 'machine.started', name: 'Machine started' },
  { id: 'app.down', name: 'App down' },
];

// Databricks synthesized event types (mirrors the poller in
// server/pkg/server/databricks_poller.go and the DatabricksEventTypes matcher).
const DATABRICKS_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'job_run.failed', name: 'Job run failed' },
  { id: 'job_run.succeeded', name: 'Job run succeeded' },
  { id: 'job_run.long_running', name: 'Job run exceeded duration' },
  { id: 'cluster.terminated_unexpectedly', name: 'Cluster terminated unexpectedly' },
  { id: 'pipeline.update_failed', name: 'DLT pipeline update failed' },
];

// Nebius synthesized node and instance event types (mirrors the poller in
// server/pkg/server/nebius_poller.go and the matcher).
const NEBIUS_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'node.gpu_error', name: 'Node GPU error' },
  { id: 'node.maintenance_scheduled', name: 'Node maintenance scheduled' },
  { id: 'node.not_ready', name: 'Node not ready' },
  { id: 'instance.stopped', name: 'Instance stopped' },
];

// Beam synthesized task, container, deployment, and machine event types
// (mirrors the poller in server/pkg/server/beam_poller.go and the matcher).
const BEAM_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'task.failed', name: 'Task failed' },
  { id: 'task.timed_out', name: 'Task timed out' },
  { id: 'container.stopped', name: 'Container stopped' },
  { id: 'deployment.scaled_to_zero', name: 'Deployment scaled to zero' },
  { id: 'queue.depth_high', name: 'Queue depth high' },
  { id: 'machine.down', name: 'Machine down' },
];

const DAYTONA_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'sandbox.created', name: 'Sandbox created' },
  { id: 'sandbox.stopped', name: 'Sandbox stopped' },
  { id: 'sandbox.error', name: 'Sandbox error' },
  { id: 'sandbox.archived', name: 'Sandbox archived' },
  { id: 'sandbox.execution_failed', name: 'Sandbox execution failed' },
  { id: 'snapshot.build_failed', name: 'Snapshot build failed' },
  { id: 'volume.error', name: 'Volume error' },
];

const SUPABASE_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'project.health_degraded', name: 'Project health degraded' },
  { id: 'backup.failed', name: 'Backup failed' },
  { id: 'replica.unhealthy', name: 'Read replica unhealthy' },
  { id: 'usage.threshold', name: 'Usage/quota threshold' },
  { id: 'branch.created', name: 'Branch created' },
  { id: 'branch.merge_failed', name: 'Branch migration failed' },
  { id: 'db.row_event', name: 'Database row event' },
];

const PLANETSCALE_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'deploy_request.opened', name: 'Deploy request opened' },
  { id: 'deploy_request.queued', name: 'Deploy request queued' },
  { id: 'deploy_request.in_progress', name: 'Deploy request in progress' },
  { id: 'deploy_request.schema_applied', name: 'Deploy request schema applied' },
  { id: 'deploy_request.errored', name: 'Deploy request errored' },
  { id: 'deploy_request.reverted', name: 'Deploy request reverted' },
  { id: 'deploy_request.closed', name: 'Deploy request closed' },
  { id: 'branch.ready', name: 'Branch ready' },
  { id: 'branch.anomaly', name: 'Branch anomaly (insights)' },
  { id: 'branch.primary_promoted', name: 'Branch primary promoted' },
  { id: 'branch.sleeping', name: 'Branch sleeping' },
  { id: 'cluster.storage', name: 'Cluster storage threshold' },
  { id: 'keyspace.storage', name: 'Keyspace storage threshold' },
  { id: 'backup.completed', name: 'Backup completed' },
  { id: 'backup.failed', name: 'Backup failed' },
];

const NEON_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'branch.created', name: 'Branch created' },
  { id: 'branch.ready', name: 'Branch ready' },
  { id: 'operation.failed', name: 'Operation failed' },
  { id: 'compute.suspended', name: 'Compute suspended' },
  { id: 'compute.active', name: 'Compute active' },
  { id: 'usage.threshold', name: 'Usage/quota threshold' },
];

// GCP trigger event types, grouped by the family they belong to. Used by the
// trigger config panel so a single GCP trigger can be narrowed to specific
// events, and so threshold fields only appear for the events that read them.
const GCP_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'cost_anomaly', name: 'Cost anomaly detected' },
  { id: 'budget_alert', name: 'Budget threshold exceeded' },
  { id: 'forecast_overrun', name: 'Cost forecast overrun' },
  { id: 'spend_spike', name: 'Daily spend spike' },
  { id: 'idle_resource', name: 'Idle resource detected' },
  { id: 'instance.preempted', name: 'Instance preempted' },
  { id: 'instance.terminated_abnormally', name: 'Instance terminated abnormally' },
  { id: 'mig.unhealthy', name: 'Instance group unhealthy' },
  { id: 'nodepool.degraded', name: 'GKE node pool degraded' },
  { id: 'cloudrun.revision_failed', name: 'Cloud Run revision failed' },
  { id: 'monitoring.alert_fired', name: 'Cloud Monitoring alert fired' },
  { id: 'logging.error_pattern', name: 'Log error pattern matched' },
  { id: 'build.failed', name: 'Cloud Build failed' },
  { id: 'scc.finding', name: 'Security Command Center finding' },
  { id: 'iam.policy_change', name: 'Privileged IAM grant' },
  { id: 'sa_key.stale', name: 'Service account key aged' },
  { id: 'storage.public_access', name: 'Public storage bucket detected' },
  { id: 'iam.public_binding', name: 'Public IAM binding detected' },
  { id: 'bigquery.job_failed', name: 'BigQuery job failed' },
  { id: 'bigquery.expensive_query', name: 'Expensive BigQuery query' },
  { id: 'cloudsql.unhealthy', name: 'Cloud SQL instance unhealthy' },
  { id: 'pubsub.backlog', name: 'Pub/Sub backlog growing' },
  { id: 'dataflow.job_failed', name: 'Dataflow job failed' },
];

// GCP_PRIVILEGED_ROLE_OPTIONS mirrors privilegedRoles in
// server/pkg/gcp/security.go: roles that can read everything, change IAM, or act
// as another identity. Listed statically rather than fetched because the IAM API
// returns thousands of roles, of which only these are relevant to the
// privileged-grant trigger.
const GCP_PRIVILEGED_ROLE_OPTIONS: { id: string; name: string }[] = [
  { id: 'roles/owner', name: 'Owner' },
  { id: 'roles/editor', name: 'Editor' },
  { id: 'roles/iam.securityAdmin', name: 'Security Admin' },
  { id: 'roles/iam.serviceAccountKeyAdmin', name: 'Service Account Key Admin' },
  { id: 'roles/iam.serviceAccountTokenCreator', name: 'Service Account Token Creator' },
  { id: 'roles/iam.serviceAccountUser', name: 'Service Account User' },
  { id: 'roles/resourcemanager.projectIamAdmin', name: 'Project IAM Admin' },
  { id: 'roles/iam.organizationRoleAdmin', name: 'Organization Role Admin' },
  { id: 'roles/iam.roleAdmin', name: 'Role Admin' },
  { id: 'roles/storage.admin', name: 'Storage Admin' },
  { id: 'roles/secretmanager.admin', name: 'Secret Manager Admin' },
];

const GCP_IDLE_RESOURCE_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'instance', name: 'Idle VM instances' },
  { id: 'disk', name: 'Unattached persistent disks' },
  { id: 'address', name: 'Unused static IPs' },
  { id: 'snapshot', name: 'Stale snapshots' },
];

// GCP severities as reported by Security Command Center and the cost pollers.
const GCP_SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low'];

// Which filter fields are meaningful for which events. A field is shown when the
// trigger matches every event ('*') or at least one event that actually reads it,
// so the panel never asks for a threshold the matcher would ignore.
const GCP_FIELD_EVENTS: Record<string, string[]> = {
  zones: ['instance.preempted', 'instance.terminated_abnormally', 'mig.unhealthy', 'nodepool.degraded', 'idle_resource'],
  instances: ['instance.preempted', 'instance.terminated_abnormally', 'idle_resource'],
  instance_groups: ['mig.unhealthy'],
  clusters: ['nodepool.degraded'],
  node_pools: ['nodepool.degraded'],
  services: ['cloudrun.revision_failed'],
  build_triggers: ['build.failed'],
  alert_policies: ['monitoring.alert_fired'],
  log: ['logging.error_pattern'],
  finding_categories: ['scc.finding'],
  service_accounts: ['iam.policy_change', 'sa_key.stale'],
  roles: ['iam.policy_change'],
  buckets: ['storage.public_access'],
  key_age: ['sa_key.stale'],
  datasets: ['bigquery.job_failed', 'bigquery.expensive_query'],
  sql_instances: ['cloudsql.unhealthy'],
  subscriptions: ['pubsub.backlog'],
  dataflow_jobs: ['dataflow.job_failed'],
  min_cost_impact: ['cost_anomaly', 'forecast_overrun'],
  spike_percent: ['spend_spike'],
  budget_threshold: ['budget_alert'],
  min_savings: ['idle_resource'],
  idle_types: ['idle_resource'],
  unhealthy_threshold: ['mig.unhealthy', 'cloudsql.unhealthy'],
  min_duration: ['nodepool.degraded'],
  min_error_count: ['logging.error_pattern'],
  max_bytes_billed: ['bigquery.expensive_query'],
  max_backlog: ['pubsub.backlog'],
};

const CLICKHOUSE_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'service.state_changed', name: 'Service state changed' },
  { id: 'service.idle', name: 'Service idle' },
  { id: 'service.scaled', name: 'Service scaled' },
  { id: 'service.version_changed', name: 'Version changed' },
  { id: 'backup.completed', name: 'Backup completed' },
  { id: 'backup.failed', name: 'Backup failed' },
  { id: 'query.error_spike', name: 'Query error spike' },
  { id: 'clickpipe.failed', name: 'ClickPipe failed' },
  { id: 'usage.threshold', name: 'Usage/spend threshold' },
  { id: 'service.too_many_parts', name: 'Too many parts' },
  { id: 'service.high_query_concurrency', name: 'High query concurrency' },
];

const JENKINS_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'build.started', name: 'Build started' },
  { id: 'build.completed', name: 'Build completed' },
];

const JENKINS_BUILD_STATUS_OPTIONS: { id: string; name: string }[] = [
  { id: 'SUCCESS', name: 'Success' },
  { id: 'FAILURE', name: 'Failure' },
  { id: 'UNSTABLE', name: 'Unstable' },
  { id: 'ABORTED', name: 'Aborted' },
];

const CIRCLECI_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'workflow-completed', name: 'Workflow completed' },
  { id: 'job-completed', name: 'Job completed' },
];

const SONARCLOUD_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'analysis.completed', name: 'Analysis completed' },
  { id: 'analysis.failed', name: 'Analysis failed' },
];

const SONARCLOUD_QUALITY_GATE_STATUS_OPTIONS: { id: string; name: string }[] = [
  { id: 'OK', name: 'OK (passed)' },
  { id: 'ERROR', name: 'Error (failed)' },
  { id: 'NONE', name: 'None (no quality gate)' },
];

const OKTA_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'user.account.lock', name: 'User locked out' },
  { id: 'user.account.report_suspicious_activity_by_enduser', name: 'Suspicious activity reported' },
  { id: 'security.threat.detected', name: 'Threat detected by Okta' },
  { id: 'user.account.privilege.grant', name: 'Admin privilege granted' },
  { id: 'user.mfa.factor.deactivate', name: 'MFA factor deactivated' },
  { id: 'user.mfa.factor.reset_all', name: 'All MFA factors reset' },
  { id: 'user.lifecycle.create', name: 'User created' },
  { id: 'user.lifecycle.deactivate', name: 'User deactivated' },
  { id: 'user.lifecycle.suspend', name: 'User suspended' },
  { id: 'group.user_membership.add', name: 'User added to group' },
  { id: 'group.user_membership.remove', name: 'User removed from group' },
  { id: 'application.user_membership.add', name: 'User assigned to app' },
  { id: 'application.user_membership.remove', name: 'User removed from app' },
];

const OKTA_OUTCOME_OPTIONS: { id: string; name: string }[] = [
  { id: 'SUCCESS', name: 'Success' },
  { id: 'FAILURE', name: 'Failure' },
];

// Kyverno violation severities as annotated on policies via
// policies.kyverno.io/severity (mirrors the KyvernoSeverities matcher).
const KYVERNO_SEVERITY_OPTIONS: { id: string; name: string }[] = [
  { id: 'critical', name: 'Critical' },
  { id: 'high', name: 'High' },
  { id: 'medium', name: 'Medium' },
  { id: 'low', name: 'Low' },
  { id: 'info', name: 'Info' },
];

// Trivy finding severities (mirrors the TrivySeverities matcher).
const TRIVY_SEVERITY_OPTIONS: { id: string; name: string }[] = [
  { id: 'critical', name: 'Critical' },
  { id: 'high', name: 'High' },
  { id: 'medium', name: 'Medium' },
  { id: 'low', name: 'Low' },
];

// Trivy report resource kinds (mirrors the TrivyResourceKinds matcher).
const TRIVY_RESOURCE_KIND_OPTIONS: { id: string; name: string }[] = [
  { id: 'Deployment', name: 'Deployment' },
  { id: 'StatefulSet', name: 'StatefulSet' },
  { id: 'DaemonSet', name: 'DaemonSet' },
  { id: 'CronJob', name: 'CronJob' },
  { id: 'Job', name: 'Job' },
  { id: 'Pod', name: 'Pod' },
  { id: 'Role', name: 'Role' },
  { id: 'ClusterRole', name: 'ClusterRole' },
];

const CIRCLECI_STATUS_OPTIONS: { id: string; name: string }[] = [
  { id: 'success', name: 'Success' },
  { id: 'failed', name: 'Failed' },
  { id: 'error', name: 'Error' },
  { id: 'canceled', name: 'Canceled' },
  { id: 'unauthorized', name: 'Unauthorized' },
];

const TERRAFORM_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'run:created', name: 'Run created' },
  { id: 'run:planning', name: 'Run planning' },
  { id: 'run:needs_attention', name: 'Run needs attention' },
  { id: 'run:applying', name: 'Run applying' },
  { id: 'run:completed', name: 'Run completed' },
  { id: 'run:errored', name: 'Run errored' },
  { id: 'assessment:drifted', name: 'Drift detected' },
  { id: 'assessment:check_failure', name: 'Assessment check failed' },
  { id: 'assessment:failed', name: 'Assessment failed' },
];

const TERRAFORM_RUN_STATUS_OPTIONS: { id: string; name: string }[] = [
  { id: 'pending', name: 'Pending' },
  { id: 'planning', name: 'Planning' },
  { id: 'planned', name: 'Planned' },
  { id: 'planned_and_finished', name: 'Planned and finished' },
  { id: 'applying', name: 'Applying' },
  { id: 'applied', name: 'Applied' },
  { id: 'errored', name: 'Errored' },
  { id: 'discarded', name: 'Discarded' },
  { id: 'canceled', name: 'Canceled' },
];

const PULUMI_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'stack_created', name: 'Stack created' },
  { id: 'stack_deleted', name: 'Stack deleted' },
  { id: 'update_succeeded', name: 'Update succeeded' },
  { id: 'update_failed', name: 'Update failed' },
  { id: 'preview_succeeded', name: 'Preview succeeded' },
  { id: 'preview_failed', name: 'Preview failed' },
  { id: 'refresh_succeeded', name: 'Refresh succeeded' },
  { id: 'refresh_failed', name: 'Refresh failed' },
  { id: 'destroy_succeeded', name: 'Destroy succeeded' },
  { id: 'destroy_failed', name: 'Destroy failed' },
  { id: 'deployment_started', name: 'Deployment started' },
  { id: 'deployment_succeeded', name: 'Deployment succeeded' },
  { id: 'deployment_failed', name: 'Deployment failed' },
  { id: 'drift_detected', name: 'Drift detected' },
  { id: 'drift_detection_succeeded', name: 'Drift detection succeeded' },
  { id: 'drift_detection_failed', name: 'Drift detection failed' },
  { id: 'drift_remediation_succeeded', name: 'Drift remediation succeeded' },
  { id: 'drift_remediation_failed', name: 'Drift remediation failed' },
  { id: 'policy_violation_mandatory', name: 'Policy violation (mandatory)' },
  { id: 'policy_violation_advisory', name: 'Policy violation (advisory)' },
];

const VAULT_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'seal.sealed', name: 'Vault sealed' },
  { id: 'seal.unsealed', name: 'Vault unsealed' },
  { id: 'health.degraded', name: 'Health degraded' },
  { id: 'secret.version_created', name: 'Secret version created' },
  { id: 'secret.stale', name: 'Secret stale (rotation due)' },
  { id: 'policy.created', name: 'Policy created' },
  { id: 'policy.deleted', name: 'Policy deleted' },
  { id: 'auth_method.enabled', name: 'Auth method enabled' },
  { id: 'auth_method.disabled', name: 'Auth method disabled' },
];

const INFISICAL_EVENT_TYPE_OPTIONS: { id: string; name: string }[] = [
  { id: 'secret.created', name: 'Secret created' },
  { id: 'secret.updated', name: 'Secret updated' },
  { id: 'secret.deleted', name: 'Secret deleted' },
  { id: 'approval.requested', name: 'Approval requested' },
  { id: 'sync.failed', name: 'Secret sync failed' },
  { id: 'identity.created', name: 'Machine identity created' },
];

// Common Infisical environment slugs. Static list so the dropdown works before
// a project is selected; users can type any custom slug via the template input.
const INFISICAL_ENVIRONMENT_OPTIONS: { id: string; name: string }[] = [
  { id: 'dev', name: 'Development (dev)' },
  { id: 'staging', name: 'Staging (staging)' },
  { id: 'prod', name: 'Production (prod)' },
];

// Fly.io platform regions (https://fly.io/docs/reference/regions/). Static list so
// the dropdown works even for empty accounts; users can still match any region via "All".
const FLY_REGION_OPTIONS: { id: string; name: string }[] = [
  { id: 'ams', name: 'Amsterdam, Netherlands (ams)' },
  { id: 'arn', name: 'Stockholm, Sweden (arn)' },
  { id: 'atl', name: 'Atlanta, Georgia (atl)' },
  { id: 'bog', name: 'Bogotá, Colombia (bog)' },
  { id: 'bom', name: 'Mumbai, India (bom)' },
  { id: 'bos', name: 'Boston, Massachusetts (bos)' },
  { id: 'cdg', name: 'Paris, France (cdg)' },
  { id: 'den', name: 'Denver, Colorado (den)' },
  { id: 'dfw', name: 'Dallas, Texas (dfw)' },
  { id: 'ewr', name: 'Secaucus, NJ (ewr)' },
  { id: 'eze', name: 'Ezeiza, Argentina (eze)' },
  { id: 'fra', name: 'Frankfurt, Germany (fra)' },
  { id: 'gdl', name: 'Guadalajara, Mexico (gdl)' },
  { id: 'gig', name: 'Rio de Janeiro, Brazil (gig)' },
  { id: 'gru', name: 'São Paulo, Brazil (gru)' },
  { id: 'hkg', name: 'Hong Kong (hkg)' },
  { id: 'iad', name: 'Ashburn, Virginia (iad)' },
  { id: 'jnb', name: 'Johannesburg, South Africa (jnb)' },
  { id: 'lax', name: 'Los Angeles, California (lax)' },
  { id: 'lhr', name: 'London, United Kingdom (lhr)' },
  { id: 'mad', name: 'Madrid, Spain (mad)' },
  { id: 'mia', name: 'Miami, Florida (mia)' },
  { id: 'nrt', name: 'Tokyo, Japan (nrt)' },
  { id: 'ord', name: 'Chicago, Illinois (ord)' },
  { id: 'otp', name: 'Bucharest, Romania (otp)' },
  { id: 'phx', name: 'Phoenix, Arizona (phx)' },
  { id: 'qro', name: 'Querétaro, Mexico (qro)' },
  { id: 'scl', name: 'Santiago, Chile (scl)' },
  { id: 'sea', name: 'Seattle, Washington (sea)' },
  { id: 'sin', name: 'Singapore (sin)' },
  { id: 'sjc', name: 'San Jose, California (sjc)' },
  { id: 'syd', name: 'Sydney, Australia (syd)' },
  { id: 'waw', name: 'Warsaw, Poland (waw)' },
  { id: 'yul', name: 'Montreal, Canada (yul)' },
  { id: 'yyz', name: 'Toronto, Canada (yyz)' },
];

// MultiSelectWithTemplate: a multi-select that supports a "* = All" pseudo value, a
// free-text fallback when no options are available, and (optionally) template-variable
// values. When the selection is a single template variable (e.g. {{signal.app_name}})
// it renders a read-only code chip with a Clear button, mirroring the action
// dynamic_select behavior so the dropdown "supports template variables".
function MultiSelectWithTemplate({
  options,
  selectedValues,
  onChange,
  allLabel = 'All',
  placeholder,
  allowTemplate = true,
}: {
  options: { id: string; name: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  allLabel?: string;
  placeholder?: string;
  allowTemplate?: boolean;
}) {
  const vals = selectedValues && selectedValues.length > 0 ? selectedValues : ['*'];
  const isTemplate = allowTemplate && vals.some(v => typeof v === 'string' && v.includes('{{'));

  // Template-variable mode: show the value(s) as a code chip with a Clear control.
  if (isTemplate) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs overflow-x-auto whitespace-nowrap">
          {vals.join(', ')}
        </code>
        <button
          type="button"
          onClick={() => onChange(['*'])}
          className="text-xs text-muted-foreground hover:text-foreground shrink-0"
        >
          Clear
        </button>
      </div>
    );
  }

  // No options loaded (e.g. not connected / empty account): free-text fallback that
  // still lets the user type values (or a template variable).
  if (options.length === 0) {
    return (
      <Input
        value={vals.filter(v => v !== '*').join(', ')}
        onChange={e => {
          const raw = e.target.value.trim();
          const parsed = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : ['*'];
          onChange(parsed.length > 0 ? parsed : ['*']);
        }}
        className="mt-1 h-8 text-sm"
        placeholder={placeholder || allLabel}
      />
    );
  }

  return (
    <ClusterMultiSelect
      clusters={options}
      selectedIds={vals}
      onChange={ids => onChange(ids.length > 0 ? ids : ['*'])}
      allLabel={allLabel}
    />
  );
}

function NamespaceMultiSelect({
  clusterIds,
  selectedNamespaces,
  onChange,
}: {
  clusterIds: string[];
  selectedNamespaces: string[];
  onChange: (ns: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<{ name: string; cluster_id: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const isAll = selectedNamespaces.length === 0 || (selectedNamespaces.length === 1 && selectedNamespaces[0] === '*');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.getWorkflowTriggerFilterOptions(clusterIds).then(data => {
      if (!cancelled) {
        const unique = Array.from(new Map(data.namespaces.map(n => [n.name, n])).values());
        setOptions(unique);
        setLoading(false);
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clusterIds.join(',')]);

  const filtered = search
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  const displayText = isAll
    ? 'All Namespaces'
    : selectedNamespaces.filter(n => n !== '*').join(', ') || 'All Namespaces';

  const toggle = (name: string) => {
    if (name === '*') { onChange(['*']); return; }
    let next: string[];
    if (isAll) {
      next = [name];
    } else if (selectedNamespaces.includes(name)) {
      next = selectedNamespaces.filter(x => x !== name);
      if (next.length === 0) next = ['*'];
    } else {
      next = [...selectedNamespaces.filter(x => x !== '*'), name];
    }
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {options.length > 5 && (
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search namespaces..."
            className="w-full px-2 py-1.5 text-xs border-b border-input bg-transparent outline-none mb-1"
          />
        )}
        <div className="max-h-48 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <button type="button" onClick={() => toggle('*')} className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAll ? 'bg-primary border-primary' : 'border-input'}`}>
                  {isAll && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                All Namespaces
              </button>
              {filtered.map(o => {
                const checked = !isAll && selectedNamespaces.includes(o.name);
                return (
                  <button key={o.name} type="button" onClick={() => toggle(o.name)} className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    {o.name}
                  </button>
                );
              })}
              {filtered.length === 0 && !loading && (
                <p className="text-xs text-muted-foreground text-center py-2">No namespaces found</p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function WorkloadMultiSelect({
  clusterIds,
  namespaces,
  selectedWorkloads,
  onChange,
}: {
  clusterIds: string[];
  namespaces: string[];
  selectedWorkloads: string[];
  onChange: (wl: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<{ name: string; kind: string; namespace: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const isAll = selectedWorkloads.length === 0 || (selectedWorkloads.length === 1 && selectedWorkloads[0] === '*');

  const nsParam = namespaces.length === 0 || (namespaces.length === 1 && namespaces[0] === '*') ? undefined : namespaces;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.getWorkflowTriggerFilterOptions(clusterIds, nsParam).then(data => {
      if (!cancelled) {
        const unique = Array.from(new Map(data.workloads.map(w => [w.namespace + '/' + w.name, w])).values());
        setOptions(unique);
        setLoading(false);
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clusterIds.join(','), (nsParam || []).join(',')]);

  const filtered = search
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()) || o.namespace.toLowerCase().includes(search.toLowerCase()))
    : options;

  const displayText = isAll
    ? 'All Workloads'
    : selectedWorkloads.filter(w => w !== '*').join(', ') || 'All Workloads';

  const toggle = (name: string) => {
    if (name === '*') { onChange(['*']); return; }
    let next: string[];
    if (isAll) {
      next = [name];
    } else if (selectedWorkloads.includes(name)) {
      next = selectedWorkloads.filter(x => x !== name);
      if (next.length === 0) next = ['*'];
    } else {
      next = [...selectedWorkloads.filter(x => x !== '*'), name];
    }
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {options.length > 5 && (
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search workloads..."
            className="w-full px-2 py-1.5 text-xs border-b border-input bg-transparent outline-none mb-1"
          />
        )}
        <div className="max-h-56 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <button type="button" onClick={() => toggle('*')} className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAll ? 'bg-primary border-primary' : 'border-input'}`}>
                  {isAll && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                All Workloads
              </button>
              {filtered.map(o => {
                const checked = !isAll && selectedWorkloads.includes(o.name);
                return (
                  <button key={o.namespace + '/' + o.name} type="button" onClick={() => toggle(o.name)} className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="truncate">{o.name}</span>
                      <span className="text-[10px] text-muted-foreground">{o.kind} · {o.namespace}</span>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && !loading && (
                <p className="text-xs text-muted-foreground text-center py-2">No workloads found</p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AWSAccountMultiSelect({
  accounts,
  selectedIds,
  onChange,
}: {
  accounts: { key: string; name: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const isAll = selectedIds.length === 0 || (selectedIds.length === 1 && selectedIds[0] === '*');

  const displayText = isAll
    ? 'All Accounts'
    : accounts
        .filter(a => selectedIds.includes(a.key))
        .map(a => a.name)
        .join(', ') || 'All Accounts';

  const toggle = (id: string) => {
    if (id === '*') {
      onChange(['*']);
      return;
    }
    let next: string[];
    if (isAll) {
      next = [id];
    } else if (selectedIds.includes(id)) {
      next = selectedIds.filter(x => x !== id);
      if (next.length === 0) next = ['*'];
    } else {
      next = [...selectedIds.filter(x => x !== '*'), id];
    }
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          <button
            type="button"
            onClick={() => toggle('*')}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${isAll ? 'bg-primary border-primary' : 'border-input'}`}>
              {isAll && <Check className="w-3 h-3 text-primary-foreground" />}
            </div>
            All Accounts
          </button>
          {accounts.map(a => {
            const checked = !isAll && selectedIds.includes(a.key);
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => toggle(a.key)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                  {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                {a.name}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SlackSearchSelect({
  source,
  value,
  onChange,
  placeholder,
}: {
  source: 'slack_channels' | 'slack_users';
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const prevValueRef = React.useRef(value);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset label when value changes externally (e.g., switching to different node)
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setSelectedLabel('');
    }
  }, [value]);

  // Resolve value to display label
  React.useEffect(() => {
    if (value && !selectedLabel) {
      if (source === 'slack_channels') {
        setSelectedLabel(`#${value}`);
      } else {
        (async () => {
          try {
            const users = await apiClient.searchSlackUsers('');
            const match = users.find(u => u.id === value);
            setSelectedLabel(match ? (match.real_name || match.display_name || match.name) : value);
          } catch {
            setSelectedLabel(value);
          }
        })();
      }
    }
  }, [value, selectedLabel, source]);

  const doSearch = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      if (source === 'slack_channels') {
        const channels = await apiClient.searchSlackChannels(q);
        setOptions(channels.map(c => ({ id: c.name, label: `#${c.name}` })));
      } else {
        const users = await apiClient.searchSlackUsers(q);
        setOptions(users.map(u => ({
          id: u.id,
          label: u.real_name || u.display_name || u.name,
        })));
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [source]);

  React.useEffect(() => {
    if (open && options.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const displayValue = selectedLabel || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => handleInput(e.target.value)}
          placeholder={`Search ${source === 'slack_channels' ? 'channels' : 'users'}...`}
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Searching...</p>}
          {!loading && options.length === 0 && query && <p className="text-xs text-muted-foreground p-2">No results</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                onChange(o.id);
                setSelectedLabel(o.label);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.id ? 'bg-accent font-medium' : ''}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Jira issue types, scoped to the selected project.
function JiraIssueTypeSelect({
  value,
  onChange,
  projectKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  projectKey: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const doFetch = React.useCallback(async () => {
    if (!projectKey) { setOptions([]); return; }
    setLoading(true);
    try {
      const types = await apiClient.getJiraIssueTypes(projectKey);
      setOptions(types);
      // Auto-resolve: if the current value is a name, keep it for display
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  React.useEffect(() => {
    if (open) doFetch();
  }, [open, projectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = value
    ? (options.find(o => o.name === value || o.id === value)?.name || value)
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || (projectKey ? placeholder : 'Select a project first')}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {!projectKey ? (
          <p className="text-xs text-muted-foreground p-2">Select a project first</p>
        ) : (
          <div className="max-h-40 overflow-y-auto">
            {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
            {!loading && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No issue types found</p>}
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
              >
                Clear selection
              </button>
            )}
            {options.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onChange(o.name);
                  setOpen(false);
                }}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name || value === o.id ? 'bg-accent font-medium' : ''}`}
              >
                <span className="truncate">{o.name}</span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Jira assignee, backed by the Jira user search API.
// Stores the Jira accountId as the value. Shows display name + email in the dropdown.
function JiraUserSearchSelect({
  value,
  onChange,
  projectKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  projectKey?: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ account_id: string; display_name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const prevValueRef = React.useRef(value);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset label when value changes externally
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setSelectedLabel('');
    }
  }, [value]);

  // Resolve account ID to display name
  React.useEffect(() => {
    if (value && !selectedLabel) {
      (async () => {
        try {
          const users = await apiClient.getJiraUsers('', projectKey);
          const match = users.find(u => u.account_id === value);
          setSelectedLabel(match ? (match.display_name || match.email) : value);
        } catch {
          setSelectedLabel(value);
        }
      })();
    }
  }, [value, selectedLabel, projectKey]);

  const doSearch = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      const users = await apiClient.getJiraUsers(q, projectKey);
      setOptions(users);
      if (value && !selectedLabel) {
        const match = users.find(u => u.account_id === value);
        if (match) setSelectedLabel(match.display_name || match.email);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [projectKey, value, selectedLabel]);

  React.useEffect(() => {
    if (open && options.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const displayValue = selectedLabel || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => handleInput(e.target.value)}
          placeholder="Search users..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Searching...</p>}
          {!loading && options.length === 0 && query && <p className="text-xs text-muted-foreground p-2">No results</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(u => (
            <button
              key={u.account_id}
              type="button"
              onClick={() => {
                onChange(u.account_id);
                setSelectedLabel(u.display_name || u.email);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === u.account_id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{u.display_name}{u.email ? ` · ${u.email}` : ''}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Fuzzy-match a raw assignee string (name, display name, or email — e.g. the
// workflow-builder agent's "raman") to a Linear user. Mirrors the server-side
// resolveLinearUserID heuristic: exact (case-insensitive) match on
// name/displayName/email wins; otherwise substring containment in either
// direction. Returns undefined if no reasonable match.
function fuzzyMatchLinearUser(
  raw: string,
  users: { id: string; name: string; display_name: string; email: string }[],
): { id: string; name: string; display_name: string; email: string } | undefined {
  const input = raw.trim().toLowerCase();
  if (!input) return undefined;
  const fields = (u: { name: string; display_name: string; email: string }) =>
    [u.name, u.display_name, u.email].map(f => (f || '').toLowerCase());

  const exact = users.find(u => fields(u).some(f => f === input));
  if (exact) return exact;
  // Email local-part exact match (e.g. "raman" matches "raman@usekestrel.ai").
  const emailLocal = users.find(u => (u.email || '').toLowerCase().split('@')[0] === input);
  if (emailLocal) return emailLocal;
  // Substring containment (input in a field, or a field in the input).
  return users.find(u => fields(u).some(f => f && (f.includes(input) || input.includes(f))));
}

// Searchable single-select for Linear workspace users. Stores the Linear
// user ID as the value. Shows display names + emails.
function LinearUserSearchSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string; display_name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const prevValueRef = React.useRef(value);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset label when value changes externally
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setSelectedLabel('');
    }
  }, [value]);

  // Resolve the stored value to a display name. The value is normally a Linear
  // user ID, but the workflow-builder agent stores the user's raw text (e.g.
  // "raman"); in that case fuzzy-match it to a real user and rewrite the config
  // to the canonical ID so the UI and stored value agree (mirrors the
  // GitHub-repo normalization pattern; the server fuzzy-matches at execution
  // time regardless).
  React.useEffect(() => {
    if (value && !value.includes('{{') && !selectedLabel) {
      (async () => {
        try {
          const users = await apiClient.getLinearUsers('');
          const match = users.find(u => u.id === value);
          if (match) {
            setSelectedLabel(match.name || match.display_name || match.email);
            return;
          }
          const fuzzy = fuzzyMatchLinearUser(value, users);
          if (fuzzy) {
            setSelectedLabel(fuzzy.name || fuzzy.display_name || fuzzy.email);
            if (fuzzy.id !== value) onChange(fuzzy.id);
            return;
          }
          setSelectedLabel(value);
        } catch {
          setSelectedLabel(value);
        }
      })();
    }
  }, [value, selectedLabel, onChange]);

  const doSearch = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      const users = await apiClient.getLinearUsers(q);
      setOptions(users);
      if (value && !value.includes('{{') && !selectedLabel) {
        const match = users.find(u => u.id === value);
        if (match) {
          setSelectedLabel(match.name || match.display_name || match.email);
        } else {
          const fuzzy = fuzzyMatchLinearUser(value, users);
          if (fuzzy) {
            setSelectedLabel(fuzzy.name || fuzzy.display_name || fuzzy.email);
            if (fuzzy.id !== value) onChange(fuzzy.id);
          }
        }
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [value, selectedLabel, onChange]);

  React.useEffect(() => {
    if (open && options.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const displayValue = selectedLabel || value || '';

  if (value && value.includes('{{')) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">
          {value}
        </code>
        <button
          type="button"
          onClick={() => { onChange(''); setSelectedLabel(''); }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => handleInput(e.target.value)}
          placeholder="Search users..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Searching...</p>}
          {!loading && options.length === 0 && query && <p className="text-xs text-muted-foreground p-2">No results</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => {
                onChange(u.id);
                setSelectedLabel(u.name || u.display_name || u.email);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === u.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{u.name || u.display_name}{u.email ? ` · ${u.email}` : ''}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Multi-select for Linear labels scoped to the selected team. Stores a
// comma-separated list of label NAMES (the server resolves names to IDs at
// execution time), so it stays compatible with template-variable values.
function LinearLabelMultiSelect({
  value,
  onChange,
  teamKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  teamKey: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const selected = value
    ? value.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  const doFetch = React.useCallback(async () => {
    if (!teamKey) { setOptions([]); return; }
    setLoading(true);
    try {
      const opts = await apiClient.getLinearLabels(teamKey);
      setOptions(opts);
      // Normalize raw/agent-provided label names (e.g. "customer") to the
      // canonical option casing (e.g. "Customer") and rewrite the stored value
      // so the UI and persisted config agree. The server resolves
      // case-insensitively at execution time, but normalizing here keeps the
      // builder display honest. Skip template-variable values.
      if (value && !value.includes('{{')) {
        const byLower = new Map(opts.map(o => [o.name.toLowerCase(), o.name]));
        const current = value.split(',').map(s => s.trim()).filter(Boolean);
        const normalized = current.map(n => byLower.get(n.toLowerCase()) ?? n);
        const next = normalized.join(', ');
        if (next !== value) onChange(next);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [teamKey, value, onChange]);

  React.useEffect(() => {
    if (open) doFetch();
  }, [open, teamKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Template-variable value: render as code with a clear button.
  if (value && value.includes('{{')) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">
          {value}
        </code>
        <button type="button" onClick={() => onChange('')} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
      </div>
    );
  }

  // Case-insensitive membership so a stored "customer" matches option
  // "Customer" (toggling off works regardless of casing).
  const isSelected = (name: string) => selected.some(s => s.toLowerCase() === name.toLowerCase());

  const toggle = (name: string) => {
    const next = isSelected(name)
      ? selected.filter(n => n.toLowerCase() !== name.toLowerCase())
      : [...selected, name];
    onChange(next.join(', '));
  };

  const filtered = search
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  const displayText = selected.length > 0 ? selected.join(', ') : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <span className={`truncate ${displayText ? '' : 'text-muted-foreground'}`}>
            {displayText || (teamKey ? placeholder : 'Select a team first')}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {!teamKey ? (
          <p className="text-xs text-muted-foreground p-2">Select a team first</p>
        ) : (
          <>
            {options.length > 5 && (
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search labels..."
                className="w-full px-2 py-1.5 text-xs border-b border-input bg-transparent outline-none mb-1"
              />
            )}
            <div className="max-h-48 overflow-y-auto">
              {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
              {!loading && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No labels found</p>}
              {!loading && filtered.map(o => {
                const checked = isSelected(o.name);
                return (
                  <button key={o.id} type="button" onClick={() => toggle(o.name)} className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <span className="truncate">{o.name}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Single-select for a Linear project scoped to the selected team. Stores the
// project NAME (the server resolves the name to an ID at execution time).
function LinearProjectSelect({
  value,
  onChange,
  teamKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  teamKey: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const doFetch = React.useCallback(async () => {
    if (!teamKey) { setOptions([]); return; }
    setLoading(true);
    try {
      setOptions(await apiClient.getLinearProjects(teamKey));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [teamKey]);

  React.useEffect(() => {
    if (open) doFetch();
  }, [open, teamKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (value && value.includes('{{')) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">
          {value}
        </code>
        <button type="button" onClick={() => onChange('')} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
      </div>
    );
  }

  const displayValue = value
    ? (options.find(o => o.name === value || o.id === value)?.name || value)
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || (teamKey ? placeholder : 'Select a team first')}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {!teamKey ? (
          <p className="text-xs text-muted-foreground p-2">Select a team first</p>
        ) : (
          <div className="max-h-48 overflow-y-auto">
            {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
            {!loading && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No projects found</p>}
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
              >
                Clear selection
              </button>
            )}
            {!loading && options.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.name); setOpen(false); }}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name || value === o.id ? 'bg-accent font-medium' : ''}`}
              >
                <span className="truncate">{o.name}</span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Single-select for a Linear workflow state (status). The team is derived from the
// target issue identifier when it's a concrete value; when the issue is only known at
// runtime (a template variable) or no states are returned, it falls back to a free-text
// input so the user can still type a state name or insert a template variable.
function LinearStateSelect({
  value,
  onChange,
  issueIdentifier,
  teamKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  issueIdentifier: string;
  teamKey: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const hasTemplateIssue = issueIdentifier.includes('{{');
  const canFetch = Boolean(teamKey) || (Boolean(issueIdentifier) && !hasTemplateIssue);

  const doFetch = React.useCallback(async () => {
    if (!canFetch) { setOptions([]); setFetched(true); return; }
    setLoading(true);
    try {
      setOptions(await apiClient.getLinearStates({ team: teamKey || undefined, issue: hasTemplateIssue ? undefined : (issueIdentifier || undefined) }));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [canFetch, teamKey, issueIdentifier, hasTemplateIssue]);

  React.useEffect(() => {
    if (open) doFetch();
  }, [open, teamKey, issueIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  // When no concrete team/issue is known, fall back to free text so a state name or
  // template variable can still be entered. (Template-variable VALUES are handled by
  // the universal gate before this component renders.)
  if (!canFetch || (fetched && !loading && options.length === 0)) {
    return (
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'In Progress'}
        className="mt-1 h-8 text-sm"
      />
    );
  }

  const displayValue = value
    ? (options.find(o => o.name === value || o.id === value)?.name || value)
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder || 'Select a status'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {!loading && options.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange(o.name); setOpen(false); }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name || value === o.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Confluence pages in a given space.
// Stores the Confluence page ID as the value. Shows page titles.
function ConfluencePageSelect({
  value,
  onChange,
  spaceKey,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  spaceKey: string;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  React.useEffect(() => {
    if (value && !selectedLabel) {
      setSelectedLabel(value);
    }
  }, [value, selectedLabel]);

  const doSearch = React.useCallback(async (q: string) => {
    if (!spaceKey) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const pages = await apiClient.getConfluencePages(spaceKey, q);
      setOptions(pages);
      if (value && !selectedLabel) {
        const match = pages.find(p => p.id === value);
        if (match) setSelectedLabel(match.title);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [spaceKey, value, selectedLabel]);

  React.useEffect(() => {
    if (open) {
      doSearch('');
    }
  }, [open, spaceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const displayValue = selectedLabel || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || (spaceKey ? placeholder : 'Select a space first')}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {!spaceKey ? (
          <p className="text-xs text-muted-foreground p-2">Select a space first</p>
        ) : (
          <>
            <Input
              value={query}
              onChange={e => handleInput(e.target.value)}
              placeholder="Search pages..."
              className="h-7 text-xs mb-1"
              autoFocus
            />
            <div className="max-h-48 overflow-y-auto">
              {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
              {!loading && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No pages found</p>}
              {value && (
                <button
                  type="button"
                  onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
                >
                  Clear selection
                </button>
              )}
              {options.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onChange(p.id);
                    setSelectedLabel(p.title);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === p.id ? 'bg-accent font-medium' : ''}`}
                >
                  <span className="truncate">{p.title}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for ArgoCD applications.
// Searchable single-select for ArgoCD clusters (only those with has_argocd=true).
function ArgoCDClusterSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const clusters = await apiClient.getArgoCDClusters();
      setOptions(clusters);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  React.useEffect(() => {
    if (open && !fetched) {
      doFetch();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { if (value && !fetched) doFetch(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedName = options.find(o => o.id === value)?.name || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedName ? '' : 'text-muted-foreground'}`}>
            {selectedName || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {(loading || !fetched) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetched && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No ArgoCD clusters found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === c.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ArgoCDAppSelect({
  value,
  onChange,
  placeholder,
  clusterId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForCluster, setFetchedForCluster] = useState('');

  const doFetch = React.useCallback(async (cid?: string) => {
    setLoading(true);
    try {
      const apps = await apiClient.getArgoCDApps(cid);
      setOptions(apps.map(a => a.name));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForCluster(cid || '_default');
    }
  }, []);

  React.useEffect(() => {
    if (open && fetchedForCluster !== (clusterId || '_default')) {
      doFetch(clusterId);
    }
  }, [open, clusterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(n => n.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search applications..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForCluster) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForCluster && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No applications found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => {
                onChange(name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ArgoCDHistorySelect({
  value,
  onChange,
  placeholder,
  clusterId,
  appName,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
  appName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Array<{ id: string; label: string; is_default: boolean }>>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForApp, setFetchedForApp] = useState('');

  const doFetch = React.useCallback(async () => {
    if (!appName) { setOptions([]); return; }
    setLoading(true);
    try {
      const history = await apiClient.getArgoCDHistory(clusterId, appName);
      setOptions(history);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForApp(appName);
    }
  }, [appName, clusterId]);

  React.useEffect(() => {
    if (open && appName && fetchedForApp !== appName) {
      doFetch();
    }
  }, [open, appName]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()) || o.id.includes(query))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value ? options.find(o => o.id === value)?.label || `#${value}` : placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by ID or revision..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Loading history...</p>}
          {!loading && !appName && <p className="text-xs text-muted-foreground p-2">Select an application first</p>}
          {!loading && appName && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No deployment history</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Use previous version (default)
            </button>
          )}
          {filtered.map(entry => (
            <button
              key={entry.id}
              type="button"
              onClick={() => { onChange(entry.id); setOpen(false); setQuery(''); }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === entry.id ? 'bg-accent font-medium' : ''} ${entry.is_default ? 'text-foreground' : ''}`}
            >
              <span className="truncate">{entry.label}{entry.is_default ? ' (previous)' : ''}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for GitOps clusters (Argo Rollouts / Flux CD).
// Generic over the fetch function so it can serve both integrations.
function GitopsClusterSelect({
  value,
  onChange,
  placeholder,
  fetchClusters,
  emptyLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  fetchClusters: () => Promise<{ id: string; name: string }[]>;
  emptyLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const clusters = await fetchClusters();
      setOptions(clusters);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [fetchClusters]);

  React.useEffect(() => {
    if (open && !fetched) {
      doFetch();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { if (value && !fetched) doFetch(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedName = options.find(o => o.id === value)?.name || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedName ? '' : 'text-muted-foreground'}`}>
            {selectedName || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {(loading || !fetched) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetched && options.length === 0 && <p className="text-xs text-muted-foreground p-2">{emptyLabel}</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === c.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Argo Rollouts rollout names.
function RolloutsNameSelect({
  value,
  onChange,
  placeholder,
  clusterId,
  namespace,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
  namespace?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ name: string; namespace: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForKey, setFetchedForKey] = useState('');

  const fetchKey = `${clusterId || '_default'}/${namespace || ''}`;

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      // Namespace may contain template variables — only pass literal values.
      const ns = namespace && !namespace.includes('{{') ? namespace : undefined;
      const rollouts = await apiClient.getRolloutsNames(clusterId, ns);
      setOptions(rollouts);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForKey(fetchKey);
    }
  }, [clusterId, namespace, fetchKey]);

  React.useEffect(() => {
    if (open && fetchedForKey !== fetchKey) {
      doFetch();
    }
  }, [open, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search rollouts..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForKey) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForKey && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No rollouts found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(o => (
            <button
              key={`${o.namespace}/${o.name}`}
              type="button"
              onClick={() => {
                onChange(o.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.name}</span>
              {o.namespace && <span className="text-muted-foreground shrink-0">({o.namespace})</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Argo Rollouts revision history (rollback target).
function RolloutsRevisionSelect({
  value,
  onChange,
  placeholder,
  clusterId,
  namespace,
  rolloutName,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
  namespace?: string;
  rolloutName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Array<{ revision: number; label: string; is_default: boolean }>>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForRollout, setFetchedForRollout] = useState('');

  const doFetch = React.useCallback(async () => {
    if (!rolloutName || rolloutName.includes('{{')) { setOptions([]); return; }
    setLoading(true);
    try {
      const ns = namespace && !namespace.includes('{{') ? namespace : undefined;
      const revisions = await apiClient.getRolloutsRevisions(clusterId, ns, rolloutName);
      setOptions(revisions);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForRollout(rolloutName);
    }
  }, [clusterId, namespace, rolloutName]);

  React.useEffect(() => {
    if (open && rolloutName && fetchedForRollout !== rolloutName) {
      doFetch();
    }
  }, [open, rolloutName]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedLabel = value
    ? options.find(o => String(o.revision) === value)?.label || `Revision ${value}`
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedLabel ? '' : 'text-muted-foreground'}`}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Loading revisions...</p>}
          {!loading && !rolloutName && <p className="text-xs text-muted-foreground p-2">Select a rollout first</p>}
          {!loading && rolloutName && fetchedForRollout === rolloutName && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No revision history</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Use previous revision (default)
            </button>
          )}
          {options.map(entry => (
            <button
              key={entry.revision}
              type="button"
              onClick={() => { onChange(String(entry.revision)); setOpen(false); }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === String(entry.revision) ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{entry.label}{entry.is_default ? ' (previous)' : ''}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Karpenter NodePools (live from the operator).
function KarpenterNodePoolSelect({
  value,
  onChange,
  placeholder,
  clusterId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForKey, setFetchedForKey] = useState('');

  const fetchKey = clusterId || '_default';

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const pools = await apiClient.getKarpenterNodePools(clusterId);
      setOptions(pools);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForKey(fetchKey);
    }
  }, [clusterId, fetchKey]);

  React.useEffect(() => {
    if (open && fetchedForKey !== fetchKey) {
      doFetch();
    }
  }, [open, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search NodePools..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForKey) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForKey && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No NodePools found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(o => (
            <button
              key={o.name}
              type="button"
              onClick={() => {
                onChange(o.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Kyverno policies (ClusterPolicies and namespaced
// Policies, live from the operator).
function KyvernoPolicySelect({
  value,
  onChange,
  placeholder,
  clusterId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ name: string; kind: string; namespace?: string; enforcement_action?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForKey, setFetchedForKey] = useState('');

  const fetchKey = clusterId || '_default';

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const policies = await apiClient.getKyvernoPolicies(clusterId);
      setOptions(policies);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForKey(fetchKey);
    }
  }, [clusterId, fetchKey]);

  React.useEffect(() => {
    if (open && fetchedForKey !== fetchKey) {
      doFetch();
    }
  }, [open, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search policies..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForKey) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForKey && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No Kyverno policies found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(o => (
            <button
              key={`${o.namespace || ''}/${o.name}`}
              type="button"
              onClick={() => {
                onChange(o.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.name}</span>
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                {o.namespace ? `${o.kind} · ${o.namespace}` : o.kind}{o.enforcement_action ? ` · ${o.enforcement_action}` : ''}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Trivy cluster compliance reports (live from the
// operator, scoped to the selected cluster).
function TrivyComplianceReportSelect({
  value,
  onChange,
  placeholder,
  clusterId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ name: string; title: string; pass_count: number; fail_count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForKey, setFetchedForKey] = useState('');

  const fetchKey = clusterId || '_default';

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const reports = await apiClient.getTrivyComplianceReports(clusterId);
      setOptions(reports);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForKey(fetchKey);
    }
  }, [clusterId, fetchKey]);

  React.useEffect(() => {
    if (open && fetchedForKey !== fetchKey) {
      doFetch();
    }
  }, [open, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()) || o.title.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search reports..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForKey) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForKey && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No Trivy compliance reports found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(o => (
            <button
              key={o.name}
              type="button"
              onClick={() => {
                onChange(o.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.title || o.name}</span>
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                {o.pass_count} pass · {o.fail_count} fail
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Flux CD resources (Kustomizations, HelmReleases, sources).
function FluxResourceSelect({
  value,
  onChange,
  placeholder,
  clusterId,
  kind,
  namespace,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clusterId?: string;
  kind?: string;
  namespace?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ kind: string; name: string; namespace: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedForKey, setFetchedForKey] = useState('');

  const fetchKey = `${clusterId || '_default'}/${kind || 'Kustomization'}/${namespace || ''}`;

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      // Namespace may contain template variables — only pass literal values.
      const ns = namespace && !namespace.includes('{{') ? namespace : undefined;
      const resources = await apiClient.getFluxResources(clusterId, kind || 'Kustomization', ns);
      setOptions(resources);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetchedForKey(fetchKey);
    }
  }, [clusterId, kind, namespace, fetchKey]);

  React.useEffect(() => {
    if (open && fetchedForKey !== fetchKey) {
      doFetch();
    }
  }, [open, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = query
    ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${kind || 'Kustomization'}s...`}
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {(loading || !fetchedForKey) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetchedForKey && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No {kind || 'Kustomization'} resources found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(o => (
            <button
              key={`${o.kind}/${o.namespace}/${o.name}`}
              type="button"
              onClick={() => {
                onChange(o.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.name ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{o.name}</span>
              {o.namespace && <span className="text-muted-foreground shrink-0">({o.namespace})</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for Datadog monitors.
// Stores the DD monitor ID as the value. Fetches all monitors on open, filters client-side.
// Searchable single-select for Datadog clusters (only those with has_datadog=true).
function FlowClusterSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiClient.listClusters();
      const withFlows = (resp.clusters || [])
        .filter((c: any) => c.has_active_flows)
        .map((c: any) => ({ id: c.id, name: c.cluster_name }));
      setOptions(withFlows);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  React.useEffect(() => { if (open && !fetched) doFetch(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (value && !fetched) doFetch(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedName = options.find(o => o.id === value)?.name || value || '';
  const noFlowClusters = fetched && options.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedName ? '' : 'text-muted-foreground'}`}>
            {selectedName || (noFlowClusters ? 'No clusters with flows' : 'Select cluster...')}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : noFlowClusters ? (
            <p className="text-xs text-muted-foreground text-center py-3">No clusters with Cilium or Istio flows enabled</p>
          ) : (
            options.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === o.id ? 'bg-accent' : ''}`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${value === o.id ? 'bg-primary border-primary' : 'border-input'}`}>
                  {value === o.id && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                {o.name}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DDClusterSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const clusters = await apiClient.getDDClusters();
      setOptions(clusters);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, []);

  React.useEffect(() => {
    if (open && !fetched) {
      doFetch();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { if (value && !fetched) doFetch(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedName = options.find(o => o.id === value)?.name || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedName ? '' : 'text-muted-foreground'}`}>
            {selectedName || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {(loading || !fetched) && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && fetched && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No Datadog clusters found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(c.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === c.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DDMonitorSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');

  React.useEffect(() => {
    if (value && !selectedLabel) {
      setSelectedLabel(value);
    }
  }, [value, selectedLabel]);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const monitors = await apiClient.getDDMonitors();
      setOptions(monitors);
      if (value && !selectedLabel) {
        const match = monitors.find(m => m.id === value);
        if (match) setSelectedLabel(match.name);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [value, selectedLabel]);

  React.useEffect(() => {
    if (open && options.length === 0) {
      doFetch();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = selectedLabel || value || '';

  const filtered = query
    ? options.filter(m => m.name.toLowerCase().includes(query.toLowerCase()) || m.id.includes(query))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search monitors..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-48 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {!loading && options.length === 0 && <p className="text-xs text-muted-foreground p-2">No monitors found</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {filtered.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onChange(m.id);
                setSelectedLabel(m.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === m.id ? 'bg-accent font-medium' : ''}`}
            >
              <div className="flex flex-col min-w-0">
                <span className="truncate">{m.name}</span>
                <span className="truncate text-muted-foreground text-[10px]">ID: {m.id}</span>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for PagerDuty services.
// Stores the PD service ID as the value. Includes a built-in "auto" option.
function PDServiceSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');

  React.useEffect(() => {
    if (value === 'auto' && !selectedLabel) {
      setSelectedLabel('Auto (route by namespace mapping)');
    } else if (value && !selectedLabel) {
      setSelectedLabel(value);
    }
  }, [value, selectedLabel]);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const services = await apiClient.getPDServices();
      setOptions(services);
      if (value && value !== 'auto' && !selectedLabel) {
        const match = services.find(s => s.id === value);
        if (match) setSelectedLabel(match.name);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [value, selectedLabel]);

  React.useEffect(() => {
    if (open && options.length === 0) {
      doFetch();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = selectedLabel || value || '';

  const filtered = query
    ? options.filter(s => s.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search services..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onChange('auto');
              setSelectedLabel('Auto (route by namespace mapping)');
              setOpen(false);
              setQuery('');
            }}
            className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === 'auto' ? 'bg-accent font-medium' : ''}`}
          >
            <span className="truncate">Auto <span className="text-muted-foreground">(route by namespace mapping)</span></span>
          </button>
          {filtered.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                onChange(s.id);
                setSelectedLabel(s.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === s.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{s.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Searchable single-select for PagerDuty escalation policies.
// Stores the PD escalation policy ID as the value.
function PDEscalationPolicySelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  React.useEffect(() => {
    if (value && !selectedLabel) {
      setSelectedLabel(value);
    }
  }, [value, selectedLabel]);

  const doSearch = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      const policies = await apiClient.getPDEscalationPolicies(q);
      setOptions(policies);
      if (value && !selectedLabel) {
        const match = policies.find(p => p.id === value);
        if (match) setSelectedLabel(match.name);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [value, selectedLabel]);

  React.useEffect(() => {
    if (open && options.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const displayValue = selectedLabel || value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayValue ? '' : 'text-muted-foreground'}`}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Input
          value={query}
          onChange={e => handleInput(e.target.value)}
          placeholder="Search policies..."
          className="h-7 text-xs mb-1"
          autoFocus
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && <p className="text-xs text-muted-foreground p-2">Searching...</p>}
          {!loading && options.length === 0 && query && <p className="text-xs text-muted-foreground p-2">No results</p>}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setSelectedLabel(''); setOpen(false); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear selection
            </button>
          )}
          {options.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onChange(p.id);
                setSelectedLabel(p.name);
                setOpen(false);
                setQuery('');
              }}
              className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${value === p.id ? 'bg-accent font-medium' : ''}`}
            >
              <span className="truncate">{p.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Multi-user search select for Slack @mention fields.
// Stores a comma-separated string of user IDs; displays user display names as badges.
// Multi-select for AWS EC2 resource IDs (instances, volumes, EIPs, snapshots)
// on the cost remediation blocks. Options come from the live AWS account via
// /api/workflows/aws-resource-ids; the stored value is a comma-separated ID
// string (what the Go handlers parse). Follows the SlackMultiUserSelect
// chip + popover styling, and additionally supports typing an arbitrary ID
// for resources the listing didn't surface.
function AWSResourceMultiSelect({
  options,
  loading,
  value,
  onChange,
  placeholder,
  resourceLabel,
}: {
  options: { key: string; name: string }[];
  loading: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  resourceLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedIds = React.useMemo(
    () => value.split(',').map(s => s.trim()).filter(Boolean),
    [value]
  );
  const selectedSet = new Set(selectedIds);
  const labelFor = (id: string) => options.find(o => o.key === id)?.name || id;

  const commit = (ids: string[]) => onChange(ids.join(', '));

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      commit(selectedIds.filter(x => x !== id));
    } else {
      commit([...selectedIds, id]);
    }
  };

  const filtered = query
    ? options.filter(o =>
        o.key.toLowerCase().includes(query.toLowerCase()) ||
        o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Allow adding a manually typed ID that isn't among the loaded options.
  const trimmedQuery = query.trim();
  const canAddManual = trimmedQuery !== '' &&
    !options.some(o => o.key === trimmedQuery) &&
    !selectedSet.has(trimmedQuery);

  const addManual = () => {
    if (!canAddManual) return;
    commit([...selectedIds, trimmedQuery]);
    setQuery('');
  };

  return (
    <div className="mt-1">
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedIds.map(id => (
            <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20 max-w-full">
              <span className="truncate">{labelFor(id)}</span>
              <button type="button" onClick={() => commit(selectedIds.filter(x => x !== id))} className="hover:text-destructive shrink-0">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          >
            <span className="text-muted-foreground truncate">{placeholder}</span>
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addManual();
              }
            }}
            placeholder={`Search or type an ID...`}
            className="h-7 text-xs mb-1"
            autoFocus
          />
          <div className="max-h-48 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            )}
            {!loading && filtered.length === 0 && !canAddManual && (
              <p className="text-xs text-muted-foreground p-2">
                {query ? 'No matches' : `No ${resourceLabel} found in this account/region`}
              </p>
            )}
            {!loading && canAddManual && (
              <button
                type="button"
                onClick={addManual}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left"
              >
                <span className="w-3 h-3 shrink-0" />
                Add "{trimmedQuery}"
              </button>
            )}
            {!loading && filtered.map(o => (
              <button
                key={o.key}
                type="button"
                onClick={() => toggle(o.key)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left ${selectedSet.has(o.key) ? 'bg-accent font-medium' : ''}`}
              >
                {selectedSet.has(o.key) && <Check className="w-3 h-3 text-primary shrink-0" />}
                {!selectedSet.has(o.key) && <span className="w-3 h-3 shrink-0" />}
                <span className="truncate">{o.name}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SlackMultiUserSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<{ id: string; label: string }[]>([]);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // Parse initial value (comma-separated IDs) into selected users
  React.useEffect(() => {
    if (value && selectedUsers.length === 0) {
      const ids = value.split(',').map(s => s.trim()).filter(Boolean);
      if (ids.length > 0) {
        setSelectedUsers(ids.map(id => ({ id, label: id })));
        // Try to resolve display names
        apiClient.searchSlackUsers('').then(users => {
          const userMap = new Map(users.map(u => [u.id, u.real_name || u.display_name || u.name]));
          setSelectedUsers(ids.map(id => ({ id, label: userMap.get(id) || id })));
        }).catch(() => {});
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      const users = await apiClient.searchSlackUsers(q);
      setOptions(users.map(u => ({
        id: u.id,
        label: u.real_name || u.display_name || u.name,
      })));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open && options.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const selectedIds = new Set(selectedUsers.map(u => u.id));

  const toggleUser = (user: { id: string; label: string }) => {
    let next: { id: string; label: string }[];
    if (selectedIds.has(user.id)) {
      next = selectedUsers.filter(u => u.id !== user.id);
    } else {
      next = [...selectedUsers, user];
    }
    setSelectedUsers(next);
    onChange(next.map(u => u.id).join(', '));
  };

  const removeUser = (id: string) => {
    const next = selectedUsers.filter(u => u.id !== id);
    setSelectedUsers(next);
    onChange(next.map(u => u.id).join(', '));
  };

  return (
    <div className="mt-1">
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedUsers.map(u => (
            <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
              {u.label}
              <button type="button" onClick={() => removeUser(u.id)} className="hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          >
            <span className="text-muted-foreground truncate">{placeholder}</span>
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
          <Input
            value={query}
            onChange={e => handleInput(e.target.value)}
            placeholder="Search users..."
            className="h-7 text-xs mb-1"
            autoFocus
          />
          <div className="max-h-40 overflow-y-auto">
            {loading && <p className="text-xs text-muted-foreground p-2">Searching...</p>}
            {!loading && options.length === 0 && query && <p className="text-xs text-muted-foreground p-2">No results</p>}
            {options.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => toggleUser(o)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${selectedIds.has(o.id) ? 'bg-accent font-medium' : ''}`}
              >
                {selectedIds.has(o.id) && <Check className="w-3 h-3 text-primary shrink-0" />}
                {!selectedIds.has(o.id) && <span className="w-3 h-3 shrink-0" />}
                {o.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Org user multi-select for approval gate restrictions.
function OrgUserMultiSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [allUsers, setAllUsers] = useState<{ id: string; email: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<{ id: string; name: string }[]>([]);

  React.useEffect(() => {
    if (allUsers.length === 0) {
      setLoading(true);
      apiClient.listTenantUsers().then((data: any) => {
        const users = (data.users || data || []).map((u: any) => ({
          id: u.id,
          email: u.email || '',
          name: u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email || u.id,
        }));
        setAllUsers(users);
        // Resolve initial selections
        if (value) {
          const ids = value.split(',').map((s: string) => s.trim()).filter(Boolean);
          setSelectedUsers(ids.map(id => {
            const found = users.find((u: any) => u.id === id);
            return { id, name: found?.name || id };
          }));
        }
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lowerQuery = query.toLowerCase();
  const filtered = allUsers.filter(u =>
    !query || u.name.toLowerCase().includes(lowerQuery) || u.email.toLowerCase().includes(lowerQuery)
  );

  const selectedIds = new Set(selectedUsers.map(u => u.id));

  const toggleUser = (user: { id: string; name: string }) => {
    let next: { id: string; name: string }[];
    if (selectedIds.has(user.id)) {
      next = selectedUsers.filter(u => u.id !== user.id);
    } else {
      next = [...selectedUsers, user];
    }
    setSelectedUsers(next);
    onChange(next.map(u => u.id).join(', '));
  };

  const removeUser = (id: string) => {
    const next = selectedUsers.filter(u => u.id !== id);
    setSelectedUsers(next);
    onChange(next.map(u => u.id).join(', '));
  };

  return (
    <div className="mt-1">
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedUsers.map(u => (
            <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
              {u.name}
              <button type="button" onClick={() => removeUser(u.id)} className="hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          >
            <span className="text-muted-foreground truncate">
              {selectedUsers.length === 0 ? placeholder : `${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''} selected`}
            </span>
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="h-7 text-xs mb-1"
            autoFocus
          />
          <div className="max-h-48 overflow-y-auto">
            {loading && <p className="text-xs text-muted-foreground p-2">Loading...</p>}
            {!loading && filtered.length === 0 && <p className="text-xs text-muted-foreground p-2">No users found</p>}
            {filtered.map(u => (
              <button
                key={u.id}
                type="button"
                onClick={() => toggleUser(u)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${selectedIds.has(u.id) ? 'bg-accent font-medium' : ''}`}
              >
                {selectedIds.has(u.id) ? <Check className="w-3 h-3 text-primary shrink-0" /> : <span className="w-3 h-3 shrink-0" />}
                <span className="truncate">{u.name}</span>
                {u.email && u.name !== u.email && <span className="text-muted-foreground truncate ml-auto">{u.email}</span>}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function GitHubWorkflowFileSelect({
  repo,
  value,
  onChange,
}: {
  repo: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const [files, setFiles] = useState<{ name: string; path: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchedRepo = useRef('');

  useEffect(() => {
    if (!repo || repo === 'auto') {
      setFiles([]);
      fetchedRepo.current = '';
      return;
    }
    if (fetchedRepo.current === repo) return;
    fetchedRepo.current = repo;
    setLoading(true);
    apiClient.getGitHubWorkflowFiles(repo).then(result => {
      setFiles(result);
      if (value && result.length > 0 && !result.some(f => f.name === value)) {
        const valueLower = value.replace(/[-_ ]/g, '').toLowerCase();
        const match = result.find(f => f.name.replace(/[-_ ]/g, '').toLowerCase().includes(valueLower)
          || valueLower.includes(f.name.replace(/[-_ .](yml|yaml)$/g, '').replace(/[-_ ]/g, '').toLowerCase()));
        if (match) {
          setTimeout(() => onChange(match.name), 0);
        }
      }
    }).catch(() => setFiles([])).finally(() => setLoading(false));
  }, [repo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!repo || repo === 'auto') {
    return <p className="text-xs text-muted-foreground mt-1 italic">Select a repository first.</p>;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading workflows...
      </div>
    );
  }

  if (files.length > 0) {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select workflow..." /></SelectTrigger>
        <SelectContent>
          {files.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="e.g. build.yml"
      className="mt-1 h-8 text-sm"
    />
  );
}

function GitHubWorkflowInputsEditor({
  repo,
  workflowFile,
  value,
  onChange,
}: {
  repo: string;
  workflowFile: string;
  value: Record<string, string>;
  onChange: (val: Record<string, string>) => void;
}) {
  const [inputs, setInputs] = useState<GitHubWorkflowInput[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const fetchedRef = useRef('');

  const fetchInputs = useCallback(async () => {
    if (!repo || !workflowFile || repo === 'auto') return;
    const key = `${repo}:${workflowFile}`;
    if (fetchedRef.current === key) return;
    fetchedRef.current = key;

    setLoading(true);
    setError('');
    try {
      const result = await apiClient.getGitHubWorkflowInputs(repo, workflowFile);
      setInputs(result);
      if (result.length === 0) setManualMode(true);
    } catch {
      setError('Could not fetch workflow inputs');
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  }, [repo, workflowFile]);

  useEffect(() => {
    fetchedRef.current = '';
    setInputs([]);
    setManualMode(false);
  }, [repo, workflowFile]);

  useEffect(() => {
    fetchInputs();
  }, [fetchInputs]);

  const toggleInput = (name: string, defaultVal: string) => {
    const next = { ...value };
    if (name in next) {
      delete next[name];
    } else {
      next[name] = defaultVal || '';
    }
    onChange(next);
  };

  const setInputValue = (name: string, val: string) => {
    onChange({ ...value, [name]: val });
  };

  if (!repo || !workflowFile || repo === 'auto') {
    return (
      <p className="text-xs text-muted-foreground mt-1 italic">
        Select a repository and workflow file to see available inputs.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading workflow inputs...
      </div>
    );
  }

  if (manualMode || inputs.length === 0) {
    const pairs: Record<string, string> = (typeof value === 'object' && value !== null && !Array.isArray(value)) ? value : {};
    const entries = Object.entries(pairs);
    const updatePair = (oldKey: string, newKey: string, val: string) => {
      const updated: Record<string, string> = {};
      for (const [k, v] of Object.entries(pairs)) {
        if (k === oldKey) updated[newKey] = val;
        else updated[k] = v;
      }
      onChange(updated);
    };
    const removePair = (key: string) => {
      const updated = { ...pairs };
      delete updated[key];
      onChange(updated);
    };
    const addPair = () => {
      let newKey = 'key';
      let i = 1;
      while (pairs[newKey] !== undefined) { newKey = `key_${i++}`; }
      onChange({ ...pairs, [newKey]: '' });
    };

    return (
      <div className="mt-1 space-y-2">
        {error && <p className="text-xs text-muted-foreground italic">{error}</p>}
        {entries.map(([k, v], idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <Input value={k} onChange={e => updatePair(k, e.target.value, v)} placeholder="key" className="h-7 text-xs flex-1" />
            <span className="text-muted-foreground text-xs">=</span>
            <Input value={v} onChange={e => updatePair(k, k, e.target.value)} placeholder="value" className="h-7 text-xs flex-1" />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removePair(k)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addPair}>
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-2">
      <p className="text-xs text-muted-foreground">
        Select which inputs to pass. Values support template variables.
      </p>
      {inputs.map(inp => {
        const isEnabled = inp.name in value;
        return (
          <div key={inp.name} className="border rounded-md p-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => toggleInput(inp.name, inp.default || '')}
                className="rounded border-muted-foreground"
              />
              <span className="text-xs font-medium flex-1 truncate">
                {inp.name}
                {inp.required && <span className="text-destructive ml-1">*</span>}
              </span>
              {inp.type && <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{inp.type}</span>}
            </div>
            {inp.description && (
              <p className="text-[11px] text-muted-foreground leading-tight flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
                {inp.description}
              </p>
            )}
            {isEnabled && (
              inp.type === 'choice' && inp.options && inp.options.length > 0 ? (
                <Select value={value[inp.name] || ''} onValueChange={v => setInputValue(inp.name, v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {inp.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : inp.type === 'boolean' ? (
                <Select value={value[inp.name] || inp.default || 'false'} onValueChange={v => setInputValue(inp.name, v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">true</SelectItem>
                    <SelectItem value="false">false</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={value[inp.name] || ''}
                  onChange={e => setInputValue(inp.name, e.target.value)}
                  placeholder={inp.default ? `Default: ${inp.default}` : `Enter ${inp.name}...`}
                  className="h-7 text-xs"
                />
              )
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground underline"
        onClick={() => setManualMode(true)}
      >
        Switch to manual key-value mode
      </button>
    </div>
  );
}

const AWS_INCIDENT_TYPES: Record<string, string[]> = {
  // Identity & Access
  iam: ['IAMSecurityEvent', 'RootAccountActivity', 'MFABypassAttempt', 'CredentialExposure', 'PrivilegeEscalation', 'UnauthorizedAccessAttempt', 'PasswordPolicyChange', 'AccessKeyModification', 'FederationConfigChange', 'CrossAccountAccessChange'],
  sts: ['IAMSecurityEvent', 'FederationConfigChange', 'CrossAccountAccessChange'],
  sso: ['SSOSecurityEvent', 'SSOPermissionSetDeletion', 'SSOAccessRevoked', 'SSOAccessGranted', 'SSOPolicyDetached', 'SSOUserDeletion', 'SSOGroupMembershipChange'],
  organizations: ['OrganizationsSecurityEvent', 'AccountLeftOrganization', 'AccountRemovedFromOrg', 'OrganizationDeletion', 'AccountClosure', 'OrgPolicyTypeDisabled', 'OrgPolicyDetached', 'OrgServiceAccessDisabled', 'DelegatedAdminRemoved'],

  // Storage
  s3: ['S3SecurityEvent', 'S3BucketDeletion', 'S3PublicAccessChange', 'S3EncryptionChange', 'S3PolicyModification', 'S3LoggingChange', 'S3VersioningChange'],

  // Compute
  ec2: ['EC2SecurityEvent', 'EC2InstanceTermination', 'EC2SecurityGroupChange', 'EC2SnapshotExposure', 'EC2AMIModification', 'KeyPairModification'],
  lambda: ['LambdaSecurityEvent', 'LambdaConfigChange', 'LambdaPermissionChange', 'LambdaFunctionDeletion', 'LambdaCodeModification', 'LambdaCodeSigningDisabled', 'LambdaTriggerChange'],
  ecs: ['ContainerSecurityEvent', 'ECSClusterDeletion', 'ECSServiceDeletion', 'ContainerExecDetected', 'ContainerTaskStarted'],
  eks: ['ContainerSecurityEvent', 'EKSClusterDeletion', 'EKSClusterConfigChange', 'EKSNodegroupDeletion', 'EKSIdentityProviderRemoved'],

  // Database
  rds: ['RDSSecurityEvent', 'RDSDatabaseDeletion', 'RDSSnapshotExposure', 'RDSSnapshotDeletion', 'RDSConfigurationChange', 'RDSAuditDisabled', 'RDSSecurityGroupChange'],
  dynamodb: ['DynamoDBSecurityEvent', 'DynamoDBTableDeletion', 'DynamoDBBackupDeletion', 'DynamoDBPolicyChange', 'DynamoDBBackupConfigChange', 'DynamoDBDataExport'],
  redshift: ['RedshiftSecurityEvent', 'RedshiftClusterDeletion', 'RedshiftSnapshotExposure', 'RedshiftAuditDisabled', 'RedshiftSnapshotDeletion', 'RedshiftSecurityGroupChange'],

  // Security
  kms: ['KMSSecurityEvent', 'KMSKeyScheduledDeletion', 'KMSKeyDisabled', 'KMSKeyPolicyChange', 'KMSGrantModification', 'KMSRotationDisabled', 'KMSUnauthorizedAccess'],
  secretsmanager: ['SecretsManagerEvent', 'SecretDeletion', 'SecretPolicyChange', 'SecretRotationChange', 'SecretAccessEvent'],
  waf: ['WAFSecurityEvent', 'WAFWebACLDeletion', 'WAFDisassociated', 'WAFRulesetDeletion', 'WAFRuleChange', 'WAFLoggingDisabled'],
  shield: ['ShieldProtectionRemoved'],

  // Networking
  vpc: ['VPCDeletion', 'VPCFlowLogsDisabled', 'SecurityGroupModification', 'NetworkACLChange', 'VPCRouteTableChange', 'VPCRouteCreation', 'VPCRouteDeletion', 'VPCRouteModification', 'VPCGatewayChange', 'VPCPeeringChange', 'VPCSubnetChange', 'NetworkTrafficBlocked', 'NetworkSecurityEvent'],
  elb: ['LoadBalancerSecurityEvent', 'LoadBalancerDeletion', 'LoadBalancerSecurityGroupChange', 'LoadBalancerListenerChange', 'LoadBalancerCertificateRemoved'],
  cloudfront: ['CloudFrontSecurityEvent', 'CloudFrontDistributionDeletion', 'CloudFrontDistributionChange', 'CloudFrontEncryptionRemoved', 'CloudFrontOriginAccessRemoved'],
  route53: ['DNSSecurityEvent', 'DNSHostedZoneDeletion', 'DNSRecordChange', 'DNSLoggingDisabled', 'DNSSECDisabled', 'DomainTransfer', 'DomainTransferLockDisabled', 'DomainNameserverChange'],
  apigateway: ['APIGatewaySecurityEvent', 'APIGatewayDeletion', 'APIGatewayAuthorizerRemoved', 'APIGatewayAuthorizerChange', 'APIGatewayImport', 'APIGatewayKeyDeletion'],

  // Monitoring & Logging
  cloudwatch: ['CloudWatchAlarmsDisabled', 'CloudWatchLogGroupDeletion'],
  config: ['AWSConfigDisabled', 'ConfigDeliveryDisabled', 'ComplianceRuleDeleted'],
  logging: ['LoggingTampering', 'CloudWatchLogGroupDeletion', 'VPCFlowLogsDisabled', 'CloudWatchAlarmsDisabled'],

  // Messaging & Streaming
  sqs: ['SQSQueueDeletion', 'SQSQueuePurged', 'SQSPolicyChange'],
  sns: ['SNSTopicDeletion', 'SNSPolicyChange'],
  kinesis: ['KinesisStreamDeletion', 'StreamEncryptionDisabled'],
  msk: ['MSKOperationalEvent', 'MSKClusterDeletion', 'MSKPartitionHotspot', 'MSKClusterCapacityInsufficient', 'MSKPartitionImbalance', 'MSKBrokerResourceConstraint', 'MSKConsumerLag', 'MSKBrokerReboot', 'MSKConfigurationChange', 'MSKSecurityChange'],
  eventbridge: ['EventBusDeletion', 'EventRuleDisabled', 'EventTargetRemoved', 'EventStreamSecurityEvent'],

  // Container Registry
  ecr: ['ECRSecurityEvent', 'ECRRepositoryDeletion', 'ECRImageDeletion', 'ECRPolicyChange', 'ECRScanningConfigChange'],

  // DevOps
  codepipeline: ['DevOpsSecurityEvent', 'PipelineDeletion', 'PipelineModification', 'PipelineStageDisabled', 'ApprovalRuleRemoved'],
  codebuild: ['DevOpsSecurityEvent', 'CodeBuildProjectDeletion', 'BuildCredentialsChange'],
  codecommit: ['DevOpsSecurityEvent', 'CodeRepositoryDeletion'],

  // Systems Manager
  ssm: ['SSMSecurityEvent', 'SSMRemoteCommandExecution', 'SSMDocumentShared', 'SSMDocumentDeletion', 'SSMParameterDeletion', 'SSMInstanceDeregistered', 'SSMSessionStarted', 'SSMDocumentChange', 'SSMParameterChange'],

  // Application
  cloudwatch_logs: ['ApplicationLogErrors'],

  // Cost & Billing
  ce: ['CostAnomaly', 'CostSpike', 'UnexpectedServiceSpend'],
  budgets: ['BudgetExceeded', 'BudgetForecastExceeded'],

  // Errors
  _errors: ['CloudServiceError', 'AccessDeniedError', 'ValidationError', 'ServiceLimitError', 'ServiceTimeoutError', 'AWSInternalError'],

  // Catch-all: union of all per-service types (auto-generated — do not curate manually)
  _all: (() => {
    const all = new Set<string>();
    const lists: Record<string, string[]> = {
      iam: ['IAMSecurityEvent', 'RootAccountActivity', 'MFABypassAttempt', 'CredentialExposure', 'PrivilegeEscalation', 'UnauthorizedAccessAttempt', 'PasswordPolicyChange', 'AccessKeyModification', 'FederationConfigChange', 'CrossAccountAccessChange'],
      sts: ['IAMSecurityEvent', 'FederationConfigChange', 'CrossAccountAccessChange'],
      sso: ['SSOSecurityEvent', 'SSOPermissionSetDeletion', 'SSOAccessRevoked', 'SSOAccessGranted', 'SSOPolicyDetached', 'SSOUserDeletion', 'SSOGroupMembershipChange'],
      organizations: ['OrganizationsSecurityEvent', 'AccountLeftOrganization', 'AccountRemovedFromOrg', 'OrganizationDeletion', 'AccountClosure', 'OrgPolicyTypeDisabled', 'OrgPolicyDetached', 'OrgServiceAccessDisabled', 'DelegatedAdminRemoved'],
      s3: ['S3SecurityEvent', 'S3BucketDeletion', 'S3PublicAccessChange', 'S3EncryptionChange', 'S3PolicyModification', 'S3LoggingChange', 'S3VersioningChange'],
      ec2: ['EC2SecurityEvent', 'EC2InstanceTermination', 'SecurityGroupModification', 'EC2SnapshotExposure', 'EC2AMIModification', 'KeyPairModification'],
      lambda: ['LambdaSecurityEvent', 'LambdaConfigChange', 'LambdaPermissionChange', 'LambdaFunctionDeletion', 'LambdaCodeModification', 'LambdaCodeSigningDisabled', 'LambdaTriggerChange'],
      ecs: ['ContainerSecurityEvent', 'ECSClusterDeletion', 'ECSServiceDeletion', 'ContainerExecDetected', 'ContainerTaskStarted'],
      eks: ['ContainerSecurityEvent', 'EKSClusterDeletion', 'EKSClusterConfigChange', 'EKSNodegroupDeletion', 'EKSIdentityProviderRemoved'],
      rds: ['RDSSecurityEvent', 'RDSDatabaseDeletion', 'RDSSnapshotExposure', 'RDSSnapshotDeletion', 'RDSConfigurationChange', 'RDSAuditDisabled', 'RDSSecurityGroupChange'],
      dynamodb: ['DynamoDBSecurityEvent', 'DynamoDBTableDeletion', 'DynamoDBBackupDeletion', 'DynamoDBPolicyChange', 'DynamoDBBackupConfigChange', 'DynamoDBDataExport'],
      redshift: ['RedshiftSecurityEvent', 'RedshiftClusterDeletion', 'RedshiftSnapshotExposure', 'RedshiftAuditDisabled', 'RedshiftSnapshotDeletion', 'RedshiftSecurityGroupChange'],
      kms: ['KMSSecurityEvent', 'KMSKeyScheduledDeletion', 'KMSKeyDisabled', 'KMSKeyPolicyChange', 'KMSGrantModification', 'KMSRotationDisabled', 'KMSUnauthorizedAccess'],
      secretsmanager: ['SecretsManagerEvent', 'SecretDeletion', 'SecretPolicyChange', 'SecretRotationChange', 'SecretAccessEvent'],
      waf: ['WAFSecurityEvent', 'WAFWebACLDeletion', 'WAFDisassociated', 'WAFRulesetDeletion', 'WAFRuleChange', 'WAFLoggingDisabled'],
      shield: ['ShieldProtectionRemoved'],
      vpc: ['VPCDeletion', 'VPCFlowLogsDisabled', 'SecurityGroupModification', 'NetworkACLChange', 'VPCRouteTableChange', 'VPCRouteCreation', 'VPCRouteDeletion', 'VPCRouteModification', 'VPCGatewayChange', 'VPCPeeringChange', 'VPCSubnetChange', 'NetworkTrafficBlocked', 'NetworkSecurityEvent'],
      elb: ['LoadBalancerSecurityEvent', 'LoadBalancerDeletion', 'LoadBalancerSecurityGroupChange', 'LoadBalancerListenerChange', 'LoadBalancerCertificateRemoved'],
      cloudfront: ['CloudFrontSecurityEvent', 'CloudFrontDistributionDeletion', 'CloudFrontDistributionChange', 'CloudFrontEncryptionRemoved', 'CloudFrontOriginAccessRemoved'],
      route53: ['DNSSecurityEvent', 'DNSHostedZoneDeletion', 'DNSRecordChange', 'DNSLoggingDisabled', 'DNSSECDisabled', 'DomainTransfer', 'DomainTransferLockDisabled', 'DomainNameserverChange'],
      apigateway: ['APIGatewaySecurityEvent', 'APIGatewayDeletion', 'APIGatewayAuthorizerRemoved', 'APIGatewayAuthorizerChange', 'APIGatewayImport', 'APIGatewayKeyDeletion'],
      cloudwatch: ['CloudWatchAlarmsDisabled', 'CloudWatchLogGroupDeletion'],
      config: ['AWSConfigDisabled', 'ConfigDeliveryDisabled', 'ComplianceRuleDeleted'],
      logging: ['LoggingTampering'],
      sqs: ['SQSQueueDeletion', 'SQSQueuePurged', 'SQSPolicyChange'],
      sns: ['SNSTopicDeletion', 'SNSPolicyChange'],
      kinesis: ['KinesisStreamDeletion', 'StreamEncryptionDisabled'],
      msk: ['MSKOperationalEvent', 'MSKClusterDeletion', 'MSKPartitionHotspot', 'MSKClusterCapacityInsufficient', 'MSKPartitionImbalance', 'MSKBrokerResourceConstraint', 'MSKConsumerLag', 'MSKBrokerReboot', 'MSKConfigurationChange', 'MSKSecurityChange'],
      eventbridge: ['EventBusDeletion', 'EventRuleDisabled', 'EventTargetRemoved', 'EventStreamSecurityEvent'],
      ecr: ['ECRSecurityEvent', 'ECRRepositoryDeletion', 'ECRImageDeletion', 'ECRPolicyChange', 'ECRScanningConfigChange'],
      codepipeline: ['DevOpsSecurityEvent', 'PipelineDeletion', 'PipelineModification', 'PipelineStageDisabled', 'ApprovalRuleRemoved'],
      codebuild: ['CodeBuildProjectDeletion', 'BuildCredentialsChange'],
      codecommit: ['CodeRepositoryDeletion'],
      ssm: ['SSMSecurityEvent', 'SSMRemoteCommandExecution', 'SSMDocumentShared', 'SSMDocumentDeletion', 'SSMParameterDeletion', 'SSMInstanceDeregistered', 'SSMSessionStarted', 'SSMDocumentChange', 'SSMParameterChange'],
      cloudwatch_logs: ['ApplicationLogErrors'],
      ce: ['CostAnomaly', 'CostSpike', 'UnexpectedServiceSpend'],
      budgets: ['BudgetExceeded', 'BudgetForecastExceeded'],
      _errors: ['CloudServiceError', 'AccessDeniedError', 'ValidationError', 'ServiceLimitError', 'ServiceTimeoutError', 'AWSInternalError'],
    };
    for (const types of Object.values(lists)) {
      for (const t of types) all.add(t);
    }
    return Array.from(all).sort();
  })(),
};

function IncidentTypeMultiSelect({
  selectedServices,
  selectedTypes,
  onChange,
}: {
  selectedServices: string[];
  selectedTypes: string[];
  onChange: (types: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  let available: string[] = [];
  if (selectedServices.length > 0) {
    const set = new Set<string>();
    for (const svc of selectedServices) {
      for (const t of (AWS_INCIDENT_TYPES[svc.toLowerCase()] || [])) {
        set.add(t);
      }
    }
    available = Array.from(set);
  } else {
    available = AWS_INCIDENT_TYPES._all;
  }

  // Include any currently-selected types that aren't in the service-filtered list
  // but ARE valid types overall (they come from a different service or the agent)
  const unknownTypes = selectedTypes.filter(t => !available.includes(t));
  const trulyUnknown = unknownTypes.filter(t => !AWS_INCIDENT_TYPES._all.includes(t));
  if (unknownTypes.length > 0) {
    available = [...available, ...unknownTypes];
  }

  const displayText = selectedTypes.length === 0
    ? 'All incident types'
    : selectedTypes.length <= 2
      ? selectedTypes.join(', ')
      : `${selectedTypes.length} selected`;

  const toggle = (t: string) => {
    if (selectedTypes.includes(t)) {
      onChange(selectedTypes.filter(x => x !== t));
    } else {
      onChange([...selectedTypes, t]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedTypes.length === 0 ? 'text-muted-foreground' : ''}`}>
            {displayText}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {selectedTypes.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear all
            </button>
          )}
          {available.map(t => {
            const checked = selectedTypes.includes(t);
            const isUnknown = trulyUnknown.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggle(t)}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer ${isUnknown ? 'text-destructive' : ''}`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                  {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                <span className="truncate">{t}{isUnknown ? ' (unrecognized)' : ''}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-west-3', label: 'Europe (Paris)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'eu-central-2', label: 'Europe (Zurich)' },
  { value: 'eu-north-1', label: 'Europe (Stockholm)' },
  { value: 'eu-south-1', label: 'Europe (Milan)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'me-central-1', label: 'Middle East (UAE)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
  { value: 'il-central-1', label: 'Israel (Tel Aviv)' },
];

function LoopActionSelect({
  value,
  options,
  onSelect,
}: {
  value: string;
  options: ActionTemplate[];
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? options.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.integration.toLowerCase().includes(search.toLowerCase()) ||
        a.id.toLowerCase().includes(search.toLowerCase()))
    : options;

  const selected = options.find(a => a.id === value);
  const displayText = selected ? `${selected.integration}: ${selected.name}` : '';

  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayText ? '' : 'text-muted-foreground'}`}>
            {displayText || 'Select an action'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="px-1 pb-1">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search actions..."
            className="w-full h-7 px-2 text-xs rounded border border-input bg-background outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {filtered.map(a => {
            const checked = a.id === value;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => { onSelect(a.id); setOpen(false); setSearch(''); }}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left"
              >
                <div className="w-4 shrink-0">
                  {checked && <Check className="w-3.5 h-3.5 text-primary" />}
                </div>
                <span className="truncate">
                  <span className="text-muted-foreground">{a.integration}:</span> {a.name}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No actions found</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Multi-select for the Poll Until exit-condition Value. Options come from the
// selected output field's catalog metadata (discrete `values`) or a dynamic
// source (live resource IDs). Custom values can still be typed and added, so
// the dropdown never blocks values the catalog doesn't know about. All value
// operators support multiple selections: equals/contains exit when ANY value
// matches; not_equals/not_contains exit only when NONE match.
function LoopConditionValueSelect({
  values,
  options,
  onChange,
  placeholder,
}: {
  values: string[];
  options: { key: string; name: string }[];
  onChange: (values: string[]) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? options.filter(o =>
        o.name.toLowerCase().includes(search.toLowerCase()) ||
        o.key.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Keep saved values that aren't in the option list (custom/typed values)
  // visible and removable.
  const customSelected = values.filter(v => !options.some(o => o.key === v));

  const toggle = (key: string) => {
    onChange(values.includes(key) ? values.filter(v => v !== key) : [...values, key]);
  };

  const trimmedSearch = search.trim();
  const canAddCustom = trimmedSearch.length > 0
    && !options.some(o => o.key === trimmedSearch)
    && !values.includes(trimmedSearch);

  const displayText = values.length > 0
    ? values.map(v => options.find(o => o.key === v)?.name || v).join(', ')
    : '';

  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${displayText ? '' : 'text-muted-foreground'}`}>
            {displayText || placeholder}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="px-1 pb-1">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && canAddCustom) {
                e.preventDefault();
                toggle(trimmedSearch);
                setSearch('');
              }
            }}
            placeholder="Search or type a custom value..."
            className="w-full h-7 px-2 text-xs rounded border border-input bg-background outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {canAddCustom && (
            <button
              type="button"
              onClick={() => { toggle(trimmedSearch); setSearch(''); }}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left"
            >
              <div className="w-4 shrink-0" />
              <span className="truncate text-muted-foreground">Add "{trimmedSearch}"</span>
            </button>
          )}
          {customSelected.map(v => (
            <button
              key={`custom-${v}`}
              type="button"
              onClick={() => toggle(v)}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left"
            >
              <div className="w-4 shrink-0">
                <Check className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="truncate font-mono">{v}</span>
            </button>
          ))}
          {filtered.map(o => {
            const checked = values.includes(o.key);
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => toggle(o.key)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-left"
              >
                <div className="w-4 shrink-0">
                  {checked && <Check className="w-3.5 h-3.5 text-primary" />}
                </div>
                <span className="truncate">
                  {o.name}
                  {o.name !== o.key && <span className="text-muted-foreground ml-1.5 font-mono text-[10px]">{o.key}</span>}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && customSelected.length === 0 && !canAddCustom && (
            <p className="text-xs text-muted-foreground text-center py-2">No options found</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AWSRegionMultiSelect({
  selectedRegions,
  onChange,
}: {
  selectedRegions: string[];
  onChange: (regions: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? AWS_REGIONS.filter(r => r.value.includes(search.toLowerCase()) || r.label.toLowerCase().includes(search.toLowerCase()))
    : AWS_REGIONS;

  const displayText = selectedRegions.length === 0
    ? 'All regions'
    : selectedRegions.length <= 2
      ? selectedRegions.join(', ')
      : `${selectedRegions.length} regions`;

  const toggle = (region: string) => {
    if (selectedRegions.includes(region)) {
      onChange(selectedRegions.filter(r => r !== region));
    } else {
      onChange([...selectedRegions, region]);
    }
  };

  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full mt-1 h-8 px-3 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        >
          <span className={`truncate ${selectedRegions.length === 0 ? 'text-muted-foreground' : ''}`}>
            {displayText}
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <div className="px-2 py-1.5">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search regions..."
            className="w-full h-7 px-2 text-xs rounded border border-input bg-background outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {selectedRegions.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer text-muted-foreground italic"
            >
              Clear all (default: all regions)
            </button>
          )}
          {filtered.map(r => {
            const checked = selectedRegions.includes(r.value);
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => toggle(r.value)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                  {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                <span className="truncate">{r.value}</span>
                <span className="text-muted-foreground ml-auto text-[10px] shrink-0">{r.label.replace(/.*\(/, '').replace(')', '')}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Searchable select for GitHub trigger filters (repos, branches, workflows)
function GitHubSearchSelect({
  options,
  value,
  onChange,
  placeholder,
  allLabel,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="mt-1 h-8 w-full justify-between text-sm font-normal">
          <span className="truncate">{value || allLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="h-7 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1">
          <button
            className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${!value ? "bg-accent font-medium" : ""}`}
            onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
          >
            {allLabel}
          </button>
          {filtered.map(o => (
            <button
              key={o.value}
              className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent truncate ${value === o.value ? "bg-accent font-medium" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); setQuery(""); }}
            >
              {o.label}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No results</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ClusterNamespaceSelect({ clusterId, value, onChange, placeholder }: { clusterId: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clusterId || clusterId.includes('{{')) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient.getWorkflowTriggerFilterOptions([clusterId]).then(data => {
      if (!cancelled) {
        const ns = Array.from(new Set((data.namespaces || []).map((n: any) => n.name))).sort();
        setOptions(ns as string[]);
        setLoading(false);
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clusterId]);

  if (value && value.includes('{{')) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 h-8 px-3 text-sm rounded-md border border-input bg-muted flex items-center font-mono text-xs">{value}</code>
        <button type="button" onClick={() => onChange('')} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
      </div>
    );
  }

  if (loading) {
    return <div className="mt-1 h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  }

  if (options.length > 0) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map(ns => (
            <SelectItem key={ns} value={ns}>{ns}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'default'}
      className="mt-1 h-8 text-sm"
    />
  );
}

function RequestSourcesMultiSelect({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const options = [
    { id: 'slack', name: 'Slack' },
    { id: 'platform', name: 'Kestrel Platform' },
    { id: 'cli', name: 'CLI' },
  ];
  const displayText = selected.length === 0
    ? 'All Sources'
    : options.filter(o => selected.includes(o.id)).map(o => o.name).join(', ');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="mt-1 flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-left hover:bg-accent transition-colors">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-1" align="start">
        <div className="space-y-0.5">
          {options.map(o => (
            <button
              key={o.id}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent"
              onClick={() => {
                const next = selected.includes(o.id) ? selected.filter(x => x !== o.id) : [...selected, o.id];
                onChange(next);
              }}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${selected.includes(o.id) ? 'bg-primary border-primary' : 'border-input'}`}>
                {selected.includes(o.id) && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              <span>{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RequestTypesMultiSelect({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const options = [
    { id: 'create', name: 'Create / Provision / Deploy' },
    { id: 'edit', name: 'Edit / Update / Scale' },
    { id: 'delete', name: 'Delete / Remove' },
    { id: 'get', name: 'Get / Investigate / Debug' },
  ];
  const displayText = selected.length === 0
    ? 'All Types'
    : options.filter(o => selected.includes(o.id)).map(o => o.name).join(', ');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="mt-1 flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-left hover:bg-accent transition-colors">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-1" align="start">
        <div className="space-y-0.5">
          {options.map(o => (
            <button
              key={o.id}
              className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent"
              onClick={() => {
                const next = selected.includes(o.id) ? selected.filter(x => x !== o.id) : [...selected, o.id];
                onChange(next);
              }}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${selected.includes(o.id) ? 'bg-primary border-primary' : 'border-input'}`}>
                {selected.includes(o.id) && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              <span>{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AllowedUsersMultiSelect({ selectedIds, onChange }: { selectedIds: string[]; onChange: (ids: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<{ id: string; email: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient.listTenantUsers().then((data: any) => {
      const members = data?.users || data || [];
      setUsers(members.map((m: any) => ({ id: m.id, email: m.email, name: m.name || m.email })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const isAll = selectedIds.length === 0;

  // Normalize: when users load, resolve any stored emails/names to UUIDs
  useEffect(() => {
    if (users.length === 0 || selectedIds.length === 0) return;
    let needsUpdate = false;
    const normalized = selectedIds.map(sid => {
      // Already a UUID (contains dashes, 36 chars)?
      if (sid.length === 36 && sid.includes('-')) return sid;
      // Try to resolve by email or name
      const match = users.find(u => u.email.toLowerCase() === sid.toLowerCase() || (u.name || '').toLowerCase() === sid.toLowerCase());
      if (match) { needsUpdate = true; return match.id; }
      return sid;
    });
    if (needsUpdate) onChange(normalized);
  }, [users]);

  const matchUser = (u: any) => selectedIds.includes(u.id) || selectedIds.includes(u.email) || selectedIds.some((sid: string) => sid.toLowerCase() === (u.name || '').toLowerCase() || sid.toLowerCase() === u.email.toLowerCase());
  const displayText = isAll ? 'None' : users.filter(matchUser).map(u => u.name || u.email).join(', ') || selectedIds.join(', ');

  const toggle = (id: string) => {
    const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="mt-1 flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-left hover:bg-accent transition-colors">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-1 max-h-[250px] overflow-y-auto" align="start">
        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-2">Loading...</p>
        ) : (
          <div className="space-y-0.5">
            {users.map(u => {
              const isSelected = selectedIds.includes(u.id) || selectedIds.includes(u.email) || selectedIds.some(sid => sid.toLowerCase() === (u.name || '').toLowerCase() || sid.toLowerCase() === u.email.toLowerCase());
              return (
              <button key={u.id} className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent" onClick={() => toggle(u.id)}>
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-input'}`}>
                  {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                <span className="truncate">{u.name || u.email}</span>
              </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function AllowedGroupsMultiSelect({ selectedIds, onChange }: { selectedIds: string[]; onChange: (ids: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient.listUserGroups().then((g: any[]) => {
      setGroups(g.map(grp => ({ id: grp.id, name: grp.name })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Normalize stored group names to IDs
  useEffect(() => {
    if (groups.length === 0 || selectedIds.length === 0) return;
    let needsUpdate = false;
    const normalized = selectedIds.map(sid => {
      if (sid.length === 36 && sid.includes('-')) return sid;
      const match = groups.find(g => g.name.toLowerCase() === sid.toLowerCase());
      if (match) { needsUpdate = true; return match.id; }
      return sid;
    });
    if (needsUpdate) onChange(normalized);
  }, [groups]);

  const isAll = selectedIds.length === 0;
  const displayText = isAll ? 'None' : groups.filter(g => selectedIds.includes(g.id) || selectedIds.some((sid: string) => sid.toLowerCase() === g.name.toLowerCase())).map(g => g.name).join(', ') || selectedIds.join(', ');

  const toggle = (id: string) => {
    const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="mt-1 flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-left hover:bg-accent transition-colors">
          <span className="truncate">{displayText}</span>
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-1 max-h-[250px] overflow-y-auto" align="start">
        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-2">Loading...</p>
        ) : groups.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">No user groups defined</p>
        ) : (
          <div className="space-y-0.5">
            {groups.map(g => (
              <button key={g.id} className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent" onClick={() => toggle(g.id)}>
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${selectedIds.includes(g.id) ? 'bg-primary border-primary' : 'border-input'}`}>
                  {selectedIds.includes(g.id) && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                <span className="truncate">{g.name}</span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
